import React, { useState, useEffect, useMemo, useRef } from 'react';
import {
  Box,
  Typography,
  Card,
  CardActionArea,
  CardContent,
  Tabs,
  Tab,
  TextField,
  Button,
  Switch,
  FormControlLabel,
  Grid,
  Alert,
  Divider,
  Select,
  MenuItem,
  FormControl,
  InputLabel,
  Chip,
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  IconButton,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  Paper,
  LinearProgress,
  CircularProgress,
} from '@mui/material';
import {
  Settings as SettingsIcon,
  Security as SecurityIcon,
  AudioFile as AudioIcon,
  SmartToy as AIIcon,
  Radio as RadioIcon,
  CloudUpload as DropboxIcon,
  People as PeopleIcon,
  Email as EmailIcon,
  Add,
  Edit,
  Delete,
  ContentCopy,
  Image as ImageIcon,
  FiberManualRecord as LoggerIcon,
  CloudDone,
  OpenInNew,
  Link as LinkIcon,
  DeleteSweep as AutoDeleteIcon,
  Subtitles as TextOutputIcon,
  PlayArrow,
  Pause,
  Equalizer as EqualizerIcon,
  Stop as StopIcon,
  Article as LogsIcon,
  Refresh as RefreshIcon,
  Storage as StorageIcon,
  Backup as BackupIcon,
  Schedule as ScheduleIcon,
  CloudDownload as RestoreIcon,
  CheckCircle as CheckCircleIcon,
  Cancel as CancelIcon,
  DeleteForever as DeleteForeverIcon,
  Https as SslIcon,
  Lock as LockIcon,
  LockOpen as LockOpenIcon,
  Download as DownloadIcon,
  UploadFile as UploadFileIcon,
  History as AuditIcon,
} from '@mui/icons-material';
import PageHeader from '../components/PageHeader';
import DropboxIntegration from '../components/DropboxIntegration';
import UserManagement from '../components/UserManagement';
import AuditTrailSettings from '../components/AuditTrailSettings';
import MfaSetup from '../components/MfaSetup';
import StudioModeSettings from '../components/StudioModeSettings';
import ServerNodesSettings from '../components/ServerNodesSettings';
import FranchiseSyndicationSettings from '../components/FranchiseSyndicationSettings';
import GatewayJoinSettings from '../components/GatewayJoinSettings';
import CrmApiKeys from '../components/CrmApiKeys';
import TextOutputsSettings from '../components/TextOutputsSettings';
import WebrtcContributionSettings from '../components/WebrtcContributionSettings';
import { useAuth } from '../contexts/AuthContext';
import { useSearchParams } from '../router';
import axios from 'axios';
import { stationNameToMount } from '../utils/mount-slug';

const API_URL = process.env.REACT_APP_API_URL || window.location.origin;

const featureSettingEnabled = (value, defaultOn = true) => {
  if (value === undefined || value === null || value === '') return defaultOn;
  return value !== false && value !== 'false';
};

const APP_FEATURE_TOGGLES = [
  { key: 'advertising_enabled', settingKey: 'system_advertising_enabled', label: 'Advertising', description: 'Traffic, commercial breaks and ad scheduling' },
  { key: 'playlists_enabled', settingKey: 'system_playlists_enabled', label: 'Playlists', description: 'Playlist generation and hour logs' },
  { key: 'clocks_enabled', settingKey: 'system_clocks_enabled', label: 'Clocks', description: 'Clocks, schedules and scheduling rules' },
  { key: 'voicetrack_enabled', settingKey: 'system_voicetrack_enabled', label: 'Voicetrack', description: 'Voicetrack recording and markers' },
  { key: 'live_enabled', settingKey: 'system_live_enabled', label: 'Live', description: 'Live control surface, mic and cartwalls' },
  { key: 'library_enabled', settingKey: 'system_library_enabled', label: 'Library', description: 'Asset library and categories' },
  { key: 'ai_content_enabled', settingKey: 'system_ai_content_enabled', label: 'AI Content', description: 'AI voicetracks, content generation and scheduling' },
  { key: 'ai_insights_enabled', settingKey: 'system_ai_insights_enabled', label: 'AI Insights', description: 'Analysis and suggestions on library items' },
  { key: 'logger_enabled', settingKey: 'system_logger_enabled', label: 'Logger', description: 'Stream recording, playback and log exports' },
  { key: 'intelligence_enabled', settingKey: 'system_intelligence_enabled', label: 'Intelligence', description: 'AI station format detection and playlist suggestions. Also enable it per person, with AI Content and a Grok API key.' },
  { key: 'studio_output_enabled', settingKey: 'system_studio_output_enabled', label: 'Studio Output', description: 'Studio Mode devices, installer builds and desktop playout' },
  { key: 'webrtc_contribution_enabled', settingKey: 'system_webrtc_contribution_enabled', label: 'WebRTC Contribution', description: 'Remote contribution users and signaling' },
  { key: 'text_output_enabled', settingKey: 'system_text_output_enabled', label: 'Text Output', description: 'Rotating text / metadata outputs (DAB DLS, HD Radio, FM, etc.)' },
  { key: 'audio_processing_enabled', settingKey: 'audio_processing_enabled', label: 'Audio Processing', description: 'FFmpeg DSP processing and Icecast relays' },
  { key: 'multi_node_enabled', settingKey: 'multi_node_enabled', label: 'Multi-Node', description: 'Multi-VM worker node deployment' },
];

const SETTINGS_SECTION_HINTS = {
  station: 'Stations, stream URLs and which product features are switched on',
  ai_features: 'Grok, voice providers and AI content controls',
  audio_engine: 'Crossfade, normalisation and engine defaults',
  audio_processing: 'Processing chains and Icecast output',
  webrtc_contrib: 'Remote contribution users and signaling',
  studio_mode: 'Studio devices, installer builds and desktop playout',
  text_output: 'Rotating text and metadata outputs',
  dropbox: 'Dropbox connection and file sync',
  security: 'Multi-factor authentication',
  storage: 'Disk usage and media storage',
  branding: 'Logo and visual identity',
  email: 'SMTP delivery and welcome emails',
  users: 'Accounts, roles and permissions',
  audit_trail: 'Searchable history of account activity, with CSV and JSON export',
  auto_delete: 'Automatic cleanup of old files',
  playback_log: 'Playback history retention',
  backup: 'Backups and restore',
  gateway: 'Gateway connection',
  franchise_syndication: 'Franchise station syndication',
  logger: 'Stream recording and log exports',
  proxy: 'Public URL and reverse proxy',
  domain_ssl: 'Custom domain and certificates',
  server_nodes: 'Multi-node worker deployment',
};

const STATION_COUNTRIES = [
  'United Kingdom',
  'Ireland',
  'United States',
  'Canada',
  'Australia',
  'New Zealand',
  'Germany',
  'France',
  'Spain',
  'Italy',
  'Netherlands',
  'Belgium',
  'Sweden',
  'Norway',
  'Denmark',
  'Finland',
  'Portugal',
  'Switzerland',
  'Austria',
  'Poland',
  'South Africa',
  'India',
  'Singapore',
  'United Arab Emirates',
  'Mexico',
  'Brazil',
  'Japan',
];

export default function Settings() {
  const [tabValue, setTabValue] = useState(-1);
  const [loading, setLoading] = useState(false);
  const [saveStatus, setSaveStatus] = useState({ type: '', message: '' });
  const {
    isSuperAdmin,
    isAdmin,
    hasStationManagement,
    hasModuleAccess,
    getHourRestrictions,
    getServerLocalHour,
    refreshPermissions,
    hasGrokApiKey,
  } = useAuth();
  const [searchParams, setSearchParams] = useSearchParams();
  const requestedTabKey = searchParams.get('tab');
  const studioOutputAllowedHours = getHourRestrictions('studio_output');
  const canViewStudioOutput = hasModuleAccess('studio_output')
    && (
      !Array.isArray(studioOutputAllowedHours)
      || studioOutputAllowedHours.map(Number).includes(getServerLocalHour())
    );
  const [syndicationEnabled, setSyndicationEnabled] = useState(false);
  
  // AI Settings
  const [aiConfig, setAiConfig] = useState({
    grok_api_key: '',
    grok_model: 'grok-4.3',
    elevenlabs_api_key: '',
    elevenlabs_voice_id: '',
    speechify_api_key: '',
    google_api_key: '',
    google_voice_name: '',
    ai_insights_enabled: false,
    ai_voicetrack_enabled: false,
    ai_content_generation_enabled: false,
    ai_scheduling_enabled: false,
  });

  // Station Management
  const [stations, setStations] = useState([]);
  const [selectedStation, setSelectedStation] = useState(null);
  const [stationDialogOpen, setStationDialogOpen] = useState(false);
  const [editingStation, setEditingStation] = useState(null);
  const [stationFormData, setStationFormData] = useState({
    name: '',
    call_sign: '',
    frequency: '',
    country: '',
    location: '',
    mode: 'single',
    is_active: true,
    icecast_enabled: false,
    icecast_mountpoint: '/stream',
    icecast_format: 'mp3',
    icecast_bitrate: 320,
    icecast_metadata_enabled: true,
    icecast_stream_title: '',
    icecast_stream_description: '',
    icecast_stream_genre: '',
    beatmix_enabled: false,
  });
  const [stationTabValue, setStationTabValue] = useState(0);

  // Station network config (independent vs inherit master)
  const [stationNetworkConfig, setStationNetworkConfig] = useState({
    mode: 'independent',
    master_station_id: null,
  });

  // Region Management
  const [regions, setRegions] = useState([]);
  const [regionDialogOpen, setRegionDialogOpen] = useState(false);
  const [editingRegion, setEditingRegion] = useState(null);
  const [regionFormData, setRegionFormData] = useState({
    name: '', code: '', timezone: 'UTC', time_shift_hours: 0, is_active: true,
    icecast_enabled: false, icecast_host: 'localhost', icecast_port: 8000,
    icecast_username: 'source', icecast_password: '', icecast_mountpoint: '',
    icecast_format: 'mp3', icecast_bitrate: 320,
    icecast_metadata_enabled: true, icecast_stream_title: '', icecast_stream_description: '',
    icecast_stream_genre: '',
  });
  const [regionalStreamStatus, setRegionalStreamStatus] = useState([]);
  const [allRegions, setAllRegions] = useState([]);

  // Audio Engine Settings
  const [audioEngineSettings, setAudioEngineSettings] = useState({
    default_engine: 'ffmpeg',
    crossfade_duration: 3.0,
    normalize_audio: true,
    sweeper_duck_level_db: -6,
  });

  // Email/SMTP Settings
  const [emailDeliveryStatus, setEmailDeliveryStatus] = useState(null);
  const [emailStatusChecking, setEmailStatusChecking] = useState(false);
  const [emailSettings, setEmailSettings] = useState({
    smtp_host: '',
    smtp_port: 587,
    smtp_secure: false,
    smtp_username: '',
    smtp_password: '',
    from_email: '',
    from_name: 'The Radio Suite',
    welcome_email_subject: 'Your Radio Suite Account',
    password_reset_subject: 'The Radio Suite - Password Reset',
    enable_email_notifications: false,
  });
  const [testEmail, setTestEmail] = useState('');
  const [smtpTesting, setSmtpTesting] = useState(false);

  // Branding (admin/superadmin)
  const [brandingAppName, setBrandingAppName] = useState('');
  const [brandingLogoUrl, setBrandingLogoUrl] = useState('');
  // URL & proxy (superadmin only)
  const [proxyPublicUrl, setProxyPublicUrl] = useState('');
  const [hideServerInfo, setHideServerInfo] = useState(false);
  const [proxySaveMessage, setProxySaveMessage] = useState({ type: '', text: '' });
  const [proxySaving, setProxySaving] = useState(false);
  const [brandingSaving, setBrandingSaving] = useState(false);
  const [brandingMessage, setBrandingMessage] = useState({ type: '', text: '' });

  // Playback Log cleanup
  const [playbackLogStats, setPlaybackLogStats] = useState(null);
  const [playbackLogRetention, setPlaybackLogRetention] = useState({ enabled: false, retention_days: 90 });
  const [playbackLogRetentionSaving, setPlaybackLogRetentionSaving] = useState(false);
  const [playbackLogCleaning, setPlaybackLogCleaning] = useState(false);
  const [playbackLogMessage, setPlaybackLogMessage] = useState({ type: '', text: '' });

  // Logger Dropbox (superadmin only)
  const [loggerDropboxConfig, setLoggerDropboxConfig] = useState({
    appKey: '', appSecret: '', redirectUri: '',
  });
  const [loggerDropboxConnected, setLoggerDropboxConnected] = useState(false);
  const [loggerDropboxConfigured, setLoggerDropboxConfigured] = useState(false);
  const [loggerRetentionDays, setLoggerRetentionDays] = useState(60);
  const [loggerSaving, setLoggerSaving] = useState(false);
  const [loggerMessage, setLoggerMessage] = useState({ type: '', text: '' });

  // Auto Delete Settings
  const [autoDeleteRules, setAutoDeleteRules] = useState([]);
  const [autoDeleteCategories, setAutoDeleteCategories] = useState([]);
  const [autoDeletePreview, setAutoDeletePreview] = useState(null);
  const [autoDeleteSaving, setAutoDeleteSaving] = useState(false);
  const [autoDeleteMessage, setAutoDeleteMessage] = useState({ type: '', text: '' });
  const [autoDeleteDialogOpen, setAutoDeleteDialogOpen] = useState(false);
  const [autoDeleteForm, setAutoDeleteForm] = useState({
    category_id: '',
    enabled: true,
    days_after_last_use: 60,
    delete_file: true,
  });
  const [autoDeleteRunning, setAutoDeleteRunning] = useState(false);
  const [mixblockAutoDelete, setMixblockAutoDelete] = useState({ enabled: true, days: 60 });
  const [mixblockAutoDeleteSaving, setMixblockAutoDeleteSaving] = useState(false);

  // Domain & SSL
  const [sslDomain, setSslDomain] = useState('');
  const [sslEmail, setSslEmail] = useState('');
  const [sslAppPort, setSslAppPort] = useState('3000');
  const [sslBackendPort, setSslBackendPort] = useState('5000');
  const [sslStatus, setSslStatus] = useState(null);
  const [sslLoading, setSslLoading] = useState(false);
  const [sslMessage, setSslMessage] = useState({ type: '', text: '' });
  const [sslStaging, setSslStaging] = useState(false);
  const sslPollRef = useRef(null);

  // Audio Processing
  const [audioProcessingEnabled, setAudioProcessingEnabled] = useState(false);
  const [audioProcessingInstances, setAudioProcessingInstances] = useState([]);
  const [audioProcessingStations, setAudioProcessingStations] = useState([]);
  const [audioProcessingPresets, setAudioProcessingPresets] = useState([]);
  const [audioProcessingCodecs, setAudioProcessingCodecs] = useState({ profiles: {}, capabilities: {} });
  const [audioProcessingLoading, setAudioProcessingLoading] = useState(false);
  const [audioProcessingMessage, setAudioProcessingMessage] = useState({ type: '', text: '' });
  const [audioProcessingLogs, setAudioProcessingLogs] = useState('');
  const [audioProcessingLogsOpen, setAudioProcessingLogsOpen] = useState(null);
  const [audioProcessingDialog, setAudioProcessingDialog] = useState({ open: false, station: null, instance: null });
  const [audioProcessingForm, setAudioProcessingForm] = useState({ preset: 'default', outputs: [] });
  // Tracks output IDs that were present when the dialog opened but have since been removed
  const [audioProcessingRemovedOutputIds, setAudioProcessingRemovedOutputIds] = useState([]);

  // System Feature Toggles (superadmin only — Station and App Configuration)
  const [systemFeatures, setSystemFeatures] = useState({
    advertising_enabled: true,
    playlists_enabled: true,
    clocks_enabled: true,
    voicetrack_enabled: true,
    live_enabled: true,
    library_enabled: true,
    ai_content_enabled: true,
    ai_insights_enabled: true,
    logger_enabled: true,
    intelligence_enabled: true,
    studio_output_enabled: true,
    webrtc_contribution_enabled: true,
    text_output_enabled: true,
    audio_processing_enabled: false,
    multi_node_enabled: false,
  });
  const [featuresSaving, setFeaturesSaving] = useState(false);
  const [featuresMessage, setFeaturesMessage] = useState({ type: '', text: '' });

  // Storage limit (superadmin sets, admin reads)
  const [storageInfo, setStorageInfo] = useState(null);
  const [storageLimitGb, setStorageLimitGb] = useState('');
  const [storageLoading, setStorageLoading] = useState(false);
  const [storageSaving, setStorageSaving] = useState(false);
  const [storageMessage, setStorageMessage] = useState({ type: '', text: '' });
  const [portableImporting, setPortableImporting] = useState(false);
  const [portableImportProgress, setPortableImportProgress] = useState(0);
  const [playoutAudioFormat, setPlayoutAudioFormat] = useState('flac');
  const [playoutAudioFormatInfo, setPlayoutAudioFormatInfo] = useState(null);
  const [playoutAudioFormatSaving, setPlayoutAudioFormatSaving] = useState(false);

  // Missing-audio recovery (superadmin only)
  const [missingAudio, setMissingAudio] = useState(null);
  const [missingAudioLoading, setMissingAudioLoading] = useState(false);
  const [missingAudioFilter, setMissingAudioFilter] = useState('');
  const [recoveryCategory, setRecoveryCategory] = useState('');
  const [recoveryUploading, setRecoveryUploading] = useState(false);
  const [recoveryProgress, setRecoveryProgress] = useState({ done: 0, total: 0 });
  const [recoveryResults, setRecoveryResults] = useState(null);
  // Per-asset background uploads: { [assetId]: { status: 'uploading'|'restored'|'error', message } }
  const [rowUploads, setRowUploads] = useState({});

  // ── Backup ──────────────────────────────────────────────────────────────────
  const [backupConfig, setBackupConfig] = useState({
    backup_managed_by: 'client',
    backup_hide_from_admin: 'false',
    backup_dropbox_type: 'existing',
    backup_dropbox_path: '/RadioSuiteBackups',
    backup_type: 'audio',
    backup_schedule_enabled: 'false',
    backup_schedule_cron: '0 2 * * *',
    backup_auto_delete_enabled: 'false',
    backup_auto_delete_mode: 'days',
    backup_auto_delete_days: '30',
    backup_auto_delete_keep: '5',
  });
  const [backupConfigLoading, setBackupConfigLoading] = useState(false);
  const [backupConfigSaving, setBackupConfigSaving] = useState(false);
  const [backupConfigMessage, setBackupConfigMessage] = useState({ type: '', text: '' });
  const [backupJobs, setBackupJobs] = useState([]);
  const [backupJobsLoading, setBackupJobsLoading] = useState(false);
  const [backupRunning, setBackupRunning] = useState(false);
  const [backupDropboxFiles, setBackupDropboxFiles] = useState([]);
  const [backupDropboxFilesLoading, setBackupDropboxFilesLoading] = useState(false);
  const [backupDedicatedDropbox, setBackupDedicatedDropbox] = useState({ connected: false });
  const [backupDedicatedLoading, setBackupDedicatedLoading] = useState(false);
  const [backupCleanupRunning, setBackupCleanupRunning] = useState(false);
  const [backupRestoreDialog, setBackupRestoreDialog] = useState({ open: false, file: null });
  const [backupMessage, setBackupMessage] = useState({ type: '', text: '' });

  const [isInitialMount, setIsInitialMount] = useState(true);

  useEffect(() => {
    fetchAIConfig();
    fetchStations();
    fetchAudioEngineSettings();
    fetchAllRegions();
    if (isSuperAdmin()) fetchEmailSettings();
    fetchBranding();
    if (isSuperAdmin()) {
      fetchLoggerDropboxConfig();
      fetchLoggerRetention();
      axios.get(`${API_URL}/api/syndication/status`, { withCredentials: true })
        .then((response) => setSyndicationEnabled(Number(response.data?.rolloutStage || 0) >= 1))
        .catch(() => setSyndicationEnabled(false));
    }
    if (isSuperAdmin() || isAdmin()) {
      fetchAutoDeleteRules();
      fetchAutoDeleteCategories();
      fetchMixblockAutoDelete();
      fetchAudioProcessingStatus();
      fetchAudioProcessingStations();
      fetchAudioProcessingPresets();
      fetchAudioProcessingCodecs();
      fetchStorageInfo();
      if (isSuperAdmin()) fetchPlayoutAudioFormat();
      if (isSuperAdmin()) fetchMissingAudio();
      fetchBackupConfig();
      fetchBackupJobs();
      if (isSuperAdmin()) fetchBackupDedicatedDropbox();
      if (isSuperAdmin()) loadSslStatus();
    }
  }, []);

  useEffect(() => {
    if (!isSuperAdmin()) return;
    const migration = playoutAudioFormatInfo?.migration;
    if (!migration || !['queued', 'running'].includes(migration.status)) return;
    const interval = setInterval(fetchPlayoutAudioFormat, 5000);
    return () => clearInterval(interval);
  }, [playoutAudioFormatInfo?.migration?.status]);

  // Auto-refresh storage usage every 60 seconds so it stays current after uploads
  useEffect(() => {
    if (!(isSuperAdmin() || isAdmin())) return;
    const interval = setInterval(fetchStorageInfo, 60000);
    return () => clearInterval(interval);
  }, []);

  // Immediately refresh storage when Library finishes an upload batch
  useEffect(() => {
    if (!(isSuperAdmin() || isAdmin())) return;
    const handler = () => fetchStorageInfo();
    window.addEventListener('storage_usage_changed', handler);
    return () => window.removeEventListener('storage_usage_changed', handler);
  }, []);

  const fetchStorageInfo = async () => {
    setStorageLoading(true);
    try {
      const res = await axios.get(`${API_URL}/api/storage`, { withCredentials: true });
      setStorageInfo(res.data);
      const gb = (res.data.limit_bytes / (1024 * 1024 * 1024)).toFixed(2);
      setStorageLimitGb(gb);
    } catch (e) {
      if (e.response?.status !== 403) console.error('Error fetching storage info:', e);
    } finally {
      setStorageLoading(false);
    }
  };

  const fetchMissingAudio = async () => {
    setMissingAudioLoading(true);
    try {
      const res = await axios.get(`${API_URL}/api/storage/missing-audio`, { withCredentials: true });
      setMissingAudio(res.data);
      setRecoveryCategory((current) => (
        current && !(res.data.categories || []).some((category) => category.id === current)
          ? ''
          : current
      ));
    } catch (e) {
      if (e.response?.status !== 403) console.error('Error scanning for missing audio:', e);
    } finally {
      setMissingAudioLoading(false);
    }
  };

  const handleDownloadMissingReport = () => {
    const rows = missingAudio?.missing || [];
    const escapeCsv = (value) => {
      const text = value == null ? '' : String(value);
      return /[",\r\n]/.test(text) ? `"${text.replace(/"/g, '""')}"` : text;
    };
    const csv = [
      ['Asset ID', 'Title', 'Artist', 'Categories', 'Expected Filename', 'Duration (seconds)'],
      ...rows.map((asset) => [
        asset.id,
        asset.title,
        asset.artist,
        (asset.category_names || []).join('; '),
        asset.filename,
        asset.duration,
      ]),
    ]
      .map((row) => row.map(escapeCsv).join(','))
      .join('\r\n');
    // BOM so Excel opens the file as UTF-8.
    const blob = new Blob([`\ufeff${csv}`], { type: 'text/csv;charset=utf-8' });
    const url = URL.createObjectURL(blob);
    const link = document.createElement('a');
    link.href = url;
    link.download = `missing-audio-report-${new Date().toISOString().slice(0, 10)}.csv`;
    document.body.appendChild(link);
    link.click();
    link.remove();
    URL.revokeObjectURL(url);
  };

  // Per-asset upload: runs in the background with inline row status, so the
  // next asset can be assigned immediately without waiting.
  const handleRowRecoveryUpload = async (event, assetId) => {
    const file = (event.target.files || [])[0];
    event.target.value = '';
    if (!file) return;
    setRowUploads((current) => ({ ...current, [assetId]: { status: 'uploading' } }));
    try {
      const form = new FormData();
      form.append('files', file);
      form.append('asset_id', assetId);
      const res = await axios.post(
        `${API_URL}/api/storage/missing-audio/recover`,
        form,
        { withCredentials: true }
      );
      if ((res.data.restored || []).length > 0) {
        setRowUploads((current) => ({ ...current, [assetId]: { status: 'restored' } }));
        // Keep the row visible with its "Restored" badge, but update counts.
        setMissingAudio((current) => {
          if (!current) return current;
          const asset = (current.missing || []).find((entry) => entry.id === assetId);
          if (!asset) return current;
          return {
            ...current,
            missing_count: Math.max(0, current.missing_count - 1),
            categories: (current.categories || []).map((category) => (
              (asset.category_ids || []).includes(category.id)
                ? { ...category, missing_count: Math.max(0, category.missing_count - 1) }
                : category
            )),
          };
        });
      } else {
        const reason = res.data.skipped?.[0]?.reason
          || res.data.unmatched?.[0]?.reason
          || 'The file was not restored';
        setRowUploads((current) => ({ ...current, [assetId]: { status: 'error', message: reason } }));
      }
    } catch (e) {
      setRowUploads((current) => ({
        ...current,
        [assetId]: { status: 'error', message: e.response?.data?.message || 'Upload failed' },
      }));
    }
  };

  const handleRecoveryUpload = async (event) => {
    const files = Array.from(event.target.files || []);
    event.target.value = '';
    if (files.length === 0) return;
    setRecoveryUploading(true);
    setRecoveryProgress({ done: 0, total: files.length });
    const totals = { restored: [], skipped: [], unmatched: [] };
    setRecoveryResults(totals);
    try {
      // The server converts several files in parallel per request; small
      // batches keep each request comfortably inside proxy timeouts. Three
      // requests stay in flight so uploads always overlap conversion (the
      // server enforces one shared transcode budget regardless).
      const batchSize = 10;
      const batches = [];
      for (let i = 0; i < files.length; i += batchSize) {
        batches.push(files.slice(i, i + batchSize));
      }
      let doneCount = 0;
      const uploadBatch = async (batch) => {
        const form = new FormData();
        batch.forEach((file) => form.append('files', file));
        if (recoveryCategory) form.append('category_id', recoveryCategory);
        const res = await axios.post(
          `${API_URL}/api/storage/missing-audio/recover`,
          form,
          { withCredentials: true }
        );
        totals.restored.push(...(res.data.restored || []));
        totals.skipped.push(...(res.data.skipped || []));
        totals.unmatched.push(...(res.data.unmatched || []));
        doneCount += batch.length;
        setRecoveryProgress({ done: doneCount, total: files.length });
        setRecoveryResults({ ...totals });
      };
      const queue = [...batches];
      const workerCount = Math.min(3, queue.length);
      await Promise.all(
        Array.from({ length: workerCount }, async () => {
          while (queue.length > 0) {
            await uploadBatch(queue.shift());
          }
        })
      );
      setStorageMessage({
        type: totals.restored.length > 0 ? 'success' : 'warning',
        text: `Audio recovery finished — ${totals.restored.length} restored, ${totals.skipped.length} skipped, ${totals.unmatched.length} unmatched.`,
      });
      await fetchMissingAudio();
      window.dispatchEvent(new Event('storage_usage_changed'));
    } catch (e) {
      setStorageMessage({ type: 'error', text: e.response?.data?.message || 'Audio recovery failed.' });
    } finally {
      setRecoveryUploading(false);
    }
  };

  const fetchPlayoutAudioFormat = async () => {
    try {
      const res = await axios.get(
        `${API_URL}/api/system-settings/playout-audio-format`,
        { withCredentials: true }
      );
      setPlayoutAudioFormat(res.data.format || 'flac');
      setPlayoutAudioFormatInfo(res.data);
    } catch (e) {
      if (e.response?.status !== 403 && e.response?.status !== 404) {
        console.error('Error fetching playout audio format:', e);
      }
    }
  };

  const handleSavePlayoutAudioFormat = async () => {
    const label = playoutAudioFormat === 'flac' ? 'FLAC' : 'Opus 320 kbps';
    if (!window.confirm(
      `Switch canonical playout audio to ${label}? Existing playable files will migrate automatically in the background.`
    )) return;
    setPlayoutAudioFormatSaving(true);
    setStorageMessage({ type: '', text: '' });
    try {
      const res = await axios.put(
        `${API_URL}/api/system-settings/playout-audio-format`,
        { format: playoutAudioFormat },
        { withCredentials: true }
      );
      setPlayoutAudioFormatInfo((previous) => ({
        ...(previous || {}),
        format: res.data.format,
        migration: res.data.migration,
      }));
      setStorageMessage({
        type: 'success',
        text: ['queued', 'running'].includes(res.data.migration?.status)
          ? `${label} is now active. The library migration is running in the background.`
          : `${label} is now active. All playable files already match this format.`,
      });
    } catch (e) {
      setStorageMessage({
        type: 'error',
        text: e.response?.data?.message || 'Failed to update the playout audio format.',
      });
      await fetchPlayoutAudioFormat();
    } finally {
      setPlayoutAudioFormatSaving(false);
    }
  };

  const handleRetryAudioFormatMigration = async () => {
    const jobId = playoutAudioFormatInfo?.migration?.id;
    if (!jobId) return;
    try {
      await axios.post(
        `${API_URL}/api/system-settings/playout-audio-format/migration/retry`,
        { job_id: jobId },
        { withCredentials: true }
      );
      await fetchPlayoutAudioFormat();
    } catch (e) {
      setStorageMessage({
        type: 'error',
        text: e.response?.data?.message || 'Failed to retry the library migration.',
      });
    }
  };

  // ── Backup helpers ────────────────────────────────────────────────────────
  const fetchBackupConfig = async () => {
    setBackupConfigLoading(true);
    try {
      const res = await axios.get(`${API_URL}/api/backup/config`, { withCredentials: true });
      setBackupConfig(prev => ({ ...prev, ...res.data }));
    } catch (e) {
      if (e.response?.status !== 403 && e.response?.status !== 404)
        console.error('Error fetching backup config:', e);
    } finally {
      setBackupConfigLoading(false);
    }
  };

  const fetchBackupJobs = async () => {
    setBackupJobsLoading(true);
    try {
      const res = await axios.get(`${API_URL}/api/backup/jobs?limit=20`, { withCredentials: true });
      setBackupJobs(res.data || []);
    } catch (e) {
      if (e.response?.status !== 403) console.error('Error fetching backup jobs:', e);
    } finally {
      setBackupJobsLoading(false);
    }
  };

  const fetchBackupDropboxFiles = async () => {
    setBackupDropboxFilesLoading(true);
    try {
      const res = await axios.get(`${API_URL}/api/backup/list-dropbox`, { withCredentials: true });
      setBackupDropboxFiles(res.data || []);
    } catch (e) {
      setBackupDropboxFiles([]);
    } finally {
      setBackupDropboxFilesLoading(false);
    }
  };

  const fetchBackupDedicatedDropbox = async () => {
    setBackupDedicatedLoading(true);
    try {
      const res = await axios.get(`${API_URL}/api/backup/dropbox/status`, { withCredentials: true });
      setBackupDedicatedDropbox(res.data || { connected: false });
    } catch (e) {
      if (e.response?.status !== 403) console.error('Error fetching dedicated Dropbox status:', e);
    } finally {
      setBackupDedicatedLoading(false);
    }
  };

  const handleSaveBackupConfig = async () => {
    setBackupConfigSaving(true);
    setBackupConfigMessage({ type: '', text: '' });
    try {
      await axios.put(`${API_URL}/api/backup/config`, backupConfig, { withCredentials: true });
      setBackupConfigMessage({ type: 'success', text: 'Backup settings saved.' });
    } catch (e) {
      setBackupConfigMessage({ type: 'error', text: e.response?.data?.message || 'Failed to save.' });
    } finally {
      setBackupConfigSaving(false);
    }
  };

  const handleRunBackup = async () => {
    if (!window.confirm('Start a backup now? This may take several minutes.')) return;
    setBackupRunning(true);
    setBackupMessage({ type: '', text: '' });
    try {
      const res = await axios.post(`${API_URL}/api/backup/run`,
        { type: backupConfig.backup_type || 'audio' },
        { withCredentials: true }
      );
      setBackupMessage({ type: 'success', text: `Backup started (Job #${res.data.jobId}). It will appear in history when complete.` });
      setTimeout(fetchBackupJobs, 3000);
      setTimeout(fetchBackupJobs, 10000);
    } catch (e) {
      setBackupMessage({ type: 'error', text: e.response?.data?.message || 'Failed to start backup.' });
    } finally {
      setBackupRunning(false);
    }
  };

  const handleDeleteBackupFile = async (file) => {
    if (!window.confirm(`Delete "${file.name}" from Dropbox? This cannot be undone.`)) return;
    try {
      await axios.delete(`${API_URL}/api/backup/dropbox-file`,
        { data: { path: file.path }, withCredentials: true }
      );
      setBackupDropboxFiles(prev => prev.filter(f => f.path !== file.path));
    } catch (e) {
      setBackupMessage({ type: 'error', text: 'Failed to delete backup file.' });
    }
  };

  const handleBackupCleanup = async () => {
    if (!window.confirm('Run cleanup now? Old backups will be deleted from Dropbox based on your retention settings.')) return;
    setBackupCleanupRunning(true);
    try {
      await axios.post(`${API_URL}/api/backup/cleanup`, {}, { withCredentials: true });
      setBackupMessage({ type: 'success', text: 'Cleanup completed.' });
      fetchBackupDropboxFiles();
    } catch (e) {
      setBackupMessage({ type: 'error', text: e.response?.data?.message || 'Cleanup failed.' });
    } finally {
      setBackupCleanupRunning(false);
    }
  };

  const handleConnectDedicatedDropbox = async () => {
    try {
      const res = await axios.get(`${API_URL}/api/backup/dropbox/auth/url`, { withCredentials: true });
      window.location.href = res.data.authUrl;
    } catch (e) {
      setBackupMessage({ type: 'error', text: e.response?.data?.message || 'Failed to get auth URL.' });
    }
  };

  const handleDisconnectDedicatedDropbox = async () => {
    if (!window.confirm('Disconnect the dedicated backup Dropbox account?')) return;
    try {
      await axios.delete(`${API_URL}/api/backup/dropbox/disconnect`, { withCredentials: true });
      setBackupDedicatedDropbox({ connected: false });
      setBackupMessage({ type: 'success', text: 'Dedicated Dropbox disconnected.' });
    } catch (e) {
      setBackupMessage({ type: 'error', text: 'Failed to disconnect.' });
    }
  };

  const formatFileSize = (bytes) => {
    if (!bytes) return '—';
    if (bytes < 1024 * 1024) return `${(bytes / 1024).toFixed(1)} KB`;
    if (bytes < 1024 * 1024 * 1024) return `${(bytes / (1024 * 1024)).toFixed(1)} MB`;
    return `${(bytes / (1024 * 1024 * 1024)).toFixed(2)} GB`;
  };

  const backupStatusChip = (status) => {
    const map = {
      success: { label: 'Success', color: '#22c55e' },
      error: { label: 'Failed', color: '#ef4444' },
      running: { label: 'Running', color: '#f59e0b' },
      pending: { label: 'Pending', color: '#888' },
    };
    const s = map[status] || map.pending;
    return (
      <Chip
        label={s.label}
        size="small"
        sx={{ bgcolor: s.color + '22', color: s.color, border: `1px solid ${s.color}55`, fontWeight: 600, fontSize: '0.7rem' }}
      />
    );
  };

  const CRON_PRESETS = [
    { label: 'Daily at 2am', value: '0 2 * * *' },
    { label: 'Daily at midnight', value: '0 0 * * *' },
    { label: 'Every 12 hours', value: '0 */12 * * *' },
    { label: 'Weekly (Sunday 2am)', value: '0 2 * * 0' },
    { label: 'Weekly (Monday 2am)', value: '0 2 * * 1' },
    { label: 'Custom…', value: '__custom__' },
  ];

  const handleSaveStorageLimit = async () => {
    const gb = parseFloat(storageLimitGb);
    if (isNaN(gb) || gb <= 0) {
      setStorageMessage({ type: 'error', text: 'Please enter a valid positive number for the storage limit.' });
      return;
    }
    setStorageSaving(true);
    setStorageMessage({ type: '', text: '' });
    try {
      await axios.put(`${API_URL}/api/storage/limit`, { limit_gb: gb }, { withCredentials: true });
      setStorageMessage({ type: 'success', text: 'Storage limit saved successfully.' });
      await fetchStorageInfo();
    } catch (e) {
      setStorageMessage({ type: 'error', text: e.response?.data?.message || 'Failed to save storage limit.' });
    } finally {
      setStorageSaving(false);
    }
  };

  const handleBackfillFileSizes = async () => {
    setStorageMessage({ type: '', text: '' });
    try {
      const res = await axios.post(`${API_URL}/api/storage/backfill`, {}, { withCredentials: true });
      const { updated, skipped, total } = res.data;
      setStorageMessage({
        type: 'success',
        text: `Backfill complete: ${updated} of ${total} assets updated (${skipped} skipped — file not found on disk).`,
      });
      await fetchStorageInfo();
    } catch (e) {
      setStorageMessage({ type: 'error', text: e.response?.data?.message || 'Failed to backfill file sizes.' });
    }
  };

  const handlePortableLibraryExport = async () => {
    if (!window.confirm(
      'Download the entire portable library now? The ZIP may be very large and can take a long time to create.'
    )) return;

    setStorageMessage({ type: 'info', text: 'Preparing the portable library export…' });
    try {
      const response = await axios.post(
        `${API_URL}/api/storage/portable-library/export`,
        {},
        { withCredentials: true }
      );
      const link = document.createElement('a');
      link.href = new URL(response.data.download_url, API_URL).toString();
      link.download = '';
      document.body.appendChild(link);
      link.click();
      link.remove();
      setStorageMessage({
        type: 'success',
        text: 'Portable library download started. Keep this page open until it finishes.',
      });
    } catch (error) {
      setStorageMessage({
        type: 'error',
        text: error.response?.data?.message || 'Failed to start portable library export.',
      });
    }
  };

  const handlePortableLibraryImport = async (event) => {
    const archive = event.target.files?.[0];
    event.target.value = '';
    if (!archive) return;
    if (!window.confirm(
      `Import "${archive.name}"? Tracks previously imported from the same source will be updated; all other tracks will be added.`
    )) return;

    setPortableImporting(true);
    setPortableImportProgress(0);
    setStorageMessage({ type: '', text: '' });
    try {
      const formData = new FormData();
      formData.append('archive', archive);
      const inspection = await axios.post(
        `${API_URL}/api/storage/portable-library/import/inspect`,
        formData,
        {
          withCredentials: true,
          onUploadProgress: (progressEvent) => {
            if (progressEvent.total) {
              setPortableImportProgress(Math.round(
                (progressEvent.loaded * 100) / progressEvent.total
              ));
            }
          },
        }
      );
      const stationMapping = { ...(inspection.data.station_mapping || {}) };
      const stationMappings = [...(inspection.data.station_mappings || [])];
      const destinationStations = inspection.data.destination_stations || [];
      for (const unresolved of inspection.data.unresolved_stations || []) {
        const choices = destinationStations
          .map((station) => `${station.id}: ${station.name}`)
          .join('\n');
        const selected = window.prompt(
          `Map source station "${unresolved.source_name || unresolved.source_id}" to a destination station ID:\n\n${choices}`
        );
        if (selected === null) {
          setStorageMessage({
            type: 'warning',
            text: 'Portable library import was cancelled. The temporary upload will expire automatically.',
          });
          return;
        }
        const destination = destinationStations.find(
          (station) => Number(station.id) === Number(selected)
        );
        if (!destination) {
          throw new Error(`Invalid destination station ID for ${unresolved.source_name || unresolved.source_id}.`);
        }
        stationMapping[unresolved.source_id] = destination.id;
        stationMappings.push({
          source_id: unresolved.source_id,
          source_name: unresolved.source_name || unresolved.source_id,
          destination_id: destination.id,
          destination_name: destination.name,
        });
      }
      if (stationMappings.length > 0) {
        const mappingText = stationMappings
          .map((mapping) => `${mapping.source_name} → ${mapping.destination_name}`)
          .join('\n');
        if (!window.confirm(
          `Confirm destination station mapping:\n\n${mappingText}\n\nContinue with the import?`
        )) {
          setStorageMessage({
            type: 'warning',
            text: 'Portable library import was cancelled. The temporary upload will expire automatically.',
          });
          return;
        }
      }
      const res = await axios.post(
        `${API_URL}/api/storage/portable-library/import/${inspection.data.import_token}`,
        { station_mapping: stationMapping },
        { withCredentials: true }
      );
      const imported = res.data.assets_imported || 0;
      const updated = res.data.assets_updated || 0;
      const categories = res.data.categories_created || 0;
      const warningCount = res.data.warnings?.length || 0;
      setStorageMessage({
        type: warningCount ? 'warning' : 'success',
        text: warningCount
          ? `Portable library import completed with partial-restoration warnings: ${imported} tracks added, ${updated} updated, ${categories} categories created.`
          : `Portable library import complete: ${imported} tracks added, ${updated} updated, ${categories} categories created.`,
        details: res.data.warnings || [],
      });
      window.dispatchEvent(new Event('storage_usage_changed'));
      await fetchStorageInfo();
    } catch (e) {
      const unresolved = e.response?.data?.unresolved_stations || [];
      setStorageMessage({
        type: 'error',
        text: e.response?.data?.message || e.message || 'Failed to import portable library.',
        details: unresolved.map((station) => (
          `${station.source_name || station.source_id}: ${station.reason}`
        )),
      });
    } finally {
      setPortableImporting(false);
      setPortableImportProgress(0);
    }
  };

  const fetchBranding = async () => {
    try {
      const res = await axios.get(`${API_URL}/api/system-settings`, { withCredentials: true });
      const d = res.data || {};
      setBrandingAppName(d.branding_app_name || '');
      setBrandingLogoUrl(d.branding_logo_url || '');
      setProxyPublicUrl(d.public_base_url || '');
      setHideServerInfo(d.hide_server_info === true || d.hide_server_info === 'true');
      // Load system-wide feature toggles (nav modules default on; infra flags default off)
      setSystemFeatures({
        advertising_enabled: featureSettingEnabled(d.system_advertising_enabled),
        playlists_enabled: featureSettingEnabled(d.system_playlists_enabled),
        clocks_enabled: featureSettingEnabled(d.system_clocks_enabled),
        voicetrack_enabled: featureSettingEnabled(d.system_voicetrack_enabled),
        live_enabled: featureSettingEnabled(d.system_live_enabled),
        library_enabled: featureSettingEnabled(d.system_library_enabled),
        ai_content_enabled: featureSettingEnabled(d.system_ai_content_enabled),
        ai_insights_enabled: featureSettingEnabled(d.system_ai_insights_enabled),
        logger_enabled: featureSettingEnabled(d.system_logger_enabled),
        intelligence_enabled: featureSettingEnabled(d.system_intelligence_enabled),
        studio_output_enabled: featureSettingEnabled(d.system_studio_output_enabled),
        webrtc_contribution_enabled: featureSettingEnabled(d.system_webrtc_contribution_enabled),
        text_output_enabled: featureSettingEnabled(d.system_text_output_enabled),
        audio_processing_enabled: featureSettingEnabled(d.audio_processing_enabled, false),
        multi_node_enabled: featureSettingEnabled(d.multi_node_enabled, false),
      });
    } catch (e) {
      if (e.response?.status !== 404) console.error('Error fetching branding:', e);
    }
  };

  // ===== Logger Dropbox (Superadmin) =====
  const fetchLoggerDropboxConfig = async () => {
    try {
      const res = await axios.get(`${API_URL}/api/logger/dropbox/config`, { withCredentials: true });
      setLoggerDropboxConfig({
        appKey: res.data.appKey || '',
        appSecret: '',
        redirectUri: res.data.redirectUri || `${API_URL}/api/logger/dropbox/auth/callback`,
      });
      setLoggerDropboxConfigured(res.data.configured || false);
      setLoggerDropboxConnected(res.data.connected || false);
    } catch (e) {
      if (e.response?.status !== 403 && e.response?.status !== 404) {
        console.error('Error fetching logger Dropbox config:', e);
      }
    }
  };

  const fetchLoggerRetention = async () => {
    try {
      const res = await axios.get(`${API_URL}/api/logger/retention`, { withCredentials: true });
      setLoggerRetentionDays(res.data.retentionDays || 60);
    } catch (e) {
      if (e.response?.status !== 403) console.error('Error fetching logger retention:', e);
    }
  };

  const handleSaveLoggerDropboxConfig = async () => {
    if (!loggerDropboxConfig.appKey || !loggerDropboxConfig.appSecret) {
      setLoggerMessage({ type: 'error', text: 'App Key and App Secret are required' });
      return;
    }
    setLoggerSaving(true);
    setLoggerMessage({ type: '', text: '' });
    try {
      await axios.post(`${API_URL}/api/logger/dropbox/config`, loggerDropboxConfig, { withCredentials: true });
      setLoggerDropboxConfigured(true);
      setLoggerMessage({ type: 'success', text: 'Logger Dropbox credentials saved' });
      setLoggerDropboxConfig({ ...loggerDropboxConfig, appSecret: '' });
    } catch (e) {
      setLoggerMessage({ type: 'error', text: e.response?.data?.message || 'Failed to save' });
    } finally {
      setLoggerSaving(false);
    }
  };

  const handleConnectLoggerDropbox = async () => {
    try {
      const res = await axios.get(`${API_URL}/api/logger/dropbox/auth/url`, { withCredentials: true });
      if (res.data.needsConfiguration) {
        setLoggerMessage({ type: 'warning', text: 'Configure Logger Dropbox credentials first' });
        return;
      }
      window.location.href = res.data.authUrl;
    } catch (e) {
      setLoggerMessage({ type: 'error', text: e.response?.data?.message || 'Failed to get auth URL' });
    }
  };

  const handleDisconnectLoggerDropbox = async () => {
    if (!window.confirm('Disconnect Logger Dropbox? Existing recordings on Dropbox will remain, but new recordings won\'t be uploaded.')) return;
    try {
      await axios.post(`${API_URL}/api/logger/dropbox/disconnect`, {}, { withCredentials: true });
      setLoggerDropboxConnected(false);
      setLoggerMessage({ type: 'success', text: 'Logger Dropbox disconnected' });
    } catch (e) {
      setLoggerMessage({ type: 'error', text: 'Failed to disconnect' });
    }
  };

  const handleSaveLoggerRetention = async () => {
    setLoggerSaving(true);
    setLoggerMessage({ type: '', text: '' });
    try {
      await axios.put(`${API_URL}/api/logger/retention`, { retentionDays: loggerRetentionDays }, { withCredentials: true });
      setLoggerMessage({ type: 'success', text: `Retention set to ${loggerRetentionDays} days` });
    } catch (e) {
      setLoggerMessage({ type: 'error', text: e.response?.data?.message || 'Failed to save retention' });
    } finally {
      setLoggerSaving(false);
    }
  };

  const handleManualCleanup = async () => {
    if (!window.confirm(`This will delete recordings older than ${loggerRetentionDays} days from Dropbox. Continue?`)) return;
    setLoggerSaving(true);
    try {
      await axios.post(`${API_URL}/api/logger/cleanup`, {}, { withCredentials: true });
      setLoggerMessage({ type: 'success', text: 'Cleanup completed' });
    } catch (e) {
      setLoggerMessage({ type: 'error', text: e.response?.data?.message || 'Cleanup failed' });
    } finally {
      setLoggerSaving(false);
    }
  };

  const saveBrandingSetting = async (key, value) => {
    try {
      await axios.post(
        `${API_URL}/api/system-settings`,
        { key, value, description: key === 'branding_app_name' ? 'App name in sidebar' : 'Sidebar logo URL' },
        { withCredentials: true }
      );
    } catch (e) {
      throw e;
    }
  };

  const handleBrandingSave = async () => {
    setBrandingSaving(true);
    setBrandingMessage({ type: '', text: '' });
    try {
      await saveBrandingSetting('branding_app_name', brandingAppName.trim());
      setBrandingMessage({ type: 'success', text: 'Branding saved. Refresh the page or re-open the sidebar to see changes.' });
    } catch (e) {
      setBrandingMessage({ type: 'error', text: e.response?.data?.message || 'Failed to save branding' });
    }
    setBrandingSaving(false);
  };

  const handleBrandingLogoUpload = async (e) => {
    const file = e.target.files?.[0];
    if (!file) return;
    setBrandingSaving(true);
    setBrandingMessage({ type: '', text: '' });
    try {
      const formData = new FormData();
      formData.append('logo', file);
      const res = await axios.post(`${API_URL}/api/system-settings/branding/logo`, formData, {
        withCredentials: true,
        headers: { 'Content-Type': 'multipart/form-data' },
      });
      const url = res.data?.url;
      if (url) {
        await saveBrandingSetting('branding_logo_url', url);
        setBrandingLogoUrl(url);
        setBrandingMessage({ type: 'success', text: 'Logo uploaded.' });
      }
    } catch (err) {
      setBrandingMessage({ type: 'error', text: err.response?.data?.message || 'Failed to upload logo' });
    }
    setBrandingSaving(false);
    e.target.value = '';
  };

  const handleProxySave = async () => {
    setProxySaving(true);
    setProxySaveMessage({ type: '', text: '' });
    try {
      const base = (proxyPublicUrl || '').trim();
      await axios.post(
        `${API_URL}/api/system-settings`,
        { key: 'public_base_url', value: base, description: 'Public URL when behind proxy' },
        { withCredentials: true }
      );
      await axios.post(
        `${API_URL}/api/system-settings`,
        { key: 'hide_server_info', value: hideServerInfo, description: 'Hide server IP and internal URLs from non-superadmins' },
        { withCredentials: true }
      );
      setProxySaveMessage({ type: 'success', text: 'URL & proxy settings saved.' });
    } catch (e) {
      setProxySaveMessage({ type: 'error', text: e.response?.data?.message || 'Failed to save' });
    } finally {
      setProxySaving(false);
    }
  };

  // ── Domain & SSL handlers ──────────────────────────────────────────────────
  const loadSslStatus = async () => {
    setSslLoading(true);
    try {
      const res = await axios.get(`${API_URL}/api/ssl/status`, { withCredentials: true });
      const data = res.data;
      setSslStatus(data);
      if (data.domain)  setSslDomain(data.domain);
      if (data.email)   setSslEmail(data.email);
    } catch (err) {
      console.error('Failed to load SSL status', err);
    } finally {
      setSslLoading(false);
    }
  };

  const pollSslUntilDone = (onDone) => {
    if (sslPollRef.current) clearInterval(sslPollRef.current);
    const startedAt = Date.now();
    const tick = async () => {
      try {
        const r = await axios.get(`${API_URL}/api/ssl/status`, { withCredentials: true });
        setSslStatus(r.data);
        if (!r.data.isRequesting) {
          clearInterval(sslPollRef.current);
          sslPollRef.current = null;
          onDone(r.data);
        } else if (Date.now() - startedAt > 150000) {
          clearInterval(sslPollRef.current);
          sslPollRef.current = null;
          setSslMessage({
            type: 'error',
            text: r.data.lastError
              ? `Certificate request failed: ${r.data.lastError}`
              : 'Certificate request is taking too long. Restart the backend if it stays stuck on Requesting, then try again.',
          });
        }
      } catch {
        // Keep polling — a single failed status fetch must not freeze the spinner.
      }
    };
    tick();
    sslPollRef.current = setInterval(tick, 5000);
  };

  useEffect(() => () => {
    if (sslPollRef.current) clearInterval(sslPollRef.current);
  }, []);


  const handleRequestCert = async () => {
    if (!sslDomain.trim() || !sslEmail.trim()) {
      setSslMessage({ type: 'error', text: 'Domain and email are required' });
      return;
    }
    setSslMessage({ type: '', text: '' });
    try {
      await axios.post(
        `${API_URL}/api/ssl/request`,
        { domain: sslDomain.trim(), email: sslEmail.trim(), staging: sslStaging },
        { withCredentials: true }
      );
      setSslMessage({ type: 'info', text: 'Certificate request started. This may take up to 2 minutes — the status below will update automatically.' });
      pollSslUntilDone((data) => {
        if (data.status === 'active') {
          setSslMessage({ type: 'success', text: '✅ Certificate issued successfully! Your HTTPS server is now running.' });
        } else if (data.status === 'error') {
          setSslMessage({ type: 'error', text: `Certificate request failed: ${data.lastError}` });
        }
      });
    } catch (err) {
      setSslMessage({ type: 'error', text: err.response?.data?.message || 'Failed to start certificate request' });
    }
  };

  const handleRenewCert = async () => {
    setSslMessage({ type: '', text: '' });
    try {
      await axios.post(`${API_URL}/api/ssl/renew`, {}, { withCredentials: true });
      setSslMessage({ type: 'info', text: 'Certificate renewal started. Polling for updates…' });
      pollSslUntilDone((data) => {
        setSslMessage(data.status === 'active'
          ? { type: 'success', text: '✅ Certificate renewed successfully!' }
          : { type: 'error', text: `Renewal failed: ${data.lastError}` }
        );
      });
    } catch (err) {
      setSslMessage({ type: 'error', text: err.response?.data?.message || 'Failed to start renewal' });
    }
  };

  const handleBrandingLogoRemove = async () => {
    setBrandingSaving(true);
    setBrandingMessage({ type: '', text: '' });
    try {
      await saveBrandingSetting('branding_logo_url', '');
      setBrandingLogoUrl('');
      setBrandingMessage({ type: 'success', text: 'Logo removed.' });
    } catch (e) {
      setBrandingMessage({ type: 'error', text: e.response?.data?.message || 'Failed to remove logo' });
    }
    setBrandingSaving(false);
  };

  // Refetch stations when user returns to this tab (e.g. after creating a station elsewhere)
  useEffect(() => {
    const onVisible = () => {
      if (document.visibilityState === 'visible') fetchStations();
    };
    document.addEventListener('visibilitychange', onVisible);
    return () => document.removeEventListener('visibilitychange', onVisible);
  }, []);

  const fetchAllRegions = async () => {
    try {
      const res = await axios.get(`${API_URL}/api/regions`, { withCredentials: true });
      setAllRegions(res.data || []);
    } catch (error) {
      if (error.response?.status !== 404) console.error('Error fetching regions:', error);
      setAllRegions([]);
    }
  };

  const outputUrls = useMemo(() => {
    const list = [];
    stations.forEach((station) => {
      const icecastHost = station.icecast_host || 'localhost';
      const icecastPort = station.icecast_port || 8000;
      const icecastBase = `http://${icecastHost}:${icecastPort}`;

      // Prefer direct stream URL (no Icecast) for main station
      const mainUrl = station.direct_stream_url || station.stream_url || (() => {
        let mount = station.icecast_mountpoint || stationNameToMount(station.name, station.id);
        if (!mount.startsWith('/')) mount = `/${mount}`;
        return `${icecastBase}${mount}`;
      })();
      list.push({ label: `${station.name} (main)`, url: mainUrl, stationId: station.id });

      allRegions
        .filter((r) => r.station_id === station.id && r.icecast_mountpoint)
        .forEach((region) => {
          const rHost = region.icecast_host || icecastHost;
          const rPort = region.icecast_port || icecastPort;
          const rBase = `http://${rHost}:${rPort}`;
          const rMount = region.icecast_mountpoint.startsWith('/') ? region.icecast_mountpoint : `/${region.icecast_mountpoint}`;
          const url = region.stream_url || `${rBase}${rMount}`;
          list.push({ label: `${station.name} → ${region.name}`, url, stationId: station.id, regionId: region.id });
        });
    });
    return list;
  }, [stations, allRegions]);

  const handleCopyUrl = (url) => {
    navigator.clipboard?.writeText(url).then(() => setSaveStatus({ type: 'success', message: 'URL copied to clipboard' }));
  };

  useEffect(() => {
    // Skip fetching station settings on initial mount to avoid race conditions
    // Only fetch if we have a valid selectedStation, we're not editing, and initial mount is complete
    if (!isInitialMount && selectedStation && !editingStation && stations.length > 0) {
      // Verify the station exists in our stations list before fetching
      const stationExists = stations.some(s => s.id === selectedStation);
      if (stationExists) {
        fetchStationSettings(selectedStation);
      }
    }
  }, [selectedStation, editingStation, stations.length, isInitialMount]);

  const fetchAIConfig = async () => {
    try {
      const response = await axios.get(`${API_URL}/api/ai/config`, {
        withCredentials: true
      });
      setAiConfig({
        grok_api_key: '',
        ...response.data,
        grok_model: response.data?.grok_model === 'grok-4.5' ? 'grok-4.5' : 'grok-4.3',
        elevenlabs_api_key: response.data?.elevenlabs_api_key || '',
        elevenlabs_voice_id: response.data?.elevenlabs_voice_id || '',
        speechify_api_key: response.data?.speechify_api_key || '',
        google_api_key: response.data?.google_api_key || response.data?.murf_api_key || '',
        google_voice_name: response.data?.google_voice_name || response.data?.murf_voice_id || '',
      });
    } catch (error) {
      console.error('Error fetching AI config:', error);
    }
  };

  const fetchStations = async () => {
    try {
      const response = await axios.get(`${API_URL}/api/stations`, {
        withCredentials: true
      });
      const stationsList = response.data || [];
      setStations(stationsList);
      // Only auto-select first station if we don't have one selected and stations exist
      if (stationsList.length > 0 && !selectedStation) {
        setSelectedStation(stationsList[0].id);
      }
      // Clear initial-mount guard only after stations have loaded so downstream effects get valid data
      setIsInitialMount(false);
    } catch (error) {
      console.error('Error fetching stations:', error);
      setStations([]);
      setIsInitialMount(false);
    }
  };

  const fetchAudioEngineSettings = async () => {
    try {
      const res = await axios.get(`${API_URL}/api/system-settings`, {
        withCredentials: true
      });
      const s = res.data || {};
      const db = typeof s.sweeper_duck_level_db === 'number' ? s.sweeper_duck_level_db : undefined;
      let crossfade = s.crossfade_duration;
      if (typeof crossfade === 'string') {
        crossfade = parseFloat(crossfade);
      }
      if (!Number.isFinite(crossfade)) crossfade = undefined;
      if (db !== undefined) {
        setAudioEngineSettings(prev => ({ ...prev, sweeper_duck_level_db: db }));
      }
      if (crossfade !== undefined) {
        setAudioEngineSettings(prev => ({ ...prev, crossfade_duration: crossfade }));
      }
    } catch (error) {
      if (error.response?.status !== 404) {
        console.error('Error fetching audio engine settings:', error);
      }
    }
  };

  const fetchStationSettings = async (stationId) => {
    if (!stationId) return;
    try {
      const response = await axios.get(`${API_URL}/api/stations/${stationId}`, {
        withCredentials: true
      });
      const station = response.data;
      setStationFormData({
        name: station.name || '',
        call_sign: station.call_sign || '',
        frequency: station.frequency || '',
        country: station.country || '',
        location: station.location || '',
        mode: station.mode || 'single',
        is_active: station.is_active !== false,
        icecast_enabled: station.icecast_enabled || false,
        icecast_mountpoint: station.icecast_mountpoint || '/stream',
        icecast_format: station.icecast_format || 'mp3',
        icecast_bitrate: station.icecast_bitrate || 320,
        icecast_metadata_enabled: station.icecast_metadata_enabled !== false,
        icecast_stream_title: station.icecast_stream_title || '',
        icecast_stream_description: station.icecast_stream_description || '',
        icecast_stream_genre: station.icecast_stream_genre || '',
        beatmix_enabled: station.beatmix_enabled || false,
      });
    } catch (error) {
      console.error('Error fetching station settings:', error);
      // Don't show error if it's a 404 (station not found) - that's expected in some cases
      if (error.response?.status !== 404) {
        console.error('Station fetch error details:', error.response?.data);
      }
    }
  };

  const handleOpenStationDialog = (station = null) => {
    if (station) {
      setEditingStation(station);
      setStationFormData({
        name: station.name || '',
        call_sign: station.call_sign || '',
        frequency: station.frequency || '',
        country: station.country || '',
        location: station.location || '',
        mode: station.mode || 'single',
        is_active: station.is_active !== false,
        icecast_enabled: station.icecast_enabled || false,
        icecast_mountpoint: station.icecast_mountpoint || '/stream',
        icecast_format: station.icecast_format || 'mp3',
        icecast_bitrate: station.icecast_bitrate || 320,
        icecast_metadata_enabled: station.icecast_metadata_enabled !== false,
        icecast_stream_title: station.icecast_stream_title || '',
        icecast_stream_description: station.icecast_stream_description || '',
        icecast_stream_genre: station.icecast_stream_genre || '',
        beatmix_enabled: station.beatmix_enabled || false,
      });
      // Load network config and regions for this station
      loadStationNetworkConfig(station.id);
      fetchRegions(station.id);
      fetchRegionalStreamStatus(station.id);
    } else {
      setEditingStation(null);
      setStationFormData({
        name: '',
        call_sign: '',
        frequency: '',
        country: '',
        location: '',
        mode: 'single',
        is_active: true,
        icecast_enabled: false,
        icecast_mountpoint: '/stream',
        icecast_format: 'mp3',
        icecast_bitrate: 320,
        icecast_metadata_enabled: true,
        icecast_stream_title: '',
        icecast_stream_description: '',
        icecast_stream_genre: '',
        beatmix_enabled: false,
      });
      setStationNetworkConfig({
        mode: 'independent',
        master_station_id: null,
      });
    }
    setStationTabValue(0);
    setStationDialogOpen(true);
  };

  const handleCloseStationDialog = () => {
    setStationDialogOpen(false);
    setEditingStation(null);
  };

  const loadStationNetworkConfig = async (stationId) => {
    if (!stationId) return;
    const key = `station_network_config_${stationId}`;
    try {
      const res = await axios.get(`${API_URL}/api/system-settings/${key}`, {
        withCredentials: true,
      });
      let value = res.data?.value;
      if (typeof value === 'string') {
        try {
          value = JSON.parse(value);
        } catch {
          // ignore parse error, fall back to string
        }
      }
      setStationNetworkConfig({
        mode: value?.mode || 'independent',
        master_station_id: value?.master_station_id || null,
      });
    } catch (error) {
      // If not found (404) or error, default to independent - this is expected for new stations
      if (error.response?.status !== 404) {
        console.error('Error loading station network config:', error.response?.data || error.message);
      }
      setStationNetworkConfig({
        mode: 'independent',
        master_station_id: null,
      });
    }
  };

  const saveStationNetworkConfig = async (stationId, config) => {
    const key = `station_network_config_${stationId}`;
    await axios.post(
      `${API_URL}/api/system-settings`,
      { key, value: config },
      { withCredentials: true }
    );
  };

  const handleSaveStation = async () => {
    if (!stationFormData.name) {
      setSaveStatus({ type: 'error', message: 'Station name is required' });
      return;
    }

    setLoading(true);
    setSaveStatus({ type: '', message: '' });

    try {
      const stationPayload = {
        ...stationFormData,
      };
      if (editingStation) {
        const res = await axios.put(`${API_URL}/api/stations/${editingStation.id}`, stationPayload, {
          withCredentials: true
        });
        // Save network config for this station
        await saveStationNetworkConfig(editingStation.id, stationNetworkConfig);
        setSaveStatus({ type: 'success', message: 'Station updated successfully' });
      } else {
        const res = await axios.post(`${API_URL}/api/stations`, stationPayload, {
          withCredentials: true
        });
        const newStationId = res.data?.id;
        if (newStationId) {
          await saveStationNetworkConfig(newStationId, stationNetworkConfig);
        }
        setSaveStatus({ type: 'success', message: 'Station created successfully' });
      }
      handleCloseStationDialog();
      fetchStations();
      fetchAllRegions();
    } catch (error) {
      console.error('Error saving station:', error);
      setSaveStatus({ type: 'error', message: error.response?.data?.message || 'Failed to save station' });
    } finally {
      setLoading(false);
    }
  };

  const handleDeleteStation = async (stationId) => {
    if (!window.confirm('Are you sure you want to delete this station?')) {
      return;
    }

    try {
      await axios.delete(`${API_URL}/api/stations/${stationId}`, {
        withCredentials: true
      });
      setSaveStatus({ type: 'success', message: 'Station deleted successfully' });
      fetchStations();
      if (selectedStation === stationId) {
        setSelectedStation(null);
      }
    } catch (error) {
      console.error('Error deleting station:', error);
      setSaveStatus({ type: 'error', message: 'Failed to delete station' });
    }
  };

  // ===== Region Management =====
  const fetchRegions = async (stationId) => {
    if (!stationId) return;
    try {
      const res = await axios.get(`${API_URL}/api/regions?station_id=${stationId}`, { withCredentials: true });
      setRegions(res.data || []);
    } catch (error) {
      console.error('Error fetching regions:', error);
    }
  };

  const fetchRegionalStreamStatus = async (stationId) => {
    if (!stationId) return;
    try {
      const res = await axios.get(`${API_URL}/api/regional-streams/status?station_id=${stationId}`, { withCredentials: true });
      setRegionalStreamStatus(res.data || []);
    } catch (error) {
      console.error('Error fetching regional stream status:', error);
    }
  };

  const handleOpenRegionDialog = (region = null) => {
    const stationId = editingStation?.id;
    const stationMount = editingStation?.icecast_mountpoint || stationNameToMount(editingStation?.name, stationId);
    if (region) {
      setEditingRegion(region);
      setRegionFormData({
        name: region.name || '', code: region.code || '', timezone: region.timezone || 'UTC',
        time_shift_hours: Number(region.time_shift_hours) || 0,
        is_active: region.is_active !== false,
        icecast_enabled: region.icecast_enabled ?? true,
        icecast_host: region.icecast_host || 'localhost',
        icecast_port: region.icecast_port ?? 8000,
        icecast_username: region.icecast_username || 'source',
        icecast_password: region.icecast_password || '',
        icecast_mountpoint: region.icecast_mountpoint || '',
        icecast_format: region.icecast_format || 'mp3',
        icecast_bitrate: region.icecast_bitrate || 320,
        icecast_metadata_enabled: region.icecast_metadata_enabled !== false,
        icecast_stream_title: region.icecast_stream_title || '',
        icecast_stream_description: region.icecast_stream_description || '',
        icecast_stream_genre: region.icecast_stream_genre || '',
      });
    } else {
      setEditingRegion(null);
      // New region: auto-enable Icecast (regions in multi-region mode always get streams)
      setRegionFormData({
        name: '', code: '', timezone: 'UTC', time_shift_hours: 0, is_active: true,
        icecast_enabled: true,
        icecast_host: editingStation?.icecast_host || 'localhost',
        icecast_port: editingStation?.icecast_port ?? 8000,
        icecast_username: 'source',
        icecast_password: '',
        icecast_mountpoint: '',
        icecast_format: editingStation?.icecast_format || 'mp3',
        icecast_bitrate: editingStation?.icecast_bitrate || 320,
        icecast_metadata_enabled: true,
        icecast_stream_title: '',
        icecast_stream_description: '',
        icecast_stream_genre: '',
      });
    }
    setRegionDialogOpen(true);
  };

  const handleSaveRegion = async () => {
    if (!regionFormData.name || !regionFormData.code) {
      setSaveStatus({ type: 'error', message: 'Region name and code are required' });
      return;
    }
    const stationId = editingStation?.id;
    if (!stationId) return;

    // Auto-generate mountpoint if Icecast enabled but not set
    let dataToSave = {
      ...regionFormData,
    };
    if (dataToSave.icecast_enabled && !dataToSave.icecast_mountpoint?.trim()) {
      const baseMount = (editingStation?.icecast_mountpoint || stationNameToMount(editingStation?.name, stationId)).replace(/\/$/, '');
      dataToSave.icecast_mountpoint = `${baseMount}-region-${dataToSave.code.toLowerCase().replace(/\s+/g, '-').replace(/[^a-z0-9-]/g, '')}`;
    }

    setLoading(true);
    try {
      if (editingRegion) {
        await axios.put(`${API_URL}/api/regions/${editingRegion.id}`, dataToSave, { withCredentials: true });
      } else {
        await axios.post(`${API_URL}/api/regions`, { ...dataToSave, station_id: stationId }, { withCredentials: true });
      }
      setRegionDialogOpen(false);
      setEditingRegion(null);
      fetchRegions(stationId);
      fetchAllRegions();
      setSaveStatus({ type: 'success', message: editingRegion ? 'Region updated' : 'Region created' });
    } catch (error) {
      console.error('Error saving region:', error);
      setSaveStatus({ type: 'error', message: error.response?.data?.message || 'Failed to save region' });
    } finally {
      setLoading(false);
    }
  };

  const handleAutoConfigureRegionIcecast = async (region) => {
    const stationId = editingStation?.id;
    if (!stationId) return;
    setLoading(true);
    try {
      await axios.post(
        `${API_URL}/api/regions/auto-configure`,
        { station_id: stationId },
        { withCredentials: true }
      );
      fetchRegions(stationId);
      fetchAllRegions();
      setSaveStatus({ type: 'success', message: `Icecast configured for ${region.name}` });
    } catch (e) {
      const msg = e.response?.data?.message || e.response?.data?.error || e.message || 'Failed to configure';
      setSaveStatus({ type: 'error', message: msg });
    } finally {
      setLoading(false);
    }
  };

  const handleAutoConfigureAllRegions = async () => {
    const stationId = editingStation?.id;
    if (!stationId) return;
    const unconfigured = regions.filter((r) => !r.icecast_enabled || !r.icecast_mountpoint);
    if (unconfigured.length === 0) {
      setSaveStatus({ type: 'info', message: 'All regions already have Icecast configured' });
      return;
    }
    setLoading(true);
    setSaveStatus({ type: '', message: '' });
    try {
      const res = await axios.post(
        `${API_URL}/api/regions/auto-configure`,
        { station_id: stationId },
        { withCredentials: true }
      );
      fetchRegions(stationId);
      fetchAllRegions();
      setSaveStatus({ type: 'success', message: res.data?.message || `Configured Icecast for ${unconfigured.length} region(s)` });
    } catch (e) {
      const msg = e.response?.data?.message || e.response?.data?.error || e.message || 'Failed to auto-configure';
      setSaveStatus({ type: 'error', message: msg });
    } finally {
      setLoading(false);
    }
  };

  const handleDeleteRegion = async (regionId) => {
    if (!window.confirm('Delete this region? This will also remove all regional voicetracks and stream configurations.')) return;
    try {
      await axios.delete(`${API_URL}/api/regions/${regionId}`, { withCredentials: true });
      fetchRegions(editingStation?.id);
      fetchAllRegions();
      setSaveStatus({ type: 'success', message: 'Region deleted' });
    } catch (error) {
      console.error('Error deleting region:', error);
      setSaveStatus({ type: 'error', message: 'Failed to delete region' });
    }
  };

  const handleStartRegionalStream = async (stationId, regionId) => {
    try {
      await axios.post(`${API_URL}/api/regional-streams/start`, { station_id: stationId, region_id: regionId }, { withCredentials: true });
      fetchRegionalStreamStatus(stationId);
    } catch (error) {
      console.error('Error starting regional stream:', error);
      setSaveStatus({ type: 'error', message: error.response?.data?.message || 'Failed to start stream' });
    }
  };

  const handleStopRegionalStream = async (stationId, regionId) => {
    try {
      await axios.post(`${API_URL}/api/regional-streams/stop`, { station_id: stationId, region_id: regionId }, { withCredentials: true });
      fetchRegionalStreamStatus(stationId);
    } catch (error) {
      console.error('Error stopping regional stream:', error);
    }
  };

  const handleSaveAI = async () => {
    if (!(isSuperAdmin() || isAdmin())) {
      setSaveStatus({ type: 'error', message: 'You do not have permission to update AI settings.' });
      return;
    }

    setLoading(true);
    setSaveStatus({ type: '', message: '' });
    
    try {
      const payload = {
        ...aiConfig,
        murf_api_key: aiConfig.google_api_key || '',
        murf_voice_id: aiConfig.google_voice_name || '',
      };

      if (!isSuperAdmin()) {
        delete payload.grok_api_key;
        delete payload.grok_model;
      }

      await axios.put(`${API_URL}/api/ai/config`, payload, {
        withCredentials: true
      });
      setAiConfig((prev) => ({
        ...prev,
        has_grok_api_key: prev.has_grok_api_key === true
          || (typeof prev.grok_api_key === 'string' && prev.grok_api_key.trim().length > 0),
      }));
      await fetchAIConfig();
      if (typeof refreshPermissions === 'function') {
        await refreshPermissions();
      }
      setSaveStatus({ type: 'success', message: 'AI settings saved successfully' });
    } catch (error) {
      console.error('Error saving AI config:', error);
      setSaveStatus({ type: 'error', message: 'Failed to save AI settings' });
    } finally {
      setLoading(false);
    }
  };


  const handleSaveSystemFeatures = async () => {
    setFeaturesSaving(true);
    setFeaturesMessage({ type: '', text: '' });
    let runtimeSettingChanged = false;
    try {
      const currentResponse = await axios.get(
        `${API_URL}/api/system-settings`,
        { withCredentials: true }
      );
      const currentSettings = currentResponse.data || {};

      // These modules have dedicated endpoints because changing them has
      // runtime side effects (process shutdown, assignment validation/cache).
      if (
        featureSettingEnabled(currentSettings.multi_node_enabled, false)
        !== !!systemFeatures.multi_node_enabled
      ) {
        await axios.put(
          `${API_URL}/api/server-nodes/settings`,
          { enabled: !!systemFeatures.multi_node_enabled },
          { withCredentials: true }
        );
        runtimeSettingChanged = true;
      }
      if (
        featureSettingEnabled(currentSettings.audio_processing_enabled, false)
        !== !!systemFeatures.audio_processing_enabled
      ) {
        await axios.post(
          `${API_URL}/api/audio-processing/enable`,
          { enabled: !!systemFeatures.audio_processing_enabled },
          { withCredentials: true }
        );
        runtimeSettingChanged = true;
      }

      const standardToggles = APP_FEATURE_TOGGLES.filter(
        (toggle) => !['audio_processing_enabled', 'multi_node_enabled'].includes(toggle.key)
      );
      const grokApiKeyConfigured = (typeof hasGrokApiKey === 'function' && hasGrokApiKey())
        || aiConfig.has_grok_api_key === true;
      await axios.put(
        `${API_URL}/api/system-settings/features`,
        {
          features: standardToggles.map((toggle) => {
            let value = !!systemFeatures[toggle.key];
            if (toggle.key === 'ai_insights_enabled' && value && !grokApiKeyConfigured) {
              value = featureSettingEnabled(currentSettings.system_ai_insights_enabled);
            }
            value = !!value;
            return {
              key: toggle.settingKey,
              value,
              description: `Enable or disable ${toggle.label} system-wide`,
            };
          }),
        },
        { withCredentials: true }
      );
      setAudioProcessingEnabled(!!systemFeatures.audio_processing_enabled);
      if (typeof refreshPermissions === 'function') {
        await refreshPermissions();
      }
      setFeaturesMessage({
        type: 'success',
        text: 'App feature settings saved. Active users update on focus or within one minute.',
      });
    } catch (error) {
      console.error('Error saving system features:', error);
      await Promise.allSettled([
        fetchBranding(),
        fetchAudioProcessingStatus(),
        typeof refreshPermissions === 'function' ? refreshPermissions() : Promise.resolve(),
      ]);
      setFeaturesMessage({
        type: 'error',
        text: runtimeSettingChanged
          ? 'Save was only partially applied. Current values have been reloaded; review them and try again.'
          : error.response?.data?.message || 'Failed to save app feature settings.',
      });
    } finally {
      setFeaturesSaving(false);
    }
  };

  const fetchEmailSettings = async () => {
    try {
      const response = await axios.get(`${API_URL}/api/email-settings`, { withCredentials: true });
      setEmailSettings(response.data);
    } catch (error) {
      if (error.response?.status !== 403) {
        console.error('Error fetching email settings:', error);
      }
    }
  };

  const fetchEmailDeliveryStatus = async (verify = false) => {
    setEmailStatusChecking(true);
    try {
      const response = await axios.get(`${API_URL}/api/email-settings/status`, {
        params: verify ? { verify: '1' } : undefined,
        withCredentials: true,
      });
      setEmailDeliveryStatus(response.data);
    } catch (error) {
      if (error.response?.status !== 403) {
        console.error('Error checking email status:', error);
      }
    } finally {
      setEmailStatusChecking(false);
    }
  };

  const handleSaveEmailSettings = async () => {
    setLoading(true);
    setSaveStatus({ type: '', message: '' });
    try {
      const response = await axios.put(`${API_URL}/api/email-settings`, emailSettings, { withCredentials: true });
      if (response.data?.status) {
        setEmailDeliveryStatus(response.data.status);
      }
      if (response.data?.status?.working) {
        setSaveStatus({ type: 'success', message: 'Email settings saved. SMTP is configured and working.' });
      } else if (response.data?.status?.configured && response.data?.status?.connectionOk === false) {
        setSaveStatus({
          type: 'error',
          message: `Settings saved, but SMTP did not connect: ${response.data.status.connectionError || 'connection failed'}`,
        });
      } else if (response.data?.status?.configured && response.data?.status?.connectionOk && !response.data?.status?.enabled) {
        setSaveStatus({
          type: 'warning',
          message: 'Settings saved and SMTP connected, but email notifications are off so mail will not send.',
        });
      } else {
        setSaveStatus({ type: 'success', message: 'Email settings saved successfully' });
      }
    } catch (error) {
      console.error('Error saving email settings:', error);
      setSaveStatus({ type: 'error', message: error.response?.data?.message || 'Failed to save email settings' });
    } finally {
      setLoading(false);
    }
  };

  const handleTestSmtpConnection = async () => {
    setSmtpTesting(true);
    setSaveStatus({ type: '', message: '' });
    try {
      await axios.post(`${API_URL}/api/email-settings/test-connection`, {
        smtp_host: emailSettings.smtp_host,
        smtp_port: emailSettings.smtp_port,
        smtp_secure: emailSettings.smtp_secure,
        smtp_username: emailSettings.smtp_username,
        smtp_password: emailSettings.smtp_password,
      }, { withCredentials: true });
      setSaveStatus({ type: 'success', message: 'SMTP connection successful!' });
    } catch (error) {
      setSaveStatus({ type: 'error', message: error.response?.data?.message || 'SMTP connection failed' });
    } finally {
      setSmtpTesting(false);
    }
  };

  const handleSendTestEmail = async () => {
    if (!testEmail) {
      setSaveStatus({ type: 'error', message: 'Please enter a recipient email address' });
      return;
    }
    setSmtpTesting(true);
    setSaveStatus({ type: '', message: '' });
    try {
      await axios.post(`${API_URL}/api/email-settings/test-send`, { to_email: testEmail }, { withCredentials: true });
      setSaveStatus({ type: 'success', message: `Test email sent to ${testEmail}` });
    } catch (error) {
      setSaveStatus({ type: 'error', message: error.response?.data?.message || 'Failed to send test email' });
    } finally {
      setSmtpTesting(false);
    }
  };

  useEffect(() => {
    if (!isSuperAdmin() || requestedTabKey !== 'email') return;
    fetchEmailDeliveryStatus(true);
  }, [requestedTabKey]);

  const handleSaveAudioEngine = async () => {
    setLoading(true);
    setSaveStatus({ type: '', message: '' });
    
    try {
      await Promise.all([
        axios.post(
          `${API_URL}/api/system-settings`,
          {
            key: 'crossfade_duration',
            value: audioEngineSettings.crossfade_duration,
            description: 'Global marker fade duration in seconds',
          },
          { withCredentials: true }
        ),
        axios.post(
          `${API_URL}/api/system-settings`,
          {
            key: 'sweeper_duck_level_db',
            value: audioEngineSettings.sweeper_duck_level_db,
            description: 'dB reduction for music when a sweeper overlays the intro (e.g. -6 = duck by 6 dB)',
          },
          { withCredentials: true }
        )
      ]);
      setSaveStatus({ type: 'success', message: 'Audio engine settings saved' });
    } catch (error) {
      console.error('Error saving audio engine settings:', error);
      setSaveStatus({ type: 'error', message: error.response?.data?.message || 'Failed to save audio engine settings' });
    } finally {
      setLoading(false);
    }
  };

  // ===== Auto Delete Settings =====
  const fetchAutoDeleteRules = async () => {
    try {
      const res = await axios.get(`${API_URL}/api/auto-delete/rules`, { withCredentials: true });
      setAutoDeleteRules(res.data);
    } catch (e) {
      if (e.response?.status !== 403) console.error('Error fetching auto-delete rules:', e);
    }
  };

  const fetchAutoDeleteCategories = async () => {
    try {
      const res = await axios.get(`${API_URL}/api/auto-delete/categories`, { withCredentials: true });
      setAutoDeleteCategories(res.data);
    } catch (e) {
      if (e.response?.status !== 403) console.error('Error fetching auto-delete categories:', e);
    }
  };

  const fetchAutoDeletePreview = async () => {
    try {
      const res = await axios.get(`${API_URL}/api/auto-delete/preview`, { withCredentials: true });
      setAutoDeletePreview(res.data);
    } catch (e) {
      console.error('Error fetching auto-delete preview:', e);
    }
  };

  const handleSaveAutoDeleteRule = async () => {
    if (!autoDeleteForm.category_id) {
      setAutoDeleteMessage({ type: 'error', text: 'Please select a category' });
      return;
    }
    setAutoDeleteSaving(true);
    setAutoDeleteMessage({ type: '', text: '' });
    try {
      await axios.post(`${API_URL}/api/auto-delete/rules`, autoDeleteForm, { withCredentials: true });
      setAutoDeleteMessage({ type: 'success', text: 'Auto-delete rule saved' });
      setAutoDeleteDialogOpen(false);
      setAutoDeleteForm({ category_id: '', enabled: true, days_after_last_use: 60, delete_file: true });
      fetchAutoDeleteRules();
      fetchAutoDeleteCategories();
    } catch (e) {
      setAutoDeleteMessage({ type: 'error', text: e.response?.data?.message || 'Failed to save rule' });
    } finally {
      setAutoDeleteSaving(false);
    }
  };

  const handleToggleAutoDeleteRule = async (rule) => {
    try {
      await axios.put(`${API_URL}/api/auto-delete/rules/${rule.id}`, { enabled: !rule.enabled }, { withCredentials: true });
      fetchAutoDeleteRules();
    } catch (e) {
      setAutoDeleteMessage({ type: 'error', text: 'Failed to toggle rule' });
    }
  };

  const handleDeleteAutoDeleteRule = async (ruleId) => {
    if (!window.confirm('Remove this auto-delete rule? Assets in this category will no longer be auto-deleted.')) return;
    try {
      await axios.delete(`${API_URL}/api/auto-delete/rules/${ruleId}`, { withCredentials: true });
      setAutoDeleteMessage({ type: 'success', text: 'Rule removed' });
      fetchAutoDeleteRules();
      fetchAutoDeleteCategories();
    } catch (e) {
      setAutoDeleteMessage({ type: 'error', text: 'Failed to delete rule' });
    }
  };

  const handleRunAutoDelete = async () => {
    if (!window.confirm('This will permanently delete all assets that match the active auto-delete rules. This cannot be undone. Continue?')) return;
    setAutoDeleteRunning(true);
    setAutoDeleteMessage({ type: '', text: '' });
    try {
      const res = await axios.post(`${API_URL}/api/auto-delete/run`, {}, { withCredentials: true });
      const d = res.data;
      const mbInfo = d.mixblocksDeleted ? `, ${d.mixblocksDeleted} mixblock(s)` : '';
      setAutoDeleteMessage({
        type: 'success',
        text: `Cleanup complete: ${d.assetsDeleted} asset(s) deleted, ${d.filesRemoved} file(s) removed${mbInfo}${d.errors > 0 ? `, ${d.errors} error(s)` : ''}`
      });
      fetchAutoDeleteRules();
    } catch (e) {
      setAutoDeleteMessage({ type: 'error', text: e.response?.data?.message || 'Cleanup failed' });
    } finally {
      setAutoDeleteRunning(false);
    }
  };

  const fetchMixblockAutoDelete = async () => {
    try {
      const res = await axios.get(`${API_URL}/api/auto-delete/mixblock-settings`, { withCredentials: true });
      setMixblockAutoDelete({ enabled: res.data.enabled, days: res.data.days });
    } catch (e) {
      if (e.response?.status !== 403) console.error('Error fetching mixblock auto-delete settings:', e);
    }
  };

  const handleSaveMixblockAutoDelete = async () => {
    setMixblockAutoDeleteSaving(true);
    try {
      await axios.post(`${API_URL}/api/auto-delete/mixblock-settings`, mixblockAutoDelete, { withCredentials: true });
      setAutoDeleteMessage({ type: 'success', text: 'Generated mixblock cleanup settings saved' });
    } catch (e) {
      setAutoDeleteMessage({ type: 'error', text: e.response?.data?.message || 'Failed to save mixblock settings' });
    } finally {
      setMixblockAutoDeleteSaving(false);
    }
  };

  // ===== Audio Processing =====

  const fetchAudioProcessingStatus = async () => {
    try {
      const res = await axios.get(`${API_URL}/api/audio-processing/status`, { withCredentials: true });
      setAudioProcessingEnabled(res.data.enabled || false);
      setAudioProcessingInstances(res.data.instances || []);
    } catch (e) {
      if (e.response?.status !== 403 && e.response?.status !== 404) {
        console.error('Error fetching audio processing status:', e);
      }
    }
  };

  const fetchAudioProcessingStations = async () => {
    try {
      const res = await axios.get(`${API_URL}/api/audio-processing/stations`, { withCredentials: true });
      setAudioProcessingStations(res.data.stations || []);
    } catch (e) {
      if (e.response?.status !== 403) console.error('Error fetching audio processing stations:', e);
    }
  };

  const fetchAudioProcessingPresets = async () => {
    try {
      const res = await axios.get(`${API_URL}/api/audio-processing/presets`, { withCredentials: true });
      setAudioProcessingPresets(res.data.presets || []);
    } catch (e) {
      if (e.response?.status !== 403) console.error('Error fetching audio processing presets:', e);
    }
  };

  const fetchAudioProcessingCodecs = async () => {
    try {
      const res = await axios.get(`${API_URL}/api/audio-processing/codecs`, { withCredentials: true });
      setAudioProcessingCodecs(res.data || { profiles: {}, capabilities: {} });
    } catch (e) {
      if (e.response?.status !== 403 && e.response?.status !== 404) {
        console.error('Error fetching audio output codecs:', e);
      }
    }
  };

  const AP_EMPTY_OUTPUT = () => ({
    _tempId: Date.now() + Math.random(),
    id: null,
    name: '',
    enabled: true,
    provider: 'icecast',
    host: '',
    port: 8000,
    mount: '/stream',
    username: 'source',
    password: '',
    format: 'mp3',
    bitrate: 320,
    custom_url: '',
  });

  const handleOpenAudioProcessingDialog = (station, instance) => {
    const existingOutputs = (instance?.outputs || []).map((o) => ({
      _tempId: o.id,
      id: o.id,
      name: o.name || '',
      enabled: o.enabled !== false,
      provider: o.provider || 'icecast',
      host: o.host || '',
      port: o.port || 8000,
      mount: o.mount || '/stream',
      username: o.username || 'source',
      password: o.password || '',
      format: o.format || 'mp3',
      bitrate: o.bitrate == null ? null : o.bitrate,
      custom_url: o.custom_url || '',
    }));
    setAudioProcessingForm({ preset: instance?.preset || 'default', outputs: existingOutputs });
    setAudioProcessingRemovedOutputIds([]);
    setAudioProcessingDialog({ open: true, station, instance: instance || null });
  };

  const apUpdateOutput = (tempId, field, value) => {
    setAudioProcessingForm((prev) => ({
      ...prev,
      outputs: prev.outputs.map((o) => o._tempId === tempId ? { ...o, [field]: value } : o),
    }));
  };

  const apUpdateOutputFormat = (tempId, format) => {
    const defaults = { mp3: 320, aac_lc: 192, aac_he_v2: 48, opus: 192, flac: null };
    setAudioProcessingForm((prev) => ({
      ...prev,
      outputs: prev.outputs.map((output) => output._tempId === tempId
        ? { ...output, format, bitrate: defaults[format] }
        : output),
    }));
  };

  const apAddOutput = () => {
    if (audioProcessingForm.outputs.length >= 5) return;
    const order = audioProcessingForm.outputs.length + 1;
    setAudioProcessingForm((prev) => ({
      ...prev,
      outputs: [...prev.outputs, { ...AP_EMPTY_OUTPUT(), name: `Output ${order}` }],
    }));
  };

  const apRemoveOutput = (tempId, existingId) => {
    if (existingId) {
      setAudioProcessingRemovedOutputIds((prev) => [...prev, existingId]);
    }
    setAudioProcessingForm((prev) => ({
      ...prev,
      outputs: prev.outputs.filter((o) => o._tempId !== tempId),
    }));
  };

  const handleSaveAudioProcessingInstance = async () => {
    const { station, instance } = audioProcessingDialog;
    try {
      setAudioProcessingLoading(true);

      // 1. Save or create the instance
      let instanceId;
      if (instance) {
        await axios.put(
          `${API_URL}/api/audio-processing/instances/${instance.id}`,
          { preset: audioProcessingForm.preset },
          { withCredentials: true }
        );
        instanceId = instance.id;
      } else {
        const targetPayload = station.target_type === 'region'
          ? { region_id: station.region_id }
          : { station_id: station.station_id || station.id };
        const r = await axios.post(
          `${API_URL}/api/audio-processing/instances`,
          { ...targetPayload, preset: audioProcessingForm.preset },
          { withCredentials: true }
        );
        instanceId = r.data.instance.id;
      }

      // 2. Delete removed outputs
      for (const oid of audioProcessingRemovedOutputIds) {
        await axios.delete(`${API_URL}/api/audio-processing/outputs/${oid}`, { withCredentials: true })
          .catch(() => { /* ignore if already gone */ });
      }

      // 3. Create new / update existing outputs
      for (const o of audioProcessingForm.outputs) {
        const payload = {
          name: o.name, enabled: o.enabled, provider: o.provider,
          host: o.host, port: o.port, mount: o.mount,
          username: o.username, password: o.password,
          format: o.format, bitrate: o.bitrate, custom_url: o.custom_url,
        };
        if (o.id) {
          await axios.put(`${API_URL}/api/audio-processing/outputs/${o.id}`, payload, { withCredentials: true });
        } else {
          await axios.post(`${API_URL}/api/audio-processing/instances/${instanceId}/outputs`, payload, { withCredentials: true });
        }
      }

      setAudioProcessingDialog({ open: false, station: null, instance: null });
      setAudioProcessingMessage({ type: 'success', text: 'Configuration saved.' });
      await fetchAudioProcessingStatus();
      await fetchAudioProcessingStations();
    } catch (e) {
      setAudioProcessingMessage({ type: 'error', text: e.response?.data?.message || 'Failed to save configuration.' });
    } finally {
      setAudioProcessingLoading(false);
    }
  };

  const handleDeleteAudioProcessingInstance = async (id) => {
    if (!window.confirm('Delete this audio processing configuration?')) return;
    try {
      await axios.delete(`${API_URL}/api/audio-processing/instances/${id}`, { withCredentials: true });
      setAudioProcessingMessage({ type: 'success', text: 'Configuration deleted.' });
      await fetchAudioProcessingStatus();
      await fetchAudioProcessingStations();
    } catch (e) {
      setAudioProcessingMessage({ type: 'error', text: e.response?.data?.message || 'Failed to delete.' });
    }
  };

  const handleStartAudioProcessing = async (id) => {
    try {
      setAudioProcessingLoading(true);
      await axios.post(`${API_URL}/api/audio-processing/instances/${id}/start`, {}, { withCredentials: true });
      setAudioProcessingMessage({ type: 'success', text: 'Processing started.' });
      await fetchAudioProcessingStatus();
    } catch (e) {
      setAudioProcessingMessage({ type: 'error', text: e.response?.data?.message || 'Failed to start.' });
    } finally {
      setAudioProcessingLoading(false);
    }
  };

  const handleStopAudioProcessing = async (id) => {
    try {
      setAudioProcessingLoading(true);
      await axios.post(`${API_URL}/api/audio-processing/instances/${id}/stop`, {}, { withCredentials: true });
      setAudioProcessingMessage({ type: 'success', text: 'Processing stopped.' });
      await fetchAudioProcessingStatus();
    } catch (e) {
      setAudioProcessingMessage({ type: 'error', text: e.response?.data?.message || 'Failed to stop.' });
    } finally {
      setAudioProcessingLoading(false);
    }
  };

  const handleViewAudioProcessingLogs = async (id) => {
    try {
      const res = await axios.get(`${API_URL}/api/audio-processing/instances/${id}/logs?lines=200`, { withCredentials: true });
      setAudioProcessingLogs(res.data.logs || '(no log output yet)');
      setAudioProcessingLogsOpen(id);
    } catch (e) {
      setAudioProcessingLogs('Failed to load logs.');
      setAudioProcessingLogsOpen(id);
    }
  };

  return (
    <Box>
      <PageHeader
        overline="System"
        title="Settings"
        subtitle="Configure stations, app features, integrations and system preferences"
        icon={<SettingsIcon />}
      />

      {saveStatus.message && (
        <Alert 
          severity={saveStatus.type} 
          sx={{ mb: 3 }}
          onClose={() => setSaveStatus({ type: '', message: '' })}
        >
          {saveStatus.message}
        </Alert>
      )}

      {(() => {
        // Build tabs dynamically based on permissions
        const tabs = [];
        if (hasStationManagement()) {
          tabs.push({ key: 'station', icon: <RadioIcon />, label: 'Station and App Configuration' });
        }
        if (isSuperAdmin() || isAdmin()) {
          if (systemFeatures.ai_content_enabled) {
            tabs.push({ key: 'ai_features', icon: <AIIcon />, label: 'AI Features' });
          }
          tabs.push({ key: 'audio_engine', icon: <AudioIcon />, label: 'Audio Engine' });
          if (systemFeatures.audio_processing_enabled) {
            tabs.push({ key: 'audio_processing', icon: <EqualizerIcon />, label: 'Audio Output' });
          }
          if (systemFeatures.webrtc_contribution_enabled) {
            tabs.push({ key: 'webrtc_contrib', icon: <RadioIcon />, label: 'WebRTC Contribution' });
          }
          if (systemFeatures.studio_output_enabled) {
            tabs.push({ key: 'studio_mode', icon: <RadioIcon />, label: 'Studio Output' });
          }
          if (systemFeatures.text_output_enabled) {
            tabs.push({ key: 'text_output', icon: <TextOutputIcon />, label: 'Text Output' });
          }
          tabs.push({ key: 'dropbox', icon: <DropboxIcon />, label: 'Dropbox' });
          tabs.push({ key: 'security', icon: <SecurityIcon />, label: 'Security / MFA' });
          tabs.push({ key: 'storage', icon: <StorageIcon />, label: 'Storage' });
          tabs.push({ key: 'branding', icon: <ImageIcon />, label: 'Branding' });
          if (isSuperAdmin()) {
            tabs.push({ key: 'email', icon: <EmailIcon />, label: 'Email / SMTP' });
          }
          tabs.push({ key: 'users', icon: <PeopleIcon />, label: 'User Management' });
          tabs.push({ key: 'audit_trail', icon: <AuditIcon />, label: 'Audit Trail' });
          tabs.push({ key: 'auto_delete', icon: <AutoDeleteIcon />, label: 'Auto Delete' });
          tabs.push({ key: 'playback_log', icon: <AutoDeleteIcon />, label: 'Playback Log' });
          // Show Backup tab unless superadmin has hidden it from admins
          const backupHidden = backupConfig.backup_hide_from_admin === 'true' && !isSuperAdmin();
          if (!backupHidden) {
            tabs.push({ key: 'backup', icon: <BackupIcon />, label: 'Backup' });
          }
        }
        if (
          !(isSuperAdmin() || isAdmin())
          && systemFeatures.studio_output_enabled
          && canViewStudioOutput
        ) {
          tabs.push({ key: 'studio_mode', icon: <RadioIcon />, label: 'Studio Output' });
        }
        if (isSuperAdmin()) {
          tabs.push({ key: 'gateway', icon: <CloudDone />, label: 'Gateway' });
          if (syndicationEnabled) {
            tabs.push({ key: 'franchise_syndication', icon: <LinkIcon />, label: 'Franchise Syndication' });
          }
          if (systemFeatures.logger_enabled) {
            tabs.push({ key: 'logger', icon: <LoggerIcon />, label: 'Logger' });
          }
          tabs.push({ key: 'proxy', icon: <LinkIcon />, label: 'URL & proxy' });
          tabs.push({ key: 'domain_ssl', icon: <SslIcon />, label: 'Domain & SSL' });
          if (systemFeatures.multi_node_enabled) {
            tabs.push({ key: 'server_nodes', icon: <StorageIcon />, label: 'Server Nodes' });
          }
        }

        const requestedTabValue = tabs.findIndex((tab) => tab.key === requestedTabKey);
        const safeTabValue = requestedTabValue >= 0
          ? requestedTabValue
          : (
            tabValue >= 0 && tabValue < tabs.length
              ? tabValue
              : (tabs.length === 1 ? 0 : -1)
          );
        const activeTab = safeTabValue >= 0 ? tabs[safeTabValue]?.key : null;

        const openSettingsTab = (nextTabValue) => {
          const index = Number(nextTabValue);
          const safeIndex = Number.isInteger(index) ? index : -1;
          setTabValue(safeIndex);
          const nextParams = new URLSearchParams(searchParams);
          if (safeIndex < 0 || !tabs[safeIndex]) {
            nextParams.delete('tab');
          } else {
            nextParams.set('tab', tabs[safeIndex].key);
          }
          setSearchParams(nextParams, { replace: true });
        };

        if (tabs.length === 0) {
          return (
            <Alert severity="info">
              Your account does not have access to any settings pages.
            </Alert>
          );
        }

        return (
          <>
            <FormControl fullWidth sx={{ mb: 3 }}>
              <Select
                value={safeTabValue}
                onChange={(e) => openSettingsTab(e.target.value)}
                displayEmpty
                renderValue={(val) => {
                  if (val === -1) {
                    return (
                      <Typography sx={{ color: '#666', fontStyle: 'italic' }}>
                        Select a setting…
                      </Typography>
                    );
                  }
                  const tab = tabs[val];
                  return tab ? (
                    <Box sx={{ display: 'flex', alignItems: 'center', gap: 1.5 }}>
                      <Box sx={{ color: '#22d3ee', display: 'flex', alignItems: 'center' }}>{tab.icon}</Box>
                      <Typography sx={{ color: 'var(--rs-text)' }}>{tab.label}</Typography>
                    </Box>
                  ) : null;
                }}
                sx={{
                  bgcolor: 'var(--rs-paper)',
                  color: 'var(--rs-text)',
                  '& .MuiOutlinedInput-notchedOutline': { borderColor: 'var(--rs-border)' },
                  '&:hover .MuiOutlinedInput-notchedOutline': { borderColor: '#22d3ee' },
                  '&.Mui-focused .MuiOutlinedInput-notchedOutline': { borderColor: '#22d3ee' },
                  '& .MuiSvgIcon-root': { color: 'var(--rs-muted)' },
                }}
                MenuProps={{
                  PaperProps: {
                    sx: {
                      bgcolor: 'var(--rs-paper)',
                      border: '1px solid var(--rs-border)',
                      '& .MuiMenuItem-root': {
                        color: 'var(--rs-text)',
                        '&:hover': { bgcolor: 'var(--rs-surface)' },
                        '&.Mui-selected': { bgcolor: 'rgba(34,211,238,0.16)', '&:hover': { bgcolor: 'rgba(34,211,238,0.22)' } },
                      },
                    },
                  },
                }}
              >
                {(tabs.length > 1 || safeTabValue < 0) && (
                  <MenuItem value={-1} disabled={safeTabValue < 0} sx={{ color: '#666', fontStyle: 'italic' }}>
                    {safeTabValue < 0 ? 'Select a setting…' : 'All settings'}
                  </MenuItem>
                )}
                {tabs.map((tab, idx) => (
                  <MenuItem key={tab.key} value={idx}>
                    <Box sx={{ display: 'flex', alignItems: 'center', gap: 1.5 }}>
                      <Box sx={{ color: safeTabValue === idx ? '#22d3ee' : 'var(--rs-muted)', display: 'flex', alignItems: 'center' }}>
                        {tab.icon}
                      </Box>
                      <Typography sx={{ color: safeTabValue === idx ? '#22d3ee' : 'var(--rs-text)' }}>
                        {tab.label}
                      </Typography>
                    </Box>
                  </MenuItem>
                ))}
              </Select>
            </FormControl>

            {activeTab == null && (
              <Box>
                <Typography variant="body2" sx={{ color: 'var(--rs-muted)', mb: 2 }}>
                  Open any setting your account can access.
                </Typography>
                <Grid container spacing={2}>
                  {tabs.map((tab, idx) => (
                    <Grid item xs={12} sm={6} md={4} lg={3} key={tab.key}>
                      <Card
                        sx={{
                          height: '100%',
                          bgcolor: 'var(--rs-paper)',
                          border: '1px solid var(--rs-border)',
                          transition: 'border-color 160ms ease, box-shadow 160ms ease',
                          '&:hover': {
                            borderColor: '#22d3ee',
                            boxShadow: '0 12px 28px -18px rgba(34,211,238,0.7)',
                          },
                          '&:focus-within': {
                            borderColor: '#22d3ee',
                          },
                        }}
                      >
                        <CardActionArea
                          onClick={() => openSettingsTab(idx)}
                          sx={{ height: '100%', alignItems: 'stretch' }}
                        >
                          <CardContent sx={{ display: 'flex', gap: 1.75, alignItems: 'flex-start', height: '100%' }}>
                            <Box
                              sx={{
                                width: 48,
                                height: 48,
                                borderRadius: '12px',
                                display: 'flex',
                                alignItems: 'center',
                                justifyContent: 'center',
                                flexShrink: 0,
                                color: '#22d3ee',
                                background: 'linear-gradient(145deg, rgba(34,211,238,0.22), rgba(59,130,246,0.12))',
                                border: '1px solid rgba(34,211,238,0.38)',
                                '& .MuiSvgIcon-root': { fontSize: 26 },
                              }}
                            >
                              {tab.icon}
                            </Box>
                            <Box sx={{ minWidth: 0 }}>
                              <Typography sx={{ fontWeight: 600, color: 'var(--rs-text)', lineHeight: 1.3, mb: 0.5 }}>
                                {tab.label}
                              </Typography>
                              <Typography variant="body2" sx={{ color: 'var(--rs-muted)' }}>
                                {SETTINGS_SECTION_HINTS[tab.key]}
                              </Typography>
                            </Box>
                          </CardContent>
                        </CardActionArea>
                      </Card>
                    </Grid>
                  ))}
                </Grid>
              </Box>
            )}

      {/* Station and App Configuration Tab */}
      {activeTab === 'station' && (
        <Box>
          <Typography variant="h6" sx={{ color: '#22d3ee', mb: 1 }}>
            Station and App Configuration
          </Typography>
          <Typography variant="body2" sx={{ color: 'var(--rs-muted)', mb: 3 }}>
            Manage stations and enable or disable product features for this installation. Disabled features are hidden from navigation and permissions for all users.
          </Typography>

          <Card sx={{ bgcolor: 'var(--rs-paper)', border: '1px solid #22d3ee55', mb: 3 }}>
            <CardContent>
              <Typography variant="h6" sx={{ color: '#22d3ee', mb: 1 }}>
                App Features
              </Typography>
              <Typography variant="body2" sx={{ color: 'var(--rs-muted)', mb: 3 }}>
                Superadmin-only controls. Turning a feature off hides it for admins and users across the app.
              </Typography>

              {featuresMessage.text && (
                <Alert
                  severity={featuresMessage.type === 'error' ? 'error' : 'success'}
                  sx={{ mb: 2 }}
                  onClose={() => setFeaturesMessage({ type: '', text: '' })}
                >
                  {featuresMessage.text}
                </Alert>
              )}

              <Grid container spacing={2}>
                {APP_FEATURE_TOGGLES.map((toggle) => {
                  const grokApiKeyConfigured = (typeof hasGrokApiKey === 'function' && hasGrokApiKey())
                    || aiConfig.has_grok_api_key === true;
                  const insightsLocked = toggle.key === 'ai_insights_enabled' && !grokApiKeyConfigured;
                  const insightsEnableLocked = insightsLocked && !systemFeatures[toggle.key];
                  return (
                    <Grid item xs={12} sm={6} key={toggle.key}>
                      <FormControlLabel
                        disabled={insightsEnableLocked}
                        control={
                          <Switch
                            checked={!!systemFeatures[toggle.key]}
                            disabled={insightsEnableLocked}
                            onChange={(e) => {
                              if (toggle.key === 'ai_insights_enabled' && e.target.checked && !grokApiKeyConfigured) {
                                return;
                              }
                              setSystemFeatures((prev) => ({ ...prev, [toggle.key]: e.target.checked }));
                            }}
                            sx={{
                              '& .MuiSwitch-switchBase.Mui-checked': { color: '#22d3ee' },
                              '& .MuiSwitch-switchBase.Mui-checked + .MuiSwitch-track': { bgcolor: '#22d3ee' },
                            }}
                          />
                        }
                        label={
                          <Box>
                            <Typography variant="body2" sx={{ fontWeight: 600 }}>{toggle.label}</Typography>
                            <Typography variant="caption" sx={{ color: 'var(--rs-muted)' }}>
                              {toggle.description}
                            </Typography>
                          </Box>
                        }
                      />
                    </Grid>
                  );
                })}
                <Grid item xs={12}>
                  <Button
                    variant="contained"
                    onClick={handleSaveSystemFeatures}
                    disabled={featuresSaving}
                    sx={{ bgcolor: '#22d3ee', '&:hover': { bgcolor: '#00b8e6' } }}
                  >
                    {featuresSaving ? 'Saving…' : 'Save App Features'}
                  </Button>
                </Grid>
              </Grid>
            </CardContent>
          </Card>

          <Box sx={{ display: 'flex', justifyContent: 'space-between', mb: 3, alignItems: 'center' }}>
            <Typography variant="h6" sx={{ color: '#22d3ee' }}>
              Stations
            </Typography>
            <Button
              variant="contained"
              startIcon={<Add />}
              onClick={() => handleOpenStationDialog()}
              sx={{ bgcolor: '#22d3ee', '&:hover': { bgcolor: '#00b8e6' } }}
            >
              New Station
            </Button>
          </Box>

          <Card sx={{ bgcolor: 'var(--rs-paper)', border: '1px solid var(--rs-border)', mb: 3 }}>
            <CardContent>
              <Typography variant="subtitle1" sx={{ color: '#22d3ee', mb: 2 }}>
                Stream Output URLs
              </Typography>
              {hideServerInfo && !isSuperAdmin() ? (
                <Typography color="text.secondary" sx={{ py: 2 }}>
                  Stream URLs are hidden by administrator.
                </Typography>
              ) : outputUrls.length > 0 ? (
                <TableContainer>
                  <Table size="small">
                    <TableHead>
                      <TableRow>
                        <TableCell sx={{ color: '#22d3ee' }}>Output</TableCell>
                        <TableCell sx={{ color: '#22d3ee' }}>URL</TableCell>
                        <TableCell sx={{ color: '#22d3ee', width: 60 }}></TableCell>
                      </TableRow>
                    </TableHead>
                    <TableBody>
                      {outputUrls.map((item, idx) => {
                        let displayUrl = item.url;
                        if (proxyPublicUrl.trim()) {
                          try {
                            const pathname = new URL(item.url).pathname;
                            displayUrl = proxyPublicUrl.replace(/\/$/, '') + pathname;
                          } catch (_) {}
                        }
                        return (
                          <TableRow key={idx}>
                            <TableCell sx={{ color: 'text.primary' }}>{item.label}</TableCell>
                            <TableCell>
                              <Typography
                                component="a"
                                href={displayUrl}
                                target="_blank"
                                rel="noopener noreferrer"
                                sx={{ color: '#22d3ee', textDecoration: 'none', '&:hover': { textDecoration: 'underline' }, wordBreak: 'break-all' }}
                              >
                                {displayUrl}
                              </Typography>
                            </TableCell>
                            <TableCell>
                              <IconButton size="small" onClick={() => handleCopyUrl(displayUrl)} sx={{ color: '#22d3ee' }} title="Copy URL">
                                <ContentCopy fontSize="small" />
                              </IconButton>
                            </TableCell>
                          </TableRow>
                        );
                      })}
                    </TableBody>
                  </Table>
                </TableContainer>
              ) : (
                <Typography color="text.secondary" sx={{ py: 2 }}>
                  No stream outputs yet. Add a station to get a direct stream URL (edit station → Stream Output). Regions add split streams.
                </Typography>
              )}
            </CardContent>
          </Card>

          {stations.length === 0 ? (
            <Card sx={{ bgcolor: 'var(--rs-paper)', border: '1px solid var(--rs-border)' }}>
              <CardContent>
                <Typography color="text.secondary" align="center" sx={{ py: 4 }}>
                  No stations configured. Create your first station to get started.
                </Typography>
              </CardContent>
            </Card>
          ) : (
            <Grid container spacing={2}>
              {stations.map((station) => (
                <Grid item xs={12} md={6} lg={4} key={station.id}>
                  <Card
                    sx={{
                      bgcolor: 'var(--rs-paper)',
                      border: '1px solid var(--rs-border)',
                      '&:hover': {
                        borderColor: '#22d3ee',
                      },
                    }}
                  >
                    <CardContent>
                      <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'start', mb: 2 }}>
                        <Box>
                          <Box sx={{ display: 'flex', alignItems: 'center', gap: 1, mb: 1 }}>
                            <RadioIcon sx={{ color: '#22d3ee' }} />
                            <Typography variant="h6" sx={{ color: '#22d3ee' }}>
                              {station.name}
                            </Typography>
                          </Box>
                          {station.call_sign && (
                            <Typography variant="body2" color="text.secondary">
                              {station.call_sign}
                            </Typography>
                          )}
                          {station.frequency && (
                            <Typography variant="body2" color="text.secondary">
                              {station.frequency}
                            </Typography>
                          )}
                          {(station.location || station.country) && (
                            <Typography variant="body2" color="text.secondary">
                              {[station.location, station.country].filter(Boolean).join(', ')}
                            </Typography>
                          )}
                        </Box>
                        <Box>
                          <Chip
                            label={station.is_active ? 'Active' : 'Inactive'}
                            size="small"
                            sx={{
                              bgcolor: station.is_active ? '#22d3ee20' : '#666',
                              color: station.is_active ? '#22d3ee' : '#999',
                            }}
                          />
                        </Box>
                      </Box>

                      {(station.direct_stream_url || station.icecast_mountpoint) && (
                        <Box sx={{ mt: 2, p: 1.5, bgcolor: 'var(--rs-bg)', borderRadius: 1 }}>
                          <Typography variant="caption" color="text.secondary" sx={{ display: 'block' }}>
                            MP3 @ 320kbps · 48kHz
                          </Typography>
                          {(station.direct_stream_url || station.stream_url) && (
                            <Typography variant="caption" color="text.secondary" sx={{ display: 'block' }}>
                              Stream: {station.direct_stream_url || station.stream_url}
                            </Typography>
                          )}
                        </Box>
                      )}

                      <Box sx={{ display: 'flex', gap: 1, mt: 2 }}>
                        <IconButton
                          size="small"
                          onClick={() => handleOpenStationDialog(station)}
                          sx={{ color: '#22d3ee' }}
                        >
                          <Edit fontSize="small" />
                        </IconButton>
                        <IconButton
                          size="small"
                          onClick={() => handleDeleteStation(station.id)}
                          sx={{ color: '#ff6b6b' }}
                        >
                          <Delete fontSize="small" />
                        </IconButton>
                      </Box>
                    </CardContent>
                  </Card>
                </Grid>
              ))}
            </Grid>
          )}

          {/* Station Dialog */}
          <Dialog open={stationDialogOpen} onClose={handleCloseStationDialog} maxWidth="md" fullWidth>
            <DialogTitle>
              <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                <RadioIcon sx={{ color: '#22d3ee' }} />
                <Typography variant="h6">
                  {editingStation ? 'Edit Station' : 'New Station'}
                </Typography>
              </Box>
            </DialogTitle>
            <DialogContent>
              <Tabs value={stationTabValue} onChange={(e, v) => setStationTabValue(v)} sx={{ mb: 3 }}>
                <Tab label="Basic Info" />
                <Tab label="Stream Output" />
                {editingStation && (
                  <Tab
                    label={
                      <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                        Regions
                        {stationFormData.mode === 'multi-region' && (
                          <Chip label="Splits" size="small" sx={{ height: 18, fontSize: '0.7rem' }} />
                        )}
                      </Box>
                    }
                  />
                )}
              </Tabs>

              {stationTabValue === 0 && (
                <Grid container spacing={2}>
                  <Grid item xs={12}>
                    <TextField
                      fullWidth
                      label="Station Name *"
                      value={stationFormData.name}
                      onChange={(e) => setStationFormData({ ...stationFormData, name: e.target.value })}
                      required
                    />
                  </Grid>
                  <Grid item xs={12} sm={6}>
                    <TextField
                      fullWidth
                      label="Call Sign"
                      value={stationFormData.call_sign}
                      onChange={(e) => setStationFormData({ ...stationFormData, call_sign: e.target.value })}
                    />
                  </Grid>
                  <Grid item xs={12} sm={6}>
                    <TextField
                      fullWidth
                      label="Frequency"
                      value={stationFormData.frequency}
                      onChange={(e) => setStationFormData({ ...stationFormData, frequency: e.target.value })}
                      placeholder="e.g., 101.5 FM"
                    />
                  </Grid>
                  <Grid item xs={12} sm={6}>
                    <FormControl fullWidth>
                      <InputLabel>Country</InputLabel>
                      <Select
                        label="Country"
                        value={stationFormData.country || ''}
                        onChange={(e) => setStationFormData({ ...stationFormData, country: e.target.value })}
                      >
                        <MenuItem value="">Not set</MenuItem>
                        {stationFormData.country && !STATION_COUNTRIES.includes(stationFormData.country) && (
                          <MenuItem value={stationFormData.country}>{stationFormData.country}</MenuItem>
                        )}
                        {STATION_COUNTRIES.map((country) => (
                          <MenuItem key={country} value={country}>{country}</MenuItem>
                        ))}
                      </Select>
                    </FormControl>
                  </Grid>
                  <Grid item xs={12} sm={6}>
                    <TextField
                      fullWidth
                      label="City / market"
                      value={stationFormData.location || ''}
                      onChange={(e) => setStationFormData({ ...stationFormData, location: e.target.value })}
                      placeholder="e.g. Manchester"
                      helperText="Used by Intelligence for charts and new releases"
                    />
                  </Grid>
                  <Grid item xs={12} sm={6}>
                    <FormControlLabel
                      control={
                        <Switch
                          checked={stationFormData.is_active}
                          onChange={(e) => setStationFormData({ ...stationFormData, is_active: e.target.checked })}
                        />
                      }
                      label="Active"
                    />
                  </Grid>
                  <Grid item xs={12}>
                    <FormControlLabel
                      control={
                        <Switch
                          checked={stationFormData.mode === 'multi-region'}
                          onChange={(e) =>
                            setStationFormData({
                              ...stationFormData,
                              mode: e.target.checked ? 'multi-region' : 'single',
                            })
                          }
                        />
                      }
                      label="Enable Regional Splits (Split Ads & Voicetracks)"
                    />
                    <Typography variant="caption" color="text.secondary" sx={{ display: 'block', ml: 7, mt: 0.5 }}>
                      When enabled, add regions in the Regions tab. Each region gets its own stream with region-specific ads and voicetracks.
                    </Typography>
                  </Grid>
                  <Grid item xs={12}>
                    <Typography variant="subtitle1" sx={{ color: '#22d3ee', mt: 2, mb: 1 }}>
                      Network Mode
                    </Typography>
                    <FormControl fullWidth sx={{ mb: 2 }}>
                      <InputLabel>Mode</InputLabel>
                      <Select
                        value={stationNetworkConfig.mode}
                        label="Mode"
                        onChange={(e) =>
                          setStationNetworkConfig((prev) => ({
                            ...prev,
                            mode: e.target.value,
                            master_station_id: e.target.value === 'independent' ? null : prev.master_station_id,
                          }))
                        }
                      >
                        <MenuItem value="independent">Independent (Own clocks & playlists)</MenuItem>
                        <MenuItem value="inherit">Inherit from Master Station</MenuItem>
                      </Select>
                    </FormControl>
                    {stationNetworkConfig.mode === 'inherit' && (
                      <FormControl fullWidth>
                        <InputLabel>Master Station</InputLabel>
                        <Select
                          value={stationNetworkConfig.master_station_id || ''}
                          label="Master Station"
                          onChange={(e) =>
                            setStationNetworkConfig((prev) => ({
                              ...prev,
                              master_station_id: e.target.value || null,
                            }))
                          }
                        >
                          <MenuItem value="">
                            <em>Select master station</em>
                          </MenuItem>
                          {stations
                            .filter((s) => !editingStation || s.id !== editingStation.id)
                            .map((s) => (
                              <MenuItem key={s.id} value={s.id}>
                                {s.name}
                              </MenuItem>
                            ))}
                        </Select>
                      </FormControl>
                    )}
                  </Grid>
                </Grid>
              )}

              {stationTabValue === 1 && (
                <Grid container spacing={2}>
                  {(editingStation || stationFormData.id) && (
                    <Grid item xs={12}>
                      <Box sx={{ p: 1.5, bgcolor: 'var(--rs-bg)', borderRadius: 1, mb: 2 }}>
                        <Typography variant="caption" color="text.secondary" sx={{ display: 'block', mb: 0.5 }}>
                          Listen URL (direct stream, no Icecast — 320k MP3 48kHz, program + mic)
                        </Typography>
                        <Box sx={{ display: 'flex', alignItems: 'center', gap: 1, flexWrap: 'wrap' }}>
                          <Typography
                            component="a"
                            href={stations.find((s) => s.id === (editingStation || stationFormData.id))?.direct_stream_url || `${API_URL}/api/stream/direct/${editingStation || stationFormData.id}.mp3`}
                            target="_blank"
                            rel="noopener noreferrer"
                            sx={{ color: '#22d3ee', wordBreak: 'break-all', fontSize: '0.875rem' }}
                          >
                            {stations.find((s) => s.id === (editingStation || stationFormData.id))?.direct_stream_url || `${API_URL}/api/stream/direct/${editingStation || stationFormData.id}.mp3`}
                          </Typography>
                          <IconButton
                            size="small"
                            onClick={() => handleCopyUrl(stations.find((s) => s.id === (editingStation || stationFormData.id))?.direct_stream_url || `${API_URL}/api/stream/direct/${editingStation || stationFormData.id}.mp3`)}
                            sx={{ color: '#22d3ee' }}
                            title="Copy URL"
                          >
                            <ContentCopy fontSize="small" />
                          </IconButton>
                        </Box>
                      </Box>
                    </Grid>
                  )}
                  <Grid item xs={12} sm={6}>
                    <TextField
                      fullWidth
                      label="Mountpoint (Icecast fallback)"
                      placeholder="/my-station"
                      value={stationFormData.icecast_mountpoint}
                      onChange={(e) => setStationFormData({ ...stationFormData, icecast_mountpoint: e.target.value })}
                      helperText="Optional Icecast mount; primary listen URL is the direct stream above"
                    />
                  </Grid>
                  <Grid item xs={12} sm={3}>
                    <TextField
                      fullWidth
                      label="Internal Source Rate"
                      value={playoutAudioFormat === 'opus_320' ? '320 kbps' : 'Lossless'}
                      disabled
                      helperText="Controlled globally by the superadmin"
                    />
                  </Grid>
                  <Grid item xs={12} sm={3}>
                    <TextField
                      fullWidth
                      label="Internal Source Format"
                      value={playoutAudioFormat === 'opus_320' ? 'Opus' : 'FLAC'}
                      disabled
                      helperText="Set under Storage"
                    />
                  </Grid>

                  <Grid item xs={12} sm={6}>
                    <TextField
                      fullWidth
                      label="Stream Title"
                      placeholder="My Radio Station"
                      value={stationFormData.icecast_stream_title}
                      onChange={(e) => setStationFormData({ ...stationFormData, icecast_stream_title: e.target.value })}
                    />
                  </Grid>
                  <Grid item xs={12} sm={3}>
                    <TextField
                      fullWidth
                      label="Genre"
                      value={stationFormData.icecast_stream_genre}
                      onChange={(e) => setStationFormData({ ...stationFormData, icecast_stream_genre: e.target.value })}
                    />
                  </Grid>
                  <Grid item xs={12} sm={3}>
                    <TextField
                      fullWidth
                      label="Description"
                      value={stationFormData.icecast_stream_description}
                      onChange={(e) => setStationFormData({ ...stationFormData, icecast_stream_description: e.target.value })}
                    />
                  </Grid>

                  <Grid item xs={12}>
                    <FormControlLabel
                      control={
                        <Switch
                          checked={stationFormData.icecast_metadata_enabled}
                          onChange={(e) => setStationFormData({ ...stationFormData, icecast_metadata_enabled: e.target.checked })}
                          color="primary"
                        />
                      }
                      label="Send Now Playing Metadata"
                    />
                    <Typography variant="caption" color="text.secondary" sx={{ display: 'block', ml: 7, mt: -0.5 }}>
                      Pushes artist &amp; title to Icecast for categories that have metadata passing enabled
                    </Typography>
                  </Grid>

                  {isSuperAdmin && (
                    <Grid item xs={12}>
                      <Divider sx={{ my: 1, borderColor: 'rgba(255,255,255,0.1)' }} />
                      <FormControlLabel
                        control={
                          <Switch
                            checked={stationFormData.beatmix_enabled || false}
                            onChange={(e) => setStationFormData({ ...stationFormData, beatmix_enabled: e.target.checked })}
                            color="secondary"
                          />
                        }
                        label="Enable Beat Mixing Engine"
                      />
                      <Typography variant="caption" color="text.secondary" sx={{ display: 'block', ml: 7, mt: -0.5 }}>
                        Enables DJ-style beat mixing with Rekordbox cue points, mixblocks in clocks, and automated transitions. Superadmin only.
                      </Typography>
                    </Grid>
                  )}
                </Grid>
              )}

              {/* Regions Tab */}
              {stationTabValue === 2 && editingStation && (
                <Box>
                  <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', mb: 2, flexWrap: 'wrap', gap: 1 }}>
                    <Typography variant="subtitle1" sx={{ color: '#22d3ee' }}>
                      Regional Split Configuration
                    </Typography>
                    <Box sx={{ display: 'flex', gap: 1 }}>
                      {regions.some((r) => !r.icecast_enabled || !r.icecast_mountpoint) && (
                        <Button
                          variant="outlined"
                          size="small"
                          onClick={handleAutoConfigureAllRegions}
                          disabled={loading}
                          sx={{ borderColor: '#22c55e', color: '#22c55e' }}
                        >
                          Auto-configure Icecast for all regions
                        </Button>
                      )}
                      <Button
                        variant="outlined"
                        size="small"
                        startIcon={<Add />}
                        onClick={() => handleOpenRegionDialog()}
                        disabled={stationFormData.mode !== 'multi-region'}
                        sx={{ borderColor: '#22d3ee', color: '#22d3ee' }}
                      >
                        Add Region
                      </Button>
                    </Box>
                  </Box>

                  {stationFormData.mode !== 'multi-region' ? (
                    <Alert severity="warning" sx={{ mb: 2 }}>
                      <Typography variant="body2">
                        Enable <strong>Regional Splits (Split Ads &amp; Voicetracks)</strong> in the Basic Info tab first,
                        then add regions here. Each region gets its own Icecast stream with region-specific ads and voicetracks.
                      </Typography>
                    </Alert>
                  ) : (
                    <Alert severity="info" sx={{ mb: 2 }}>
                      <Typography variant="body2">
                        Regions enable content splitting: separate voicetracks, ads, and Icecast streams per region.
                        Each region gets its own mountpoint. Shared music content plays on all regions, while voicetracks
                        and commercial breaks can be region-specific.
                      </Typography>
                    </Alert>
                  )}

                  {regions.length === 0 ? (
                    <Typography color="text.secondary" sx={{ py: 2, textAlign: 'center' }}>
                      {stationFormData.mode === 'multi-region'
                        ? 'No regions configured. Click "Add Region" to set up your first regional stream.'
                        : 'Enable Regional Splits in Basic Info, then add regions here.'}
                    </Typography>
                  ) : (
                    <TableContainer component={Paper} sx={{ bgcolor: 'var(--rs-bg)' }}>
                      <Table size="small">
                        <TableHead>
                          <TableRow>
                            <TableCell sx={{ color: '#22d3ee' }}>Name</TableCell>
                            <TableCell sx={{ color: '#22d3ee' }}>Code</TableCell>
                            <TableCell sx={{ color: '#22d3ee' }}>Timezone</TableCell>
                            <TableCell sx={{ color: '#22d3ee' }}>Icecast</TableCell>
                            <TableCell sx={{ color: '#22d3ee' }}>Stream Status</TableCell>
                            <TableCell sx={{ color: '#22d3ee' }}>Actions</TableCell>
                          </TableRow>
                        </TableHead>
                        <TableBody>
                          {regions.map((region) => {
                            const streamStatus = regionalStreamStatus.find(s => s.regionId === region.id);
                            return (
                              <TableRow key={region.id}>
                                <TableCell>
                                  <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                                    <Chip
                                      label={region.is_active ? 'Active' : 'Inactive'}
                                      size="small"
                                      sx={{
                                        bgcolor: region.is_active ? '#22d3ee20' : '#66620',
                                        color: region.is_active ? '#22d3ee' : '#999',
                                        fontSize: '0.65rem',
                                      }}
                                    />
                                    {region.name}
                                  </Box>
                                </TableCell>
                                <TableCell>{region.code}</TableCell>
                                <TableCell>
                                  <Box sx={{ display: 'flex', alignItems: 'center', gap: 0.75 }}>
                                    {region.timezone}
                                    {Number(region.time_shift_hours) > 0 && (
                                      <Chip
                                        label={`-${region.time_shift_hours}h`}
                                        size="small"
                                        sx={{ bgcolor: '#3b82f620', color: '#93c5fd', fontSize: '0.65rem' }}
                                      />
                                    )}
                                  </Box>
                                </TableCell>
                                <TableCell>
                                  {region.icecast_enabled && region.icecast_mountpoint ? (
                                    hideServerInfo && !isSuperAdmin() ? (
                                      <Typography variant="caption" color="text.secondary">Configured</Typography>
                                    ) : (
                                      <Typography variant="caption" color="text.secondary">
                                        {region.icecast_host}:{region.icecast_port}{region.icecast_mountpoint} · Internal {playoutAudioFormat === 'opus_320' ? 'Opus 320kbps' : 'FLAC'} · 48000Hz
                                      </Typography>
                                    )
                                  ) : (
                                    <Button
                                      size="small"
                                      onClick={() => handleAutoConfigureRegionIcecast(region)}
                                      disabled={loading}
                                      sx={{ color: '#22c55e', fontSize: '0.75rem' }}
                                    >
                                      Configure Icecast
                                    </Button>
                                  )}
                                </TableCell>
                                <TableCell>
                                  <Chip
                                    label={streamStatus?.status || 'stopped'}
                                    size="small"
                                    sx={{
                                      bgcolor: streamStatus?.status === 'running' ? '#00ff4420' : 'var(--rs-border)',
                                      color: streamStatus?.status === 'running' ? '#00ff44' : '#999',
                                      fontSize: '0.65rem',
                                    }}
                                  />
                                </TableCell>
                                <TableCell>
                                  <Box sx={{ display: 'flex', gap: 0.5 }}>
                                    <IconButton size="small" onClick={() => handleOpenRegionDialog(region)} sx={{ color: '#22d3ee' }}>
                                      <Edit fontSize="small" />
                                    </IconButton>
                                    <IconButton size="small" onClick={() => handleDeleteRegion(region.id)} sx={{ color: '#ff6b6b' }}>
                                      <Delete fontSize="small" />
                                    </IconButton>
                                    {region.icecast_enabled && (
                                      <>
                                        {streamStatus?.status === 'running' ? (
                                          <Button size="small" color="error" onClick={() => handleStopRegionalStream(editingStation.id, region.id)}>
                                            Stop
                                          </Button>
                                        ) : (
                                          <Button size="small" sx={{ color: '#00ff44' }} onClick={() => handleStartRegionalStream(editingStation.id, region.id)}>
                                            Start
                                          </Button>
                                        )}
                                      </>
                                    )}
                                  </Box>
                                </TableCell>
                              </TableRow>
                            );
                          })}
                        </TableBody>
                      </Table>
                    </TableContainer>
                  )}
                </Box>
              )}
            </DialogContent>
            <DialogActions>
              <Button onClick={handleCloseStationDialog}>Cancel</Button>
              <Button onClick={handleSaveStation} variant="contained" sx={{ bgcolor: '#22d3ee' }} disabled={loading}>
                {editingStation ? 'Update' : 'Create'}
              </Button>
            </DialogActions>
          </Dialog>

          {/* Region Dialog */}
          <Dialog open={regionDialogOpen} onClose={() => setRegionDialogOpen(false)} maxWidth="md" fullWidth>
            <DialogTitle>
              {editingRegion ? 'Edit Region' : 'Add Region'}
            </DialogTitle>
            <DialogContent>
              <Grid container spacing={2} sx={{ mt: 1 }}>
                <Grid item xs={12} sm={6}>
                  <TextField
                    fullWidth label="Region Name *" value={regionFormData.name}
                    onChange={(e) => setRegionFormData({ ...regionFormData, name: e.target.value })}
                    placeholder="e.g., North West, London, Scotland"
                  />
                </Grid>
                <Grid item xs={12} sm={3}>
                  <TextField
                    fullWidth label="Code *" value={regionFormData.code}
                    onChange={(e) => setRegionFormData({ ...regionFormData, code: e.target.value.toUpperCase() })}
                    placeholder="e.g., NW, LON, SCO"
                    inputProps={{ maxLength: 10 }}
                  />
                </Grid>
                <Grid item xs={12} sm={3}>
                  <FormControl fullWidth>
                    <InputLabel>Timezone</InputLabel>
                    <Select
                      value={regionFormData.timezone} label="Timezone"
                      onChange={(e) => setRegionFormData({ ...regionFormData, timezone: e.target.value })}
                    >
                      <MenuItem value="UTC">UTC</MenuItem>
                      <MenuItem value="Europe/London">Europe/London</MenuItem>
                      <MenuItem value="Europe/Paris">Europe/Paris</MenuItem>
                      <MenuItem value="Europe/Berlin">Europe/Berlin</MenuItem>
                      <MenuItem value="America/New_York">US Eastern</MenuItem>
                      <MenuItem value="America/Chicago">US Central</MenuItem>
                      <MenuItem value="America/Denver">US Mountain</MenuItem>
                      <MenuItem value="America/Los_Angeles">US Pacific</MenuItem>
                      <MenuItem value="Australia/Sydney">Australia/Sydney</MenuItem>
                    </Select>
                  </FormControl>
                </Grid>
                <Grid item xs={12} sm={4}>
                  <FormControl fullWidth>
                    <InputLabel>Time Shift</InputLabel>
                    <Select
                      value={regionFormData.time_shift_hours} label="Time Shift"
                      onChange={(e) => setRegionFormData({ ...regionFormData, time_shift_hours: Number(e.target.value) })}
                    >
                      <MenuItem value={0}>Live (with master)</MenuItem>
                      {[1, 2, 3, 4, 5, 6, 7, 8, 9, 10, 11, 12].map((h) => (
                        <MenuItem key={h} value={h}>{h} hour{h === 1 ? '' : 's'} behind</MenuItem>
                      ))}
                    </Select>
                  </FormControl>
                </Grid>
                {regionFormData.time_shift_hours > 0 && (
                  <Grid item xs={12}>
                    <Alert severity="info" sx={{ py: 0.5 }}>
                      <Typography variant="body2">
                        This region will replay the schedule from {regionFormData.time_shift_hours} hour{regionFormData.time_shift_hours === 1 ? '' : 's'} earlier,
                        so content airs at the correct local time. Master skips and live resync won't affect it.
                      </Typography>
                    </Alert>
                  </Grid>
                )}
                <Grid item xs={12}>
                  <FormControlLabel
                    control={<Switch checked={regionFormData.is_active} onChange={(e) => setRegionFormData({ ...regionFormData, is_active: e.target.checked })} />}
                    label="Active"
                  />
                </Grid>

                <Grid item xs={12}>
                  <Divider sx={{ my: 1 }} />
                  <Typography variant="subtitle2" sx={{ color: '#22d3ee', mb: 1 }}>
                    Regional Icecast Stream
                  </Typography>
                  <FormControlLabel
                    control={<Switch checked={regionFormData.icecast_enabled} onChange={(e) => setRegionFormData({ ...regionFormData, icecast_enabled: e.target.checked })} />}
                    label="Enable Icecast for this region"
                  />
                </Grid>

                {regionFormData.icecast_enabled && (
                  <>
                    <Grid item xs={12}>
                      <Alert severity="info" sx={{ py: 0.5 }}>
                        <Typography variant="body2">
                          {hideServerInfo && !isSuperAdmin()
                            ? "Uses station's Icecast server. Only the mountpoint needs to be unique."
                            : `Uses station's Icecast server (localhost:8000). Only the mountpoint needs to be unique.`}
                        </Typography>
                      </Alert>
                    </Grid>
                    <Grid item xs={12}>
                      <TextField
                        fullWidth
                        label="Mountpoint"
                        value={regionFormData.icecast_mountpoint}
                        onChange={(e) => setRegionFormData({ ...regionFormData, icecast_mountpoint: e.target.value })}
                        placeholder={
                          regionFormData.code
                            ? `${(stationFormData.icecast_mountpoint || stationNameToMount(editingStation?.name, editingStation?.id)).replace(/\/$/, '')}-region-${regionFormData.code.toLowerCase().replace(/\s+/g, '-')}`
                            : 'Enter region code above, mount auto-derived from station name'
                        }
                        helperText="Auto-suggested from region code. Must start with / and be unique."
                      />
                    </Grid>
                    <Grid item xs={12}>
                      <TextField
                        fullWidth
                        label="Stream Title"
                        value={regionFormData.icecast_stream_title}
                        onChange={(e) => setRegionFormData({ ...regionFormData, icecast_stream_title: e.target.value })}
                        placeholder={regionFormData.name ? `${stationFormData.name} - ${regionFormData.name}` : 'Optional'}
                      />
                    </Grid>
                  </>
                )}
              </Grid>
            </DialogContent>
            <DialogActions>
              <Button onClick={() => setRegionDialogOpen(false)}>Cancel</Button>
              <Button onClick={handleSaveRegion} variant="contained" sx={{ bgcolor: '#22d3ee' }} disabled={loading}>
                {editingRegion ? 'Update' : 'Create'}
              </Button>
            </DialogActions>
          </Dialog>
        </Box>
      )}

      {/* AI Settings Tab */}
      {activeTab === 'ai_features' && (
        <Box>
        {isSuperAdmin() && !systemFeatures.ai_content_enabled && (
          <Alert severity="info" sx={{ mb: 3 }}>
            AI Content is disabled system-wide. Enable it under Station and App Configuration → App Features.
          </Alert>
        )}

        <Card sx={{ bgcolor: 'var(--rs-paper)', border: '1px solid var(--rs-border)' }}>
          <CardContent>
            <Typography variant="h6" sx={{ color: '#22d3ee', mb: 3 }}>
              AI Configuration
            </Typography>

            <Grid container spacing={2}>
              {isSuperAdmin() && (
                <>
                  <Grid item xs={12}>
                    <Typography variant="subtitle1" sx={{ color: '#22d3ee', mb: 2 }}>
                      Grok Configuration
                    </Typography>
                    <Typography variant="body2" sx={{ color: '#9ca3af', mb: 2 }}>
                      Used for script generation and AI Insights. Insights do one quick live web lookup. Grok 4.3 is the best balance of speed, cost, and accuracy for both. Get a key from console.x.ai.
                    </Typography>
                  </Grid>
                  <Grid item xs={12} sm={6}>
                    <TextField
                      fullWidth
                      label="Grok API Key"
                      type="password"
                      value={aiConfig.grok_api_key || ''}
                      onChange={(e) => setAiConfig({ ...aiConfig, grok_api_key: e.target.value })}
                      placeholder="xai-..."
                    />
                  </Grid>
                  <Grid item xs={12} sm={6}>
                    <FormControl fullWidth>
                      <InputLabel>Model</InputLabel>
                      <Select
                        value={aiConfig.grok_model === 'grok-4.3' || aiConfig.grok_model === 'grok-4.5'
                          ? aiConfig.grok_model
                          : 'grok-4.3'}
                        onChange={(e) => setAiConfig({ ...aiConfig, grok_model: e.target.value })}
                        label="Model"
                      >
                        <MenuItem value="grok-4.3">Grok 4.3 (Recommended)</MenuItem>
                        <MenuItem value="grok-4.5">Grok 4.5</MenuItem>
                      </Select>
                    </FormControl>
                  </Grid>
                </>
              )}

              {(isSuperAdmin() || isAdmin()) && (
                <>
                  <Grid item xs={12}>
                    <Divider sx={{ my: 2 }} />
                    <Typography variant="subtitle1" sx={{ color: '#22d3ee', mb: 2 }}>
                      Voice Provider Configuration
                    </Typography>
                  </Grid>

                  <Grid item xs={12} sm={6}>
                    <TextField
                      fullWidth
                      label="ElevenLabs API Key"
                      type="password"
                      value={aiConfig.elevenlabs_api_key || ''}
                      onChange={(e) => setAiConfig({ ...aiConfig, elevenlabs_api_key: e.target.value })}
                    />
                  </Grid>
                  <Grid item xs={12} sm={6}>
                    <TextField
                      fullWidth
                      label="Default ElevenLabs Voice ID (optional)"
                      value={aiConfig.elevenlabs_voice_id || ''}
                      onChange={(e) => setAiConfig({ ...aiConfig, elevenlabs_voice_id: e.target.value })}
                    />
                  </Grid>

                  <Grid item xs={12} sm={6}>
                    <TextField
                      fullWidth
                      label="Speechify API Key"
                      type="password"
                      value={aiConfig.speechify_api_key || ''}
                      onChange={(e) => setAiConfig({ ...aiConfig, speechify_api_key: e.target.value })}
                    />
                  </Grid>

                  <Grid item xs={12} sm={6}>
                    <TextField
                      fullWidth
                      label="Google TTS API Key"
                      type="password"
                      value={aiConfig.google_api_key || ''}
                      onChange={(e) => setAiConfig({ ...aiConfig, google_api_key: e.target.value })}
                    />
                  </Grid>

                  <Grid item xs={12} sm={6}>
                    <TextField
                      fullWidth
                      label="Default Google Voice (optional)"
                      value={aiConfig.google_voice_name || ''}
                      onChange={(e) => setAiConfig({ ...aiConfig, google_voice_name: e.target.value })}
                      placeholder="en-US-Chirp3-HD-Charon"
                    />
                  </Grid>

                  <Grid item xs={12}>
                    <Divider sx={{ my: 2 }} />
                    <Typography variant="subtitle1" sx={{ color: '#22d3ee', mb: 1 }}>
                      On-Air Voicetracks
                    </Typography>
                    <FormControlLabel
                      control={
                        <Switch
                          checked={aiConfig.require_vt_approval === true}
                          onChange={(e) => setAiConfig({ ...aiConfig, require_vt_approval: e.target.checked })}
                        />
                      }
                      label="Hold AI voicetracks for approval before air"
                    />
                    <Typography variant="body2" sx={{ color: '#9ca3af', mt: 0.5 }}>
                      When on, each generated link waits in AI Content → Generated Links until someone listens and
                      approves it. Links not approved by air time are dropped, so only turn this on when the
                      queue is being watched. When off, links air automatically; anything that trips a banned-word
                      rule is regenerated once and flagged with a warning in Generated Links.
                    </Typography>
                  </Grid>

                  <Grid item xs={12}>
                    <Button
                      variant="contained"
                      onClick={handleSaveAI}
                      disabled={loading}
                      sx={{ bgcolor: '#22d3ee', mt: 2 }}
                    >
                      Save AI Settings
                    </Button>
                  </Grid>
                </>
              )}
            </Grid>
          </CardContent>
        </Card>
        </Box>
      )}

      {/* Audio Engine Settings Tab */}
      {activeTab === 'audio_engine' && (
        <Card sx={{ bgcolor: 'var(--rs-paper)', border: '1px solid var(--rs-border)' }}>
          <CardContent>
            <Typography variant="h6" sx={{ color: '#22d3ee', mb: 3 }}>
              Audio Engine Configuration
            </Typography>

            <Grid container spacing={2}>
              <Grid item xs={12}>
                <Divider sx={{ my: 2 }} />
                <Typography variant="subtitle1" sx={{ color: '#22d3ee', mb: 2 }}>
                  Audio Processing Settings
                </Typography>
              </Grid>

              <Grid item xs={12} sm={6}>
                <TextField
                  fullWidth
                  label="Crossfade Duration (seconds)"
                  type="number"
                  inputProps={{ min: 0, max: 10, step: 0.1 }}
                  value={audioEngineSettings.crossfade_duration}
                  onChange={(e) => setAudioEngineSettings({ ...audioEngineSettings, crossfade_duration: parseFloat(e.target.value) || 3.0 })}
                />
              </Grid>

              <Grid item xs={12} sm={6}>
                <FormControlLabel
                  control={
                    <Switch
                      checked={audioEngineSettings.normalize_audio}
                      onChange={(e) => setAudioEngineSettings({ ...audioEngineSettings, normalize_audio: e.target.checked })}
                    />
                  }
                  label="Normalize Audio"
                />
              </Grid>

              <Grid item xs={12}>
                <Divider sx={{ my: 2 }} />
                <Typography variant="subtitle1" sx={{ color: '#22d3ee', mb: 2 }}>
                  Sweeper Overlay Settings
                </Typography>
              </Grid>

              <Grid item xs={12} sm={6}>
                <TextField
                  fullWidth
                  label="Sweeper duck level (dB)"
                  type="number"
                  inputProps={{ min: -24, max: 0, step: 1 }}
                  value={audioEngineSettings.sweeper_duck_level_db}
                  onChange={(e) => setAudioEngineSettings({ ...audioEngineSettings, sweeper_duck_level_db: parseInt(e.target.value, 10) || -6 })}
                  helperText="Reduce underlying track by this many dB when a sweeper plays over the intro (e.g. -6 = duck by 6 dB)."
                />
              </Grid>

              <Grid item xs={12}>
                <Alert severity="info" sx={{ mt: 2 }}>
                  <Typography variant="body2">
                    <strong>Note:</strong> Audio engine settings are applied globally.
                  </Typography>
                </Alert>
              </Grid>

              <Grid item xs={12}>
                <Button
                  variant="contained"
                  onClick={handleSaveAudioEngine}
                  disabled={loading}
                  sx={{ bgcolor: '#22d3ee', mt: 2 }}
                >
                  Save Audio Engine Settings
                </Button>
              </Grid>
            </Grid>
          </CardContent>
        </Card>
      )}

      {activeTab === 'webrtc_contrib' && (
        <WebrtcContributionSettings stations={stations} />
      )}

      {/* Studio Output Tab (devices + per-customer builds + setup) */}
      {activeTab === 'studio_mode' && (
        <StudioModeSettings />
      )}

      {/* Server Nodes Tab (multi-VM deployment) — superadmin only */}
      {activeTab === 'server_nodes' && isSuperAdmin() && (
        <ServerNodesSettings />
      )}

      {activeTab === 'gateway' && isSuperAdmin() && (
        <GatewayJoinSettings />
      )}

      {activeTab === 'franchise_syndication' && isSuperAdmin() && syndicationEnabled && (
        <FranchiseSyndicationSettings />
      )}

      {/* Dropbox Integration Tab */}
      {activeTab === 'dropbox' && (
        <DropboxIntegration />
      )}

      {/* Security / MFA Tab */}
      {activeTab === 'security' && (
        <>
          <MfaSetup />
          {isSuperAdmin() && <CrmApiKeys />}
        </>
      )}

      {/* Branding Tab (admin/superadmin) */}
      {activeTab === 'branding' && (
        <Card sx={{ borderRadius: 2 }}>
          <CardContent>
            <Typography variant="h6" sx={{ color: 'primary.main', mb: 3 }}>
              Sidebar branding
            </Typography>
            <Typography variant="body2" color="text.secondary" sx={{ mb: 3 }}>
              Customise the app name and logo shown in the sidebar and on welcome emails. Leave app name blank to use the default &quot;The Radio Suite&quot;. If no logo is uploaded, emails use the RadioSuite logo.
            </Typography>
            {brandingMessage.text && (
              <Alert severity={brandingMessage.type === 'error' ? 'error' : 'success'} sx={{ mb: 2 }} onClose={() => setBrandingMessage({ type: '', text: '' })}>
                {brandingMessage.text}
              </Alert>
            )}
            <Grid container spacing={3}>
              <Grid item xs={12} sm={6}>
                <TextField
                  fullWidth
                  label="App name"
                  value={brandingAppName}
                  onChange={(e) => setBrandingAppName(e.target.value)}
                  placeholder="The Radio Suite"
                  helperText="Shown next to the logo in the sidebar and on welcome emails"
                />
              </Grid>
              <Grid item xs={12}>
                <Typography variant="subtitle2" sx={{ mb: 1 }}>Logo</Typography>
                <Box sx={{ display: 'flex', alignItems: 'center', gap: 2, flexWrap: 'wrap' }}>
                  {brandingLogoUrl && (
                    <Box
                      sx={{
                        width: 56,
                        height: 56,
                        borderRadius: 2,
                        border: '1px solid',
                        borderColor: 'divider',
                        overflow: 'hidden',
                        bgcolor: 'action.hover',
                        display: 'flex',
                        alignItems: 'center',
                        justifyContent: 'center',
                      }}
                    >
                      <img
                        src={brandingLogoUrl.startsWith('http') ? brandingLogoUrl : `${API_URL}${brandingLogoUrl}`}
                        alt=""
                        style={{ width: '100%', height: '100%', objectFit: 'contain' }}
                      />
                    </Box>
                  )}
                  <Button variant="outlined" component="label" disabled={brandingSaving} sx={{ borderRadius: 2 }}>
                    {brandingLogoUrl ? 'Replace logo' : 'Upload logo'}
                    <input type="file" hidden accept="image/png,image/jpeg,image/gif,image/webp,image/svg+xml" onChange={handleBrandingLogoUpload} />
                  </Button>
                  {brandingLogoUrl && (
                    <Button variant="text" color="secondary" onClick={handleBrandingLogoRemove} disabled={brandingSaving} sx={{ borderRadius: 2 }}>
                      Remove logo
                    </Button>
                  )}
                </Box>
                <Typography variant="caption" color="text.secondary" sx={{ display: 'block', mt: 0.5 }}>
                  PNG, JPG, GIF, WebP or SVG. Max 2 MB. Used in the sidebar and on welcome emails. SVG uploads stay in the sidebar; emails use the RadioSuite logo instead.
                </Typography>
              </Grid>
              <Grid item xs={12}>
                <Button variant="contained" onClick={handleBrandingSave} disabled={brandingSaving} sx={{ borderRadius: 2 }}>
                  {brandingSaving ? 'Saving…' : 'Save app name'}
                </Button>
              </Grid>
            </Grid>
          </CardContent>
        </Card>
      )}

      {/* Email / SMTP Settings Tab — superadmin only */}
      {activeTab === 'email' && isSuperAdmin() && (
        <Card sx={{ bgcolor: 'var(--rs-paper)', border: '1px solid var(--rs-border)' }}>
          <CardContent>
            <Typography variant="h6" sx={{ color: '#22d3ee', mb: 3 }}>
              Email / SMTP Configuration
            </Typography>

            {emailStatusChecking && !emailDeliveryStatus && (
              <Alert severity="info" sx={{ mb: 3 }} icon={<CircularProgress size={18} />}>
                Checking saved SMTP settings…
              </Alert>
            )}
            {!emailStatusChecking && emailDeliveryStatus?.working && (
              <Alert severity="success" sx={{ mb: 3 }} icon={<CheckCircleIcon />}>
                SMTP is configured and working
                {emailDeliveryStatus.fromEmail ? ` (from ${emailDeliveryStatus.fromEmail})` : ''}.
                Welcome emails, password resets, storage alerts, and Live Studio invites will send.
                {emailDeliveryStatus.appUrl ? ` Login links will use ${emailDeliveryStatus.appUrl}.` : ''}
              </Alert>
            )}
            {!emailStatusChecking && emailDeliveryStatus?.configured && emailDeliveryStatus?.connectionOk && !emailDeliveryStatus?.enabled && (
              <Alert severity="warning" sx={{ mb: 3 }}>
                SMTP connected successfully, but email notifications are off. Turn them on below or mail will not send.
              </Alert>
            )}
            {!emailStatusChecking && emailDeliveryStatus?.configured && emailDeliveryStatus?.connectionOk === false && (
              <Alert severity="error" sx={{ mb: 3 }}>
                SMTP is saved but the connection failed
                {emailDeliveryStatus.connectionError ? `: ${emailDeliveryStatus.connectionError}` : '.'}
                Check host, port, SSL, and credentials, then save again.
              </Alert>
            )}
            {!emailStatusChecking && emailDeliveryStatus && !emailDeliveryStatus.configured && (
              <Alert severity="info" sx={{ mb: 3 }}>
                SMTP is not configured. Welcome emails, password resets, storage alerts, and Live Studio invites will not send until a working server is saved.
              </Alert>
            )}

            <Grid container spacing={2}>
              <Grid item xs={12}>
                <Typography variant="subtitle1" sx={{ color: '#22d3ee', mb: 1 }}>
                  SMTP Server
                </Typography>
              </Grid>
              <Grid item xs={12} sm={6}>
                <TextField
                  fullWidth
                  label="SMTP Host"
                  value={emailSettings.smtp_host}
                  onChange={(e) => setEmailSettings({ ...emailSettings, smtp_host: e.target.value })}
                  placeholder="e.g., smtp.gmail.com"
                />
              </Grid>
              <Grid item xs={12} sm={3}>
                <TextField
                  fullWidth
                  label="Port"
                  type="number"
                  value={emailSettings.smtp_port}
                  onChange={(e) => setEmailSettings({ ...emailSettings, smtp_port: parseInt(e.target.value) || 587 })}
                />
              </Grid>
              <Grid item xs={12} sm={3}>
                <FormControlLabel
                  control={
                    <Switch
                      checked={emailSettings.smtp_secure}
                      onChange={(e) => setEmailSettings({ ...emailSettings, smtp_secure: e.target.checked })}
                    />
                  }
                  label="SSL/TLS"
                />
              </Grid>
              <Grid item xs={12} sm={6}>
                <TextField
                  fullWidth
                  label="SMTP Username"
                  value={emailSettings.smtp_username}
                  onChange={(e) => setEmailSettings({ ...emailSettings, smtp_username: e.target.value })}
                />
              </Grid>
              <Grid item xs={12} sm={6}>
                <TextField
                  fullWidth
                  label="SMTP Password"
                  type="password"
                  value={emailSettings.smtp_password}
                  onChange={(e) => setEmailSettings({ ...emailSettings, smtp_password: e.target.value })}
                />
              </Grid>

              <Grid item xs={12}>
                <Divider sx={{ my: 2 }} />
                <Typography variant="subtitle1" sx={{ color: '#22d3ee', mb: 1 }}>
                  Sender Details
                </Typography>
              </Grid>
              <Grid item xs={12} sm={6}>
                <TextField
                  fullWidth
                  label="From Email"
                  value={emailSettings.from_email}
                  onChange={(e) => setEmailSettings({ ...emailSettings, from_email: e.target.value })}
                  placeholder="noreply@yourdomain.com"
                />
              </Grid>
              <Grid item xs={12} sm={6}>
                <TextField
                  fullWidth
                  label="From Name"
                  value={emailSettings.from_name}
                  onChange={(e) => setEmailSettings({ ...emailSettings, from_name: e.target.value })}
                  placeholder="The Radio Suite"
                />
              </Grid>

              <Grid item xs={12}>
                <Divider sx={{ my: 2 }} />
                <Typography variant="subtitle1" sx={{ color: '#22d3ee', mb: 1 }}>
                  Email Subjects
                </Typography>
              </Grid>
              <Grid item xs={12} sm={6}>
                <TextField
                  fullWidth
                  label="Welcome Email Subject"
                  value={emailSettings.welcome_email_subject}
                  onChange={(e) => setEmailSettings({ ...emailSettings, welcome_email_subject: e.target.value })}
                />
              </Grid>
              <Grid item xs={12} sm={6}>
                <TextField
                  fullWidth
                  label="Password Reset Subject"
                  value={emailSettings.password_reset_subject}
                  onChange={(e) => setEmailSettings({ ...emailSettings, password_reset_subject: e.target.value })}
                />
              </Grid>

              <Grid item xs={12}>
                <FormControlLabel
                  control={
                    <Switch
                      checked={!!emailSettings.enable_email_notifications}
                      onChange={(e) => setEmailSettings({ ...emailSettings, enable_email_notifications: e.target.checked })}
                    />
                  }
                  label="Enable email notifications"
                />
                <Typography variant="caption" color="text.secondary" sx={{ display: 'block', ml: 6, mt: -0.5 }}>
                  Required for welcome emails, password resets, storage alerts, and Live Studio invites. Test emails can still be sent with this off.
                </Typography>
              </Grid>

              <Grid item xs={12}>
                <Divider sx={{ my: 2 }} />
                <Box sx={{ display: 'flex', gap: 2, flexWrap: 'wrap', alignItems: 'center' }}>
                  <Button
                    variant="contained"
                    onClick={handleSaveEmailSettings}
                    disabled={loading}
                    sx={{ bgcolor: '#22d3ee', '&:hover': { bgcolor: '#00b8e6' } }}
                  >
                    Save Settings
                  </Button>
                  <Button
                    variant="outlined"
                    onClick={handleTestSmtpConnection}
                    disabled={smtpTesting || !emailSettings.smtp_host}
                    sx={{ borderColor: '#22c55e', color: '#22c55e' }}
                  >
                    {smtpTesting ? 'Testing...' : 'Test Connection'}
                  </Button>
                  <Button
                    variant="outlined"
                    onClick={() => fetchEmailDeliveryStatus(true)}
                    disabled={emailStatusChecking}
                    sx={{ borderColor: '#22d3ee', color: '#22d3ee' }}
                  >
                    {emailStatusChecking ? 'Checking…' : 'Check saved settings'}
                  </Button>
                </Box>
              </Grid>

              <Grid item xs={12}>
                <Divider sx={{ my: 2 }} />
                <Typography variant="subtitle1" sx={{ color: '#22d3ee', mb: 1 }}>
                  Send Test Email
                </Typography>
                <Box sx={{ display: 'flex', gap: 2, alignItems: 'flex-start' }}>
                  <TextField
                    label="Recipient Email"
                    value={testEmail}
                    onChange={(e) => setTestEmail(e.target.value)}
                    placeholder="test@example.com"
                    sx={{ flex: 1 }}
                  />
                  <Button
                    variant="outlined"
                    onClick={handleSendTestEmail}
                    disabled={smtpTesting || !testEmail}
                    sx={{ borderColor: '#f59e0b', color: '#f59e0b', mt: 1 }}
                  >
                    {smtpTesting ? 'Sending...' : 'Send Test'}
                  </Button>
                </Box>
              </Grid>
            </Grid>
          </CardContent>
        </Card>
      )}

      {/* User Management Tab */}
      {activeTab === 'users' && (
        <UserManagement />
      )}

      {activeTab === 'audit_trail' && (
        <AuditTrailSettings />
      )}

      {/* Logger Tab (Superadmin only) */}
      {activeTab === 'logger' && (
        <Box>
          {loggerMessage.text && (
            <Alert
              severity={loggerMessage.type || 'info'}
              sx={{ mb: 3 }}
              onClose={() => setLoggerMessage({ type: '', text: '' })}
            >
              {loggerMessage.text}
            </Alert>
          )}

          {!systemFeatures.logger_enabled && (
            <Alert severity="info" sx={{ mb: 3 }}>
              Logger is disabled system-wide. Enable it under Station and App Configuration → App Features.
            </Alert>
          )}

          {/* Logger Dropbox Connection */}
          <Card sx={{ bgcolor: 'var(--rs-paper)', border: '1px solid var(--rs-border)', mb: 3 }}>
            <CardContent>
              <Typography variant="h6" sx={{ color: '#22d3ee', mb: 2, display: 'flex', alignItems: 'center', gap: 1 }}>
                <LoggerIcon sx={{ color: '#ef4444' }} />
                Logger Dropbox Account
              </Typography>

              <Alert severity="info" sx={{ mb: 3, bgcolor: 'var(--rs-bg)', border: '1px solid var(--rs-border)' }}>
                <Typography variant="body2" sx={{ color: '#ccc' }}>
                  The Logger Dropbox is a <strong>separate</strong> Dropbox account used exclusively for storing
                  stream recordings. This is different from the asset sync Dropbox. Recordings are automatically
                  uploaded hourly, and old recordings are cleaned up based on the retention period below.
                </Typography>
              </Alert>

              <Box sx={{ display: 'flex', alignItems: 'center', gap: 2, mb: 3 }}>
                <Chip
                  icon={loggerDropboxConnected ? <CloudDone /> : <DropboxIcon />}
                  label={loggerDropboxConnected ? 'Logger Dropbox Connected' : 'Not Connected'}
                  color={loggerDropboxConnected ? 'success' : 'default'}
                  variant="outlined"
                />
                {loggerDropboxConnected ? (
                  <Button
                    variant="outlined"
                    color="error"
                    size="small"
                    onClick={handleDisconnectLoggerDropbox}
                    sx={{ borderColor: '#ff6b6b', color: '#ff6b6b' }}
                  >
                    Disconnect
                  </Button>
                ) : loggerDropboxConfigured ? (
                  <Button
                    variant="contained"
                    size="small"
                    onClick={handleConnectLoggerDropbox}
                    sx={{ bgcolor: '#0061ff', '&:hover': { bgcolor: '#0052cc' } }}
                  >
                    Connect Logger Dropbox
                  </Button>
                ) : null}
              </Box>

              <Divider sx={{ my: 2 }} />

              <Typography variant="subtitle2" sx={{ color: '#22d3ee', mb: 2 }}>
                Logger Dropbox App Credentials
              </Typography>

              <Grid container spacing={2}>
                <Grid item xs={12}>
                  <TextField
                    fullWidth
                    label="Logger Dropbox App Key"
                    value={loggerDropboxConfig.appKey}
                    onChange={(e) => setLoggerDropboxConfig({ ...loggerDropboxConfig, appKey: e.target.value })}
                    placeholder="Enter your Logger Dropbox App Key"
                    size="small"
                  />
                </Grid>
                <Grid item xs={12}>
                  <TextField
                    fullWidth
                    label="Logger Dropbox App Secret"
                    type="password"
                    value={loggerDropboxConfig.appSecret}
                    onChange={(e) => setLoggerDropboxConfig({ ...loggerDropboxConfig, appSecret: e.target.value })}
                    placeholder="Enter your Logger Dropbox App Secret"
                    size="small"
                    helperText="Create a separate Dropbox app at dropbox.com/developers/apps for the logger"
                  />
                </Grid>
                <Grid item xs={12}>
                  <TextField
                    fullWidth
                    label="Redirect URI"
                    value={loggerDropboxConfig.redirectUri}
                    onChange={(e) => setLoggerDropboxConfig({ ...loggerDropboxConfig, redirectUri: e.target.value })}
                    size="small"
                    helperText="Must match the redirect URI in your Logger Dropbox app settings"
                  />
                </Grid>
                <Grid item xs={12}>
                  <Button
                    variant="contained"
                    onClick={handleSaveLoggerDropboxConfig}
                    disabled={loggerSaving || !loggerDropboxConfig.appKey || !loggerDropboxConfig.appSecret}
                    sx={{ bgcolor: '#22d3ee', '&:hover': { bgcolor: '#00b8e6' } }}
                  >
                    Save Logger Dropbox Credentials
                  </Button>
                </Grid>
              </Grid>
            </CardContent>
          </Card>

          {/* Retention Settings */}
          <Card sx={{ bgcolor: 'var(--rs-paper)', border: '1px solid var(--rs-border)' }}>
            <CardContent>
              <Typography variant="h6" sx={{ color: '#22d3ee', mb: 2 }}>
                Recording Retention
              </Typography>

              <Typography variant="body2" sx={{ color: '#ccc', mb: 3 }}>
                Recordings older than the retention period are automatically deleted from Dropbox.
                This runs daily at 3 AM. Set the number of days to keep recordings.
              </Typography>

              <Grid container spacing={2} alignItems="center">
                <Grid item xs={12} sm={4}>
                  <TextField
                    fullWidth
                    label="Retention Days"
                    type="number"
                    value={loggerRetentionDays}
                    onChange={(e) => setLoggerRetentionDays(parseInt(e.target.value) || 60)}
                    inputProps={{ min: 1, max: 365 }}
                    size="small"
                    helperText={`Recordings older than ${loggerRetentionDays} days will be deleted`}
                  />
                </Grid>
                <Grid item xs={12} sm={4}>
                  <Button
                    variant="contained"
                    onClick={handleSaveLoggerRetention}
                    disabled={loggerSaving}
                    sx={{ bgcolor: '#22d3ee', '&:hover': { bgcolor: '#00b8e6' } }}
                  >
                    Save Retention
                  </Button>
                </Grid>
                <Grid item xs={12} sm={4}>
                  <Button
                    variant="outlined"
                    onClick={handleManualCleanup}
                    disabled={loggerSaving}
                    color="warning"
                  >
                    {loggerSaving ? 'Running...' : 'Run Cleanup Now'}
                  </Button>
                </Grid>
              </Grid>
            </CardContent>
          </Card>
        </Box>
      )}

      {activeTab === 'proxy' && (
        <Card sx={{ bgcolor: 'var(--rs-paper)', border: '1px solid var(--rs-border)' }}>
          <CardContent>
            <Typography variant="h6" sx={{ color: '#22d3ee', mb: 2 }}>
              URL &amp; proxy
            </Typography>
            <Typography variant="body2" color="text.secondary" sx={{ mb: 3 }}>
              When the app is behind a reverse proxy, set the public URL shown inside the app and optionally hide
              internal server addresses from non-superadmins.
            </Typography>
            {proxySaveMessage.text && (
              <Alert
                severity={proxySaveMessage.type === 'error' ? 'error' : 'success'}
                sx={{ mb: 2 }}
                onClose={() => setProxySaveMessage({ type: '', text: '' })}
              >
                {proxySaveMessage.text}
              </Alert>
            )}
            <Grid container spacing={3}>
              <Grid item xs={12}>
                <TextField
                  fullWidth
                  label="Public app URL"
                  value={proxyPublicUrl}
                  onChange={(e) => setProxyPublicUrl(e.target.value)}
                  placeholder="https://radio.example.com"
                  helperText="URL users see when behind a proxy. Leave blank to use the current origin. Used for display and stream URL rewriting."
                />
              </Grid>
              <Grid item xs={12}>
                <FormControlLabel
                  control={
                    <Switch
                      checked={hideServerInfo}
                      onChange={(e) => setHideServerInfo(e.target.checked)}
                    />
                  }
                  label="Hide server IP and internal URLs from non-superadmins"
                />
                <Typography variant="caption" color="text.secondary" sx={{ display: 'block', mt: 0.5 }}>
                  When enabled, stream URLs and Icecast host/port are hidden from admins and users. Only superadmins see real addresses.
                </Typography>
              </Grid>
              <Grid item xs={12}>
                <Button variant="contained" onClick={handleProxySave} disabled={proxySaving} sx={{ bgcolor: '#22d3ee', '&:hover': { bgcolor: '#00b8e6' } }}>
                  {proxySaving ? 'Saving…' : 'Save'}
                </Button>
              </Grid>
            </Grid>
          </CardContent>
        </Card>
      )}

      {/* Domain & SSL Tab — superadmin only */}
      {activeTab === 'domain_ssl' && isSuperAdmin() && (
        <Box>
          <Typography variant="h6" sx={{ color: '#22d3ee', mb: 1, display: 'flex', alignItems: 'center', gap: 1 }}>
            <SslIcon /> Domain &amp; SSL / TLS
          </Typography>
          <Typography variant="body2" color="text.secondary" sx={{ mb: 3 }}>
            Connect your server to a domain with a free, auto-renewing Let&apos;s Encrypt SSL certificate.
            The HTTPS server starts automatically once a certificate is issued, and renews itself before it expires.
          </Typography>

          {sslMessage.text && (
            <Alert
              severity={sslMessage.type === 'info' ? 'info' : sslMessage.type === 'success' ? 'success' : 'error'}
              sx={{ mb: 2 }}
              onClose={() => setSslMessage({ type: '', text: '' })}
            >
              {sslMessage.text}
            </Alert>
          )}

          {/* ── Status bar ────────────────────────────────────────────── */}
          {sslStatus && (
            <Card sx={{ bgcolor: 'var(--rs-paper)', border: '1px solid var(--rs-border)', mb: 3 }}>
              <CardContent>
                <Box sx={{ display: 'flex', alignItems: 'center', gap: 2, flexWrap: 'wrap' }}>
                  {sslStatus.certExists && sslStatus.daysUntilExpiry !== null && sslStatus.daysUntilExpiry > 0 ? (
                    <>
                      <LockIcon sx={{ color: '#22c55e', fontSize: 32 }} />
                      <Box>
                        <Typography variant="subtitle1" sx={{ color: '#22c55e', fontWeight: 700 }}>
                          Certificate Active
                        </Typography>
                        <Typography variant="body2" color="text.secondary">
                          Domain: <strong style={{ color: 'var(--rs-text)' }}>{sslStatus.domain}</strong>
                          &nbsp;·&nbsp;Expires in <strong style={{ color: sslStatus.daysUntilExpiry < 14 ? '#f59e0b' : '#22c55e' }}>
                            {sslStatus.daysUntilExpiry} days
                          </strong>
                          {sslStatus.certExpiry && (
                            <> ({new Date(sslStatus.certExpiry).toLocaleDateString()})</>
                          )}
                        </Typography>
                        <Typography variant="caption" color="text.secondary">
                          Auto-renews when under 30 days remaining · HTTPS server on port {process.env.REACT_APP_HTTPS_PORT || '5001'}
                        </Typography>
                      </Box>
                    </>
                  ) : sslStatus.isRequesting ? (
                    <>
                      <CircularProgress size={28} sx={{ color: '#22d3ee' }} />
                      <Box>
                        <Typography variant="subtitle1" sx={{ color: '#22d3ee' }}>
                          Requesting certificate…
                        </Typography>
                        <Typography variant="body2" color="text.secondary">
                          Communicating with Let&apos;s Encrypt. This usually takes 30–90 seconds.
                        </Typography>
                      </Box>
                    </>
                  ) : (
                    <>
                      <LockOpenIcon sx={{ color: '#888', fontSize: 32 }} />
                      <Box>
                        <Typography variant="subtitle1" sx={{ color: '#888' }}>
                          No certificate
                        </Typography>
                        <Typography variant="body2" color="text.secondary">
                          {sslStatus.lastError
                            ? `Last error: ${sslStatus.lastError}`
                            : 'Fill in the details below and click Request Certificate.'}
                        </Typography>
                      </Box>
                    </>
                  )}
                </Box>
              </CardContent>
            </Card>
          )}

          {/* ── Configuration ─────────────────────────────────────────── */}
          <Card sx={{ bgcolor: 'var(--rs-paper)', border: '1px solid var(--rs-border)', mb: 3 }}>
            <CardContent>
              <Typography variant="subtitle1" sx={{ color: 'var(--rs-text)', fontWeight: 600, mb: 2 }}>
                Certificate Configuration
              </Typography>
              <Grid container spacing={2}>
                <Grid item xs={12} md={6}>
                  <TextField
                    fullWidth
                    label="Domain name"
                    value={sslDomain}
                    onChange={e => setSslDomain(e.target.value)}
                    placeholder="radio.example.com"
                    helperText="Must already point to this server's IP via DNS"
                    disabled={sslStatus?.isRequesting}
                  />
                </Grid>
                <Grid item xs={12} md={6}>
                  <TextField
                    fullWidth
                    label="Contact email"
                    value={sslEmail}
                    onChange={e => setSslEmail(e.target.value)}
                    placeholder="admin@example.com"
                    helperText="Used by Let's Encrypt for expiry notices"
                    disabled={sslStatus?.isRequesting}
                  />
                </Grid>
                <Grid item xs={12} md={3}>
                  <TextField
                    fullWidth
                    label="Frontend port"
                    value={sslAppPort}
                    onChange={e => setSslAppPort(e.target.value)}
                    helperText="Port serving the dashboard UI (default 3000)"
                    disabled={sslStatus?.isRequesting}
                  />
                </Grid>
                <Grid item xs={12} md={3}>
                  <TextField
                    fullWidth
                    label="Backend API port"
                    value={sslBackendPort}
                    onChange={e => setSslBackendPort(e.target.value)}
                    helperText="RadioSuite backend port (default 5000)"
                    disabled={sslStatus?.isRequesting}
                  />
                </Grid>
                <Grid item xs={12}>
                  <FormControlLabel
                    control={
                      <Switch
                        checked={sslStaging}
                        onChange={e => setSslStaging(e.target.checked)}
                        disabled={sslStatus?.isRequesting}
                      />
                    }
                    label={
                      <Box>
                        <Typography variant="body2">Use Let&apos;s Encrypt staging environment</Typography>
                        <Typography variant="caption" color="text.secondary">
                          Enable this for testing — staging certificates are not trusted by browsers but won&apos;t hit rate limits
                        </Typography>
                      </Box>
                    }
                  />
                </Grid>
                <Grid item xs={12}>
                  <Box sx={{ display: 'flex', gap: 2, flexWrap: 'wrap' }}>
                    <Button
                      variant="contained"
                      onClick={handleRequestCert}
                      disabled={sslStatus?.isRequesting || !sslDomain.trim() || !sslEmail.trim()}
                      startIcon={sslStatus?.isRequesting ? <CircularProgress size={16} color="inherit" /> : <LockIcon />}
                      sx={{ bgcolor: '#22d3ee', '&:hover': { bgcolor: '#00b8e6' } }}
                    >
                      {sslStatus?.isRequesting ? 'Requesting…' : sslStatus?.certExists ? 'Re-issue Certificate' : 'Request Certificate'}
                    </Button>
                    {sslStatus?.certExists && (
                      <Button
                        variant="outlined"
                        onClick={handleRenewCert}
                        disabled={sslStatus?.isRequesting}
                        startIcon={<RefreshIcon />}
                        sx={{ borderColor: '#5b6675', color: '#ccc' }}
                      >
                        Force Renew
                      </Button>
                    )}
                    <Button
                      variant="text"
                      onClick={loadSslStatus}
                      disabled={sslLoading}
                      startIcon={<RefreshIcon />}
                      sx={{ color: '#888' }}
                    >
                      Refresh Status
                    </Button>
                  </Box>
                </Grid>
              </Grid>
            </CardContent>
          </Card>

          {/* ── How it works ─────────────────────────────────────────── */}
          <Card sx={{ bgcolor: 'var(--rs-bg)', border: '1px solid var(--rs-surface)', mb: 3 }}>
            <CardContent>
              <Typography variant="subtitle2" sx={{ color: '#888', mb: 1 }}>How it works</Typography>
              <Typography variant="body2" color="text.secondary" component="div">
                <ol style={{ paddingLeft: '1.2em', margin: 0 }}>
                  <li style={{ marginBottom: 4 }}>Point your domain&apos;s <strong>DNS A record</strong> to this server&apos;s public IP.</li>
                  <li style={{ marginBottom: 4 }}>Enter your domain and email above, then click <strong>Request Certificate</strong>.</li>
                  <li style={{ marginBottom: 4 }}>The backend completes a Let&apos;s Encrypt HTTP-01 challenge automatically — port forwarding was configured by the install script.</li>
                  <li style={{ marginBottom: 4 }}>Once the certificate is issued, <strong>nginx is configured automatically</strong> with full SSL termination and the HTTPS server starts.</li>
                  <li>Certificates <strong>auto-renew</strong> every day at 3 AM when fewer than 30 days remain.</li>
                </ol>
              </Typography>
            </CardContent>
          </Card>
        </Box>
      )}

      {/* Text Output Tab */}
      {activeTab === 'text_output' && (
        <TextOutputsSettings />
      )}

      {/* Auto Delete Settings Tab */}
      {activeTab === 'auto_delete' && (
        <Box>
          <Typography variant="h6" sx={{ color: '#22d3ee', mb: 1 }}>
            Auto Delete Settings
          </Typography>
          <Typography variant="body2" color="text.secondary" sx={{ mb: 3 }}>
            Automatically delete assets from specific categories after they haven't been used for a set number of days.
            This frees up storage and keeps your asset library clean. The cleanup runs automatically every 6 hours.
          </Typography>

          {autoDeleteMessage.text && (
            <Alert
              severity={autoDeleteMessage.type === 'error' ? 'error' : 'success'}
              sx={{ mb: 2 }}
              onClose={() => setAutoDeleteMessage({ type: '', text: '' })}
            >
              {autoDeleteMessage.text}
            </Alert>
          )}

          {/* Actions row */}
          <Box sx={{ display: 'flex', gap: 2, mb: 3 }}>
            <Button
              variant="contained"
              startIcon={<Add />}
              onClick={() => {
                setAutoDeleteForm({ category_id: '', enabled: true, days_after_last_use: 60, delete_file: true });
                setAutoDeleteDialogOpen(true);
              }}
              sx={{ bgcolor: '#22d3ee', '&:hover': { bgcolor: '#00b8e6' } }}
            >
              Add Rule
            </Button>
            <Button
              variant="outlined"
              onClick={() => fetchAutoDeletePreview()}
              color="info"
            >
              Preview Deletions
            </Button>
            <Button
              variant="outlined"
              onClick={handleRunAutoDelete}
              disabled={autoDeleteRunning}
              color="warning"
            >
              {autoDeleteRunning ? 'Running...' : 'Run Cleanup Now'}
            </Button>
          </Box>

          {/* Rules table */}
          <Card sx={{ bgcolor: 'var(--rs-paper)', border: '1px solid var(--rs-border)', mb: 3 }}>
            <CardContent>
              <Typography variant="subtitle1" sx={{ color: '#22d3ee', mb: 2 }}>
                Active Rules
              </Typography>
              {autoDeleteRules.length === 0 ? (
                <Typography variant="body2" color="text.secondary">
                  No auto-delete rules configured. Add a rule to automatically clean up assets in specific categories.
                </Typography>
              ) : (
                <TableContainer component={Paper} sx={{ bgcolor: 'transparent' }}>
                  <Table size="small">
                    <TableHead>
                      <TableRow>
                        <TableCell sx={{ color: 'var(--rs-muted)', borderBottom: '1px solid var(--rs-border)' }}>Category</TableCell>
                        <TableCell sx={{ color: 'var(--rs-muted)', borderBottom: '1px solid var(--rs-border)' }}>Delete After</TableCell>
                        <TableCell sx={{ color: 'var(--rs-muted)', borderBottom: '1px solid var(--rs-border)' }}>Delete Files</TableCell>
                        <TableCell sx={{ color: 'var(--rs-muted)', borderBottom: '1px solid var(--rs-border)' }}>Assets</TableCell>
                        <TableCell sx={{ color: 'var(--rs-muted)', borderBottom: '1px solid var(--rs-border)' }}>Enabled</TableCell>
                        <TableCell sx={{ color: 'var(--rs-muted)', borderBottom: '1px solid var(--rs-border)' }}>Actions</TableCell>
                      </TableRow>
                    </TableHead>
                    <TableBody>
                      {autoDeleteRules.map((rule) => (
                        <TableRow key={rule.id}>
                          <TableCell sx={{ borderBottom: '1px solid var(--rs-border)' }}>
                            <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                              <Box sx={{ width: 12, height: 12, borderRadius: '50%', bgcolor: rule.category_color || '#666' }} />
                              <Typography variant="body2">{rule.category_name}</Typography>
                              {rule.category_type && rule.category_type !== 'other' && (
                                <Chip label={rule.category_type} size="small" sx={{ fontSize: '0.65rem', height: 20 }} />
                              )}
                            </Box>
                          </TableCell>
                          <TableCell sx={{ borderBottom: '1px solid var(--rs-border)' }}>
                            <Typography variant="body2">{rule.days_after_last_use} days</Typography>
                          </TableCell>
                          <TableCell sx={{ borderBottom: '1px solid var(--rs-border)' }}>
                            <Chip
                              label={rule.delete_file ? 'Yes' : 'DB only'}
                              size="small"
                              color={rule.delete_file ? 'error' : 'default'}
                              sx={{ fontSize: '0.7rem', height: 22 }}
                            />
                          </TableCell>
                          <TableCell sx={{ borderBottom: '1px solid var(--rs-border)' }}>
                            <Typography variant="body2">{rule.asset_count || 0}</Typography>
                          </TableCell>
                          <TableCell sx={{ borderBottom: '1px solid var(--rs-border)' }}>
                            <Switch
                              checked={rule.enabled}
                              onChange={() => handleToggleAutoDeleteRule(rule)}
                              size="small"
                              color="primary"
                            />
                          </TableCell>
                          <TableCell sx={{ borderBottom: '1px solid var(--rs-border)' }}>
                            <Box sx={{ display: 'flex', gap: 0.5 }}>
                              <IconButton
                                size="small"
                                onClick={() => {
                                  setAutoDeleteForm({
                                    category_id: rule.category_id,
                                    enabled: rule.enabled,
                                    days_after_last_use: rule.days_after_last_use,
                                    delete_file: rule.delete_file,
                                  });
                                  setAutoDeleteDialogOpen(true);
                                }}
                                sx={{ color: '#22d3ee' }}
                              >
                                <Edit fontSize="small" />
                              </IconButton>
                              <IconButton
                                size="small"
                                onClick={() => handleDeleteAutoDeleteRule(rule.id)}
                                sx={{ color: '#ff6b6b' }}
                              >
                                <Delete fontSize="small" />
                              </IconButton>
                            </Box>
                          </TableCell>
                        </TableRow>
                      ))}
                    </TableBody>
                  </Table>
                </TableContainer>
              )}
            </CardContent>
          </Card>

          {/* Generated Mixblock Cleanup */}
          <Card sx={{ bgcolor: 'var(--rs-paper)', border: '1px solid var(--rs-border)', mb: 3 }}>
            <CardContent>
              <Typography variant="subtitle1" sx={{ color: '#22d3ee', mb: 1 }}>
                Generated Mixblock Cleanup
              </Typography>
              <Typography variant="body2" color="text.secondary" sx={{ mb: 2 }}>
                Automatically delete auto-generated mixblock audio files after a set number of days.
                These are the mixed audio files created by the beat-mix engine for scheduled playout.
              </Typography>
              <Grid container spacing={2} alignItems="center">
                <Grid item xs={12} sm={3}>
                  <FormControlLabel
                    control={
                      <Switch
                        checked={mixblockAutoDelete.enabled}
                        onChange={(e) => setMixblockAutoDelete({ ...mixblockAutoDelete, enabled: e.target.checked })}
                        color="primary"
                      />
                    }
                    label="Enabled"
                  />
                </Grid>
                <Grid item xs={12} sm={4}>
                  <TextField
                    fullWidth
                    type="number"
                    label="Delete after (days)"
                    value={mixblockAutoDelete.days}
                    onChange={(e) => setMixblockAutoDelete({ ...mixblockAutoDelete, days: parseInt(e.target.value) || 60 })}
                    inputProps={{ min: 1, max: 3650 }}
                    size="small"
                    helperText="Generated mixblocks older than this will be removed"
                  />
                </Grid>
                <Grid item xs={12} sm={3}>
                  <Button
                    variant="contained"
                    onClick={handleSaveMixblockAutoDelete}
                    disabled={mixblockAutoDeleteSaving}
                    sx={{ bgcolor: '#22d3ee', '&:hover': { bgcolor: '#00b8e6' } }}
                  >
                    {mixblockAutoDeleteSaving ? 'Saving...' : 'Save'}
                  </Button>
                </Grid>
              </Grid>
            </CardContent>
          </Card>

          {/* Preview panel */}
          {autoDeletePreview && (
            <Card sx={{ bgcolor: 'var(--rs-paper)', border: '1px solid var(--rs-border)', mb: 3 }}>
              <CardContent>
                <Typography variant="subtitle1" sx={{ color: '#f59e0b', mb: 2 }}>
                  Preview: {autoDeletePreview.count} asset(s) would be deleted
                </Typography>
                {autoDeletePreview.count === 0 ? (
                  <Typography variant="body2" color="text.secondary">
                    No assets currently match any active auto-delete rules. Everything is up to date.
                  </Typography>
                ) : (
                  <TableContainer component={Paper} sx={{ bgcolor: 'transparent', maxHeight: 300 }}>
                    <Table size="small" stickyHeader>
                      <TableHead>
                        <TableRow>
                          <TableCell sx={{ bgcolor: 'var(--rs-paper)', color: 'var(--rs-muted)', borderBottom: '1px solid var(--rs-border)' }}>Title</TableCell>
                          <TableCell sx={{ bgcolor: 'var(--rs-paper)', color: 'var(--rs-muted)', borderBottom: '1px solid var(--rs-border)' }}>Artist</TableCell>
                          <TableCell sx={{ bgcolor: 'var(--rs-paper)', color: 'var(--rs-muted)', borderBottom: '1px solid var(--rs-border)' }}>Category</TableCell>
                          <TableCell sx={{ bgcolor: 'var(--rs-paper)', color: 'var(--rs-muted)', borderBottom: '1px solid var(--rs-border)' }}>Last Used</TableCell>
                          <TableCell sx={{ bgcolor: 'var(--rs-paper)', color: 'var(--rs-muted)', borderBottom: '1px solid var(--rs-border)' }}>Rule</TableCell>
                        </TableRow>
                      </TableHead>
                      <TableBody>
                        {autoDeletePreview.assets.map((asset) => (
                          <TableRow key={asset.id}>
                            <TableCell sx={{ borderBottom: '1px solid var(--rs-border)' }}>
                              <Typography variant="body2">{asset.title}</Typography>
                            </TableCell>
                            <TableCell sx={{ borderBottom: '1px solid var(--rs-border)' }}>
                              <Typography variant="body2" color="text.secondary">{asset.artist || '—'}</Typography>
                            </TableCell>
                            <TableCell sx={{ borderBottom: '1px solid var(--rs-border)' }}>
                              <Typography variant="body2">{asset.category_name}</Typography>
                            </TableCell>
                            <TableCell sx={{ borderBottom: '1px solid var(--rs-border)' }}>
                              <Typography variant="body2" color="text.secondary">
                                {asset.effective_last_use ? new Date(asset.effective_last_use).toLocaleDateString() : 'Never'}
                              </Typography>
                            </TableCell>
                            <TableCell sx={{ borderBottom: '1px solid var(--rs-border)' }}>
                              <Typography variant="body2">{asset.days_after_last_use}d</Typography>
                            </TableCell>
                          </TableRow>
                        ))}
                      </TableBody>
                    </Table>
                  </TableContainer>
                )}
                <Button
                  variant="text"
                  size="small"
                  onClick={() => setAutoDeletePreview(null)}
                  sx={{ mt: 1, color: 'var(--rs-muted)' }}
                >
                  Dismiss Preview
                </Button>
              </CardContent>
            </Card>
          )}

          {/* Info card */}
          <Card sx={{ bgcolor: '#1a2a1a', border: '1px solid #2a4a2a' }}>
            <CardContent>
              <Typography variant="subtitle2" sx={{ color: '#22c55e', mb: 1 }}>
                How Auto Delete Works
              </Typography>
              <Typography variant="body2" color="text.secondary" sx={{ mb: 0.5 }}>
                1. Add a rule for any category (e.g. Voicetrack, AI generated content, etc.)
              </Typography>
              <Typography variant="body2" color="text.secondary" sx={{ mb: 0.5 }}>
                2. Set the number of days after last use before assets are automatically deleted.
              </Typography>
              <Typography variant="body2" color="text.secondary" sx={{ mb: 0.5 }}>
                3. "Last use" is based on the most recent time the asset was played on air or scheduled.
                   If never played, the upload date is used instead.
              </Typography>
              <Typography variant="body2" color="text.secondary" sx={{ mb: 0.5 }}>
                4. The cleanup runs automatically every 6 hours. You can also run it manually.
              </Typography>
              <Typography variant="body2" color="text.secondary">
                5. Deleted assets are removed from the database and optionally the filesystem, freeing up storage space.
              </Typography>
            </CardContent>
          </Card>

          {/* Add/Edit Rule Dialog */}
          <Dialog open={autoDeleteDialogOpen} onClose={() => setAutoDeleteDialogOpen(false)} maxWidth="sm" fullWidth>
            <DialogTitle sx={{ bgcolor: 'var(--rs-paper)', color: '#22d3ee' }}>
              {autoDeleteForm.category_id && autoDeleteRules.some(r => r.category_id === autoDeleteForm.category_id)
                ? 'Edit Auto Delete Rule'
                : 'Add Auto Delete Rule'}
            </DialogTitle>
            <DialogContent sx={{ bgcolor: 'var(--rs-paper)' }}>
              <Grid container spacing={2} sx={{ mt: 1 }}>
                <Grid item xs={12}>
                  <FormControl fullWidth>
                    <InputLabel>Category</InputLabel>
                    <Select
                      value={autoDeleteForm.category_id}
                      label="Category"
                      onChange={(e) => setAutoDeleteForm({ ...autoDeleteForm, category_id: e.target.value })}
                    >
                      {autoDeleteCategories
                        .filter(c => !c.has_rule || c.id === autoDeleteForm.category_id)
                        .map(c => (
                          <MenuItem key={c.id} value={c.id}>
                            <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                              <Box sx={{ width: 10, height: 10, borderRadius: '50%', bgcolor: c.color || '#666' }} />
                              {c.name}
                              <Typography variant="caption" color="text.secondary" sx={{ ml: 1 }}>
                                ({c.asset_count} assets)
                              </Typography>
                            </Box>
                          </MenuItem>
                        ))}
                    </Select>
                  </FormControl>
                </Grid>
                <Grid item xs={12} sm={6}>
                  <TextField
                    fullWidth
                    type="number"
                    label="Days after last use"
                    value={autoDeleteForm.days_after_last_use}
                    onChange={(e) => setAutoDeleteForm({ ...autoDeleteForm, days_after_last_use: parseInt(e.target.value) || 60 })}
                    inputProps={{ min: 1, max: 3650 }}
                    helperText="Assets unused for this many days will be deleted"
                  />
                </Grid>
                <Grid item xs={12} sm={6}>
                  <FormControlLabel
                    control={
                      <Switch
                        checked={autoDeleteForm.delete_file}
                        onChange={(e) => setAutoDeleteForm({ ...autoDeleteForm, delete_file: e.target.checked })}
                      />
                    }
                    label="Also delete audio file"
                    sx={{ mt: 1 }}
                  />
                  <Typography variant="caption" color="text.secondary" sx={{ display: 'block' }}>
                    When disabled, only the database record is removed
                  </Typography>
                </Grid>
                <Grid item xs={12}>
                  <FormControlLabel
                    control={
                      <Switch
                        checked={autoDeleteForm.enabled}
                        onChange={(e) => setAutoDeleteForm({ ...autoDeleteForm, enabled: e.target.checked })}
                      />
                    }
                    label="Enable this rule"
                  />
                </Grid>
              </Grid>
            </DialogContent>
            <DialogActions sx={{ bgcolor: 'var(--rs-paper)' }}>
              <Button onClick={() => setAutoDeleteDialogOpen(false)}>Cancel</Button>
              <Button
                variant="contained"
                onClick={handleSaveAutoDeleteRule}
                disabled={autoDeleteSaving}
                sx={{ bgcolor: '#22d3ee', '&:hover': { bgcolor: '#00b8e6' } }}
              >
                {autoDeleteSaving ? 'Saving...' : 'Save Rule'}
              </Button>
            </DialogActions>
          </Dialog>
        </Box>
      )}

      {/* Playback Log Cleanup Tab */}
      {activeTab === 'playback_log' && (() => {
        // Auto-load stats and retention setting when tab first renders
        const loadPlaybackLogData = async () => {
          try {
            const [statsRes, retRes] = await Promise.all([
              axios.get(`${API_URL}/api/playback-log/stats`, { withCredentials: true }),
              axios.get(`${API_URL}/api/playback-log/retention`, { withCredentials: true }),
            ]);
            setPlaybackLogStats(statsRes.data);
            setPlaybackLogRetention(retRes.data);
          } catch (err) {
            console.error('Error loading playback log data:', err);
          }
        };
        if (!playbackLogStats) loadPlaybackLogData();

        return (
        <Box>
          <Typography variant="h6" sx={{ color: '#22d3ee', mb: 1 }}>
            Playback Log Management
          </Typography>
          <Typography variant="body2" color="text.secondary" sx={{ mb: 3 }}>
            The playback log records every asset played on air, powering the Reports tab in the Asset Library.
            This is completely separate from the Traffic Logger (audio recordings) and does not affect it in any way.
          </Typography>

          {/* Stats card */}
          <Card sx={{ bgcolor: 'var(--rs-paper)', border: '1px solid var(--rs-border)', mb: 3 }}>
            <CardContent>
              <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', mb: 2 }}>
                <Typography variant="subtitle1" sx={{ color: '#22d3ee' }}>Log Statistics</Typography>
                <Button
                  size="small"
                  variant="outlined"
                  onClick={async () => {
                    try {
                      const res = await axios.get(`${API_URL}/api/playback-log/stats`, { withCredentials: true });
                      setPlaybackLogStats(res.data);
                    } catch (err) {
                      console.error('Error fetching playback log stats:', err);
                    }
                  }}
                  sx={{ borderColor: '#22d3ee', color: '#22d3ee' }}
                >
                  Refresh Stats
                </Button>
              </Box>
              {playbackLogStats ? (
                <Grid container spacing={2}>
                  <Grid item xs={6} sm={3}>
                    <Typography variant="caption" color="text.secondary">Total Entries</Typography>
                    <Typography variant="h6" sx={{ color: 'var(--rs-text)' }}>{playbackLogStats.total_entries || 0}</Typography>
                  </Grid>
                  <Grid item xs={6} sm={3}>
                    <Typography variant="caption" color="text.secondary">Unique Assets</Typography>
                    <Typography variant="h6" sx={{ color: 'var(--rs-text)' }}>{playbackLogStats.unique_assets || 0}</Typography>
                  </Grid>
                  <Grid item xs={6} sm={3}>
                    <Typography variant="caption" color="text.secondary">Oldest Entry</Typography>
                    <Typography variant="body2" sx={{ color: 'var(--rs-text)' }}>
                      {playbackLogStats.oldest_entry ? new Date(playbackLogStats.oldest_entry).toLocaleDateString() : '—'}
                    </Typography>
                  </Grid>
                  <Grid item xs={6} sm={3}>
                    <Typography variant="caption" color="text.secondary">Table Size</Typography>
                    <Typography variant="body2" sx={{ color: 'var(--rs-text)' }}>{playbackLogStats.table_size || '—'}</Typography>
                  </Grid>
                </Grid>
              ) : (
                <Typography variant="body2" color="text.secondary">
                  Loading statistics…
                </Typography>
              )}
            </CardContent>
          </Card>

          {/* Automatic retention card */}
          <Card sx={{ bgcolor: 'var(--rs-paper)', border: '1px solid var(--rs-border)', mb: 3 }}>
            <CardContent>
              <Typography variant="subtitle1" sx={{ color: '#22d3ee', mb: 1 }}>Automatic Cleanup Schedule</Typography>
              <Typography variant="body2" color="text.secondary" sx={{ mb: 2 }}>
                When enabled, old playback log entries are automatically deleted once per day at 3:00 AM.
                For example, setting 60 days means any entry that reaches its 60th day will be removed overnight.
              </Typography>
              <Grid container spacing={2} alignItems="center">
                <Grid item xs={12} sm={3}>
                  <FormControlLabel
                    control={
                      <Switch
                        checked={playbackLogRetention.enabled}
                        onChange={(e) => setPlaybackLogRetention(prev => ({ ...prev, enabled: e.target.checked }))}
                      />
                    }
                    label="Enable automatic cleanup"
                  />
                </Grid>
                <Grid item xs={12} sm={4}>
                  <FormControl fullWidth disabled={!playbackLogRetention.enabled}>
                    <InputLabel>Delete entries older than</InputLabel>
                    <Select
                      value={playbackLogRetention.retention_days}
                      label="Delete entries older than"
                      onChange={(e) => setPlaybackLogRetention(prev => ({ ...prev, retention_days: e.target.value }))}
                    >
                      <MenuItem value={7}>7 days</MenuItem>
                      <MenuItem value={14}>14 days</MenuItem>
                      <MenuItem value={30}>30 days</MenuItem>
                      <MenuItem value={60}>60 days</MenuItem>
                      <MenuItem value={90}>90 days</MenuItem>
                      <MenuItem value={180}>180 days</MenuItem>
                      <MenuItem value={365}>1 year</MenuItem>
                      <MenuItem value={730}>2 years</MenuItem>
                    </Select>
                  </FormControl>
                </Grid>
                <Grid item xs={12} sm={3}>
                  <Button
                    variant="contained"
                    disabled={playbackLogRetentionSaving}
                    onClick={async () => {
                      setPlaybackLogRetentionSaving(true);
                      setPlaybackLogMessage({ type: '', text: '' });
                      try {
                        await axios.put(`${API_URL}/api/playback-log/retention`, playbackLogRetention, { withCredentials: true });
                        setPlaybackLogMessage({
                          type: 'success',
                          text: playbackLogRetention.enabled
                            ? `Saved — entries older than ${playbackLogRetention.retention_days} days will be auto-deleted daily at 3:00 AM`
                            : 'Automatic cleanup disabled — log data will be kept indefinitely',
                        });
                      } catch (err) {
                        setPlaybackLogMessage({ type: 'error', text: err.response?.data?.message || 'Failed to save' });
                      } finally {
                        setPlaybackLogRetentionSaving(false);
                      }
                    }}
                    sx={{ bgcolor: '#22d3ee', '&:hover': { bgcolor: '#00b8e6' } }}
                  >
                    {playbackLogRetentionSaving ? 'Saving…' : 'Save Setting'}
                  </Button>
                </Grid>
              </Grid>
              {playbackLogRetention.enabled && (
                <Alert severity="info" sx={{ mt: 2 }}>
                  Entries older than {playbackLogRetention.retention_days} days will be automatically removed every night at 3:00 AM.
                </Alert>
              )}
              {playbackLogMessage.text && (
                <Alert severity={playbackLogMessage.type || 'info'} sx={{ mt: 2 }}>
                  {playbackLogMessage.text}
                </Alert>
              )}
            </CardContent>
          </Card>

          {/* Manual cleanup card */}
          <Card sx={{ bgcolor: 'var(--rs-paper)', border: '1px solid var(--rs-border)', mb: 3 }}>
            <CardContent>
              <Typography variant="subtitle1" sx={{ color: '#f59e0b', mb: 1 }}>Manual Cleanup</Typography>
              <Typography variant="body2" color="text.secondary" sx={{ mb: 2 }}>
                Run a one-off cleanup now, or wipe all data. This does not change the scheduled setting above.
              </Typography>
              <Box sx={{ display: 'flex', gap: 2, flexWrap: 'wrap' }}>
                <Button
                  variant="contained"
                  color="warning"
                  disabled={playbackLogCleaning}
                  onClick={async () => {
                    const days = playbackLogRetention.retention_days || 90;
                    if (!window.confirm(`Delete all playback log entries older than ${days} days? This cannot be undone.`)) return;
                    setPlaybackLogCleaning(true);
                    setPlaybackLogMessage({ type: '', text: '' });
                    try {
                      const res = await axios.delete(`${API_URL}/api/playback-log/cleanup`, {
                        data: { older_than_days: days },
                        withCredentials: true,
                      });
                      setPlaybackLogMessage({ type: 'success', text: res.data.message });
                      const statsRes = await axios.get(`${API_URL}/api/playback-log/stats`, { withCredentials: true });
                      setPlaybackLogStats(statsRes.data);
                    } catch (err) {
                      setPlaybackLogMessage({ type: 'error', text: err.response?.data?.message || 'Cleanup failed' });
                    } finally {
                      setPlaybackLogCleaning(false);
                    }
                  }}
                >
                  {playbackLogCleaning ? 'Cleaning…' : `Delete entries > ${playbackLogRetention.retention_days || 90} days now`}
                </Button>
                <Button
                  variant="outlined"
                  color="error"
                  disabled={playbackLogCleaning}
                  onClick={async () => {
                    if (!window.confirm('Delete ALL playback log entries? This removes ALL reporting data and cannot be undone.')) return;
                    if (!window.confirm('Final warning — ALL playback data will be permanently lost. Continue?')) return;
                    setPlaybackLogCleaning(true);
                    setPlaybackLogMessage({ type: '', text: '' });
                    try {
                      await axios.delete(`${API_URL}/api/playback-log/cleanup`, {
                        data: { older_than_days: 0 },
                        withCredentials: true,
                      });
                      setPlaybackLogMessage({ type: 'success', text: 'All playback log data cleared' });
                      const statsRes = await axios.get(`${API_URL}/api/playback-log/stats`, { withCredentials: true });
                      setPlaybackLogStats(statsRes.data);
                    } catch (err) {
                      setPlaybackLogMessage({ type: 'error', text: err.response?.data?.message || 'Cleanup failed' });
                    } finally {
                      setPlaybackLogCleaning(false);
                    }
                  }}
                >
                  Clear All Data
                </Button>
              </Box>
            </CardContent>
          </Card>

          {/* Info card */}
          <Card sx={{ bgcolor: 'var(--rs-bg)', border: '1px solid var(--rs-surface)' }}>
            <CardContent>
              <Typography variant="subtitle2" sx={{ color: '#888', mb: 1 }}>How it works</Typography>
              <Typography variant="body2" color="text.secondary" sx={{ mb: 0.5 }}>
                1. Every time an asset plays on air, it is logged with the asset, station, time, duration, and categories.
              </Typography>
              <Typography variant="body2" color="text.secondary" sx={{ mb: 0.5 }}>
                2. The "Reports" tab in the Asset Library lets you run on-demand reports grouped by asset, category, hour, or day.
              </Typography>
              <Typography variant="body2" color="text.secondary" sx={{ mb: 0.5 }}>
                3. Playback reports can be downloaded as Excel or PDF. Items in each category can also be downloaded as Excel or PDF.
              </Typography>
              <Typography variant="body2" color="text.secondary" sx={{ mb: 0.5 }}>
                4. When auto-cleanup is enabled, old entries are purged daily at 3:00 AM based on the retention period you set.
              </Typography>
              <Typography variant="body2" color="text.secondary" sx={{ mb: 0.5 }}>
                5. This only affects the playback log (<em>asset_playback_log</em>). It does <strong>not</strong> touch Traffic Logger recordings, 
                   Dropbox data, playout schedules, or any other tables.
              </Typography>
              <Typography variant="body2" color="text.secondary">
                6. You can also run a manual cleanup or clear all data at any time using the buttons above.
              </Typography>
            </CardContent>
          </Card>
        </Box>
        );
      })()}

      {/* Audio Output and Processing Tab */}
      {activeTab === 'audio_processing' && (
        <Box>
          {audioProcessingMessage.text && (
            <Alert
              severity={audioProcessingMessage.type === 'error' ? 'error' : 'success'}
              sx={{ mb: 2 }}
              onClose={() => setAudioProcessingMessage({ type: '', text: '' })}
            >
              {audioProcessingMessage.text}
            </Alert>
          )}

          {!audioProcessingEnabled && !isSuperAdmin() && (
            <Alert severity="info">
              Audio processing has not been enabled by the system administrator.
            </Alert>
          )}

          {!audioProcessingEnabled && isSuperAdmin() && (
            <Alert severity="info" sx={{ mb: 2 }}>
              Enable Audio Processing under Station and App Configuration → App Features.
            </Alert>
          )}

          {audioProcessingEnabled && (
            <>
              <Alert severity="info" sx={{ mb: 3 }}>
                Click <strong>+</strong> next to any station to enable processing for it. The station's Icecast
                mountpoint is connected automatically — no manual URL entry needed.
              </Alert>

              {/* Station list */}
              <Card sx={{ bgcolor: 'var(--rs-paper)', border: '1px solid var(--rs-border)', mb: 3 }}>
                <CardContent>
                  <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', mb: 2 }}>
                    <Typography variant="h6" sx={{ color: '#22d3ee' }}>
                      Stream Processing
                    </Typography>
                    <Button
                      size="small"
                      startIcon={<RefreshIcon />}
                      onClick={() => { fetchAudioProcessingStatus(); fetchAudioProcessingStations(); }}
                      sx={{ color: '#22d3ee' }}
                    >
                      Refresh
                    </Button>
                  </Box>

                    <TableContainer component={Paper} sx={{ bgcolor: 'transparent', border: '1px solid var(--rs-border)' }}>
                    <Table size="small">
                      <TableHead>
                        <TableRow>
                          <TableCell sx={{ color: '#22d3ee' }}>Station</TableCell>
                          <TableCell sx={{ color: '#22d3ee' }}>Preset</TableCell>
                          <TableCell sx={{ color: '#22d3ee' }}>Output Relay</TableCell>
                          <TableCell sx={{ color: '#22d3ee' }}>Status</TableCell>
                          <TableCell sx={{ color: '#22d3ee', width: 180 }}>Actions</TableCell>
                        </TableRow>
                      </TableHead>
                      <TableBody>
                        {audioProcessingStations.length === 0 ? (
                          <TableRow>
                            <TableCell colSpan={5} sx={{ color: 'text.secondary', textAlign: 'center', py: 3 }}>
                              No stations configured. Add stations in Station and App Configuration first.
                            </TableCell>
                          </TableRow>
                        ) : audioProcessingStations.map((station) => {
                          const instance = audioProcessingInstances.find(
                            (inst) => station.target_type === 'region'
                              ? inst.region_id === station.region_id
                              : (inst.region_id == null && inst.station_id === (station.station_id || station.id))
                          );
                          const isRunning = instance?.status === 'running';
                          const preset = audioProcessingPresets.find((p) => p.id === instance?.preset);
                          return (
                            <TableRow key={station.target_key || `${station.target_type || 'station'}-${station.id}`}>
                              <TableCell>
                                <Typography variant="body2" sx={{ fontWeight: 600 }}>{station.name}</Typography>
                                {station.call_sign && (
                                  <Typography variant="caption" color="text.secondary">{station.call_sign}</Typography>
                                )}
                              </TableCell>
                              <TableCell>
                                {instance ? (
                                  <Chip
                                    label={preset?.name || instance.preset || 'Default'}
                                    size="small"
                                    variant="outlined"
                                    sx={{ borderColor: '#5b6675', fontSize: '0.7rem' }}
                                  />
                                ) : (
                                  <Typography variant="caption" color="text.secondary">—</Typography>
                                )}
                              </TableCell>
                              <TableCell>
                                {instance && (instance.outputs || []).filter((o) => o.enabled).length > 0 ? (
                                  <Chip
                                    label={`${(instance.outputs || []).filter((o) => o.enabled).length} active`}
                                    size="small"
                                    color="primary"
                                    variant="outlined"
                                    sx={{ fontSize: '0.7rem' }}
                                  />
                                ) : (
                                  <Typography variant="caption" color="text.secondary">None</Typography>
                                )}
                              </TableCell>
                              <TableCell>
                                {instance ? (
                                  <Chip
                                    icon={isRunning ? <PlayArrow sx={{ fontSize: '0.9rem !important' }} /> : <StopIcon sx={{ fontSize: '0.9rem !important' }} />}
                                    label={isRunning ? 'Running' : (instance.status === 'error' ? 'Error' : 'Stopped')}
                                    color={isRunning ? 'success' : (instance.status === 'error' ? 'error' : 'default')}
                                    size="small"
                                  />
                                ) : (
                                  <Chip label="Not configured" size="small" variant="outlined" sx={{ borderColor: '#3a4456', color: '#888', fontSize: '0.65rem' }} />
                                )}
                              </TableCell>
                              <TableCell>
                                <Box sx={{ display: 'flex', gap: 0.5 }}>
                                  <IconButton
                                    size="small"
                                    sx={{ color: '#22d3ee' }}
                                    title={instance ? 'Edit settings' : 'Enable & configure'}
                                    onClick={() => handleOpenAudioProcessingDialog(station, instance)}
                                  >
                                    {instance ? <Edit fontSize="small" /> : <Add fontSize="small" />}
                                  </IconButton>
                                  {instance && !isRunning && (
                                    <IconButton
                                      size="small"
                                      color="success"
                                      title="Start processing"
                                      onClick={() => handleStartAudioProcessing(instance.id)}
                                      disabled={audioProcessingLoading}
                                    >
                                      <PlayArrow fontSize="small" />
                                    </IconButton>
                                  )}
                                  {instance && isRunning && (
                                    <IconButton
                                      size="small"
                                      color="warning"
                                      title="Stop processing"
                                      onClick={() => handleStopAudioProcessing(instance.id)}
                                      disabled={audioProcessingLoading}
                                    >
                                      <StopIcon fontSize="small" />
                                    </IconButton>
                                  )}
                                  {instance && (
                                    <IconButton
                                      size="small"
                                      sx={{ color: 'var(--rs-muted)' }}
                                      title="View logs"
                                      onClick={() => handleViewAudioProcessingLogs(instance.id)}
                                    >
                                      <LogsIcon fontSize="small" />
                                    </IconButton>
                                  )}
                                  {instance && (
                                    <IconButton
                                      size="small"
                                      color="error"
                                      title="Remove from audio processing"
                                      onClick={() => handleDeleteAudioProcessingInstance(instance.id)}
                                    >
                                      <Delete fontSize="small" />
                                    </IconButton>
                                  )}
                                </Box>
                              </TableCell>
                            </TableRow>
                          );
                        })}
                      </TableBody>
                    </Table>
                  </TableContainer>
                </CardContent>
              </Card>
            </>
          )}

          {/* Configure Dialog */}
          <Dialog
            open={audioProcessingDialog.open}
            onClose={() => setAudioProcessingDialog({ open: false, station: null, instance: null })}
            maxWidth="md"
            fullWidth
          >
            <DialogTitle sx={{ color: '#22d3ee', pb: 0 }}>
              {audioProcessingDialog.instance ? 'Edit Audio Output and Processing' : 'Enable Audio Output and Processing'}
            </DialogTitle>
            <DialogContent>
              {/* Station info header */}
              {audioProcessingDialog.station && (
                <Box sx={{ mb: 2, mt: 1, p: 1.5, bgcolor: 'var(--rs-bg)', borderRadius: 1, border: '1px solid var(--rs-border)' }}>
                  <Typography variant="subtitle1" sx={{ fontWeight: 600 }}>
                    {audioProcessingDialog.station.name}
                    {audioProcessingDialog.station.call_sign && (
                      <Typography component="span" variant="caption" color="text.secondary" sx={{ ml: 1 }}>
                        {audioProcessingDialog.station.call_sign}
                      </Typography>
                    )}
                  </Typography>
                  <Typography variant="caption" color="text.secondary" sx={{ display: 'block', mt: 0.5 }}>
                    Automatically connected from this station's Icecast mountpoint. Auto-starts on every server restart.
                  </Typography>
                </Box>
              )}

              {/* DSP Preset */}
              <FormControl fullWidth size="small" sx={{ mb: 3 }}>
                <InputLabel>DSP Processing Preset</InputLabel>
                <Select
                  label="DSP Processing Preset"
                  value={audioProcessingForm.preset}
                  onChange={(e) => setAudioProcessingForm({ ...audioProcessingForm, preset: e.target.value })}
                >
                  {audioProcessingPresets.map((p) => (
                    <MenuItem key={p.id} value={p.id}>
                      <Box>
                        <Typography variant="body2">{p.name}</Typography>
                        <Typography variant="caption" color="text.secondary">{p.description}</Typography>
                      </Box>
                    </MenuItem>
                  ))}
                </Select>
              </FormControl>

              {/* Outputs section */}
              <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', mb: 1 }}>
                <Typography variant="subtitle2" sx={{ color: '#22d3ee' }}>
                  Outputs ({audioProcessingForm.outputs.length} / 5)
                </Typography>
                <Button
                  size="small"
                  startIcon={<Add />}
                  onClick={apAddOutput}
                  disabled={audioProcessingForm.outputs.length >= 5}
                  sx={{ color: '#22d3ee' }}
                >
                  Add Output
                </Button>
              </Box>

              {audioProcessingForm.outputs.length === 0 && (
                <Typography variant="body2" color="text.secondary" sx={{ mb: 2, fontStyle: 'italic' }}>
                  No outputs configured. Click "Add Output" to relay the processed audio to an Icecast, Shoutcast, or custom destination.
                </Typography>
              )}

              {audioProcessingForm.outputs.map((output, idx) => (
                <Card key={output._tempId} sx={{ bgcolor: 'var(--rs-bg)', border: '1px solid var(--rs-border)', mb: 2 }}>
                  <CardContent sx={{ pb: '12px !important' }}>
                    {/* Output header */}
                    <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', mb: 1.5 }}>
                      <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                        <Typography variant="subtitle2" sx={{ color: 'var(--rs-muted)' }}>
                          Output {idx + 1}
                        </Typography>
                        <FormControlLabel
                          control={
                            <Switch
                              size="small"
                              checked={output.enabled}
                              onChange={(e) => apUpdateOutput(output._tempId, 'enabled', e.target.checked)}
                              color="primary"
                            />
                          }
                          label={<Typography variant="caption">{output.enabled ? 'Enabled' : 'Disabled'}</Typography>}
                          sx={{ ml: 0 }}
                        />
                      </Box>
                      <IconButton size="small" color="error" onClick={() => apRemoveOutput(output._tempId, output.id)} title="Remove output">
                        <Delete fontSize="small" />
                      </IconButton>
                    </Box>

                    <Grid container spacing={1.5}>
                      {/* Name */}
                      <Grid item xs={12} sm={6}>
                        <TextField
                          fullWidth size="small" label="Output Name"
                          placeholder={`Output ${idx + 1}`}
                          value={output.name}
                          onChange={(e) => apUpdateOutput(output._tempId, 'name', e.target.value)}
                        />
                      </Grid>

                      {/* Provider */}
                      <Grid item xs={12} sm={6}>
                        <FormControl fullWidth size="small">
                          <InputLabel>Type</InputLabel>
                          <Select
                            label="Type"
                            value={output.provider}
                            onChange={(e) => apUpdateOutput(output._tempId, 'provider', e.target.value)}
                          >
                            <MenuItem value="icecast">Icecast</MenuItem>
                            <MenuItem value="shoutcast">Shoutcast</MenuItem>
                            <MenuItem value="custom">Custom URL</MenuItem>
                          </Select>
                        </FormControl>
                      </Grid>

                      {output.provider === 'custom' ? (
                        /* Custom URL mode */
                        <>
                          <Grid item xs={12}>
                            <TextField
                              fullWidth size="small" label="Target URL"
                              placeholder="icecast://source:password@host:8000/mount"
                              value={output.custom_url}
                              onChange={(e) => apUpdateOutput(output._tempId, 'custom_url', e.target.value)}
                              helperText="Full FFmpeg output URL, e.g. icecast://source:pass@host:8000/live.mp3"
                            />
                          </Grid>
                          <Grid item xs={12} sm={6}>
                            <FormControl fullWidth size="small">
                              <InputLabel>Format</InputLabel>
                              <Select label="Format" value={output.format} onChange={(e) => apUpdateOutputFormat(output._tempId, e.target.value)}>
                                <MenuItem value="mp3">MP3</MenuItem>
                                <MenuItem value="aac_lc">AAC-LC</MenuItem>
                                <MenuItem value="aac_he_v2" disabled={audioProcessingCodecs.capabilities?.aac_he_v2 === false}>
                                  HE-AAC v2
                                </MenuItem>
                                <MenuItem value="opus" disabled={audioProcessingCodecs.capabilities?.opus === false}>Opus</MenuItem>
                                <MenuItem value="flac" disabled={audioProcessingCodecs.capabilities?.flac === false}>FLAC</MenuItem>
                              </Select>
                            </FormControl>
                          </Grid>
                          <Grid item xs={12} sm={6}>
                            <TextField
                              fullWidth size="small" label="Bitrate (kbps)" type="number"
                              value={output.bitrate ?? ''}
                              onChange={(e) => apUpdateOutput(
                                output._tempId,
                                'bitrate',
                                e.target.value === '' ? null : parseInt(e.target.value, 10)
                              )}
                              disabled={output.format === 'flac'}
                              helperText={output.format === 'flac' ? 'Lossless; bitrate is determined by the audio.' : ''}
                              inputProps={{
                                min: output.format === 'aac_he_v2' ? 24 : 64,
                                max: output.format === 'aac_he_v2' ? 64 : 320,
                                step: output.format === 'aac_he_v2' ? 8 : 32,
                              }}
                            />
                          </Grid>
                        </>
                      ) : (
                        /* Icecast / Shoutcast mode */
                        <>
                          <Grid item xs={12} sm={7}>
                            <TextField
                              fullWidth size="small" label="Host"
                              placeholder="stream.example.com"
                              value={output.host}
                              onChange={(e) => apUpdateOutput(output._tempId, 'host', e.target.value)}
                            />
                          </Grid>
                          <Grid item xs={12} sm={5}>
                            <TextField
                              fullWidth size="small" label="Port" type="number"
                              value={output.port}
                              onChange={(e) => apUpdateOutput(output._tempId, 'port', parseInt(e.target.value) || 8000)}
                              inputProps={{ min: 1, max: 65535 }}
                            />
                          </Grid>
                          <Grid item xs={12} sm={6}>
                            <TextField
                              fullWidth size="small" label="Mount Point"
                              placeholder="/stream"
                              value={output.mount}
                              onChange={(e) => apUpdateOutput(output._tempId, 'mount', e.target.value)}
                            />
                          </Grid>
                          <Grid item xs={12} sm={6}>
                            <FormControl fullWidth size="small">
                              <InputLabel>Format</InputLabel>
                              <Select label="Format" value={output.format} onChange={(e) => apUpdateOutputFormat(output._tempId, e.target.value)}>
                                <MenuItem value="mp3">MP3</MenuItem>
                                <MenuItem value="aac_lc">AAC-LC</MenuItem>
                                <MenuItem value="aac_he_v2" disabled={audioProcessingCodecs.capabilities?.aac_he_v2 === false}>
                                  HE-AAC v2
                                </MenuItem>
                                <MenuItem
                                  value="opus"
                                  disabled={output.provider === 'shoutcast' || audioProcessingCodecs.capabilities?.opus === false}
                                >
                                  Opus
                                </MenuItem>
                                <MenuItem
                                  value="flac"
                                  disabled={output.provider === 'shoutcast' || audioProcessingCodecs.capabilities?.flac === false}
                                >
                                  FLAC
                                </MenuItem>
                              </Select>
                            </FormControl>
                          </Grid>
                          <Grid item xs={12} sm={4}>
                            <TextField
                              fullWidth size="small" label="Bitrate (kbps)" type="number"
                              value={output.bitrate ?? ''}
                              onChange={(e) => apUpdateOutput(
                                output._tempId,
                                'bitrate',
                                e.target.value === '' ? null : parseInt(e.target.value, 10)
                              )}
                              disabled={output.format === 'flac'}
                              helperText={output.format === 'flac' ? 'Lossless; bitrate is determined by the audio.' : ''}
                              inputProps={{
                                min: output.format === 'aac_he_v2' ? 24 : 64,
                                max: output.format === 'aac_he_v2' ? 64 : 320,
                                step: output.format === 'aac_he_v2' ? 8 : 32,
                              }}
                            />
                          </Grid>
                          <Grid item xs={12} sm={4}>
                            <TextField
                              fullWidth size="small" label="Username"
                              value={output.username}
                              onChange={(e) => apUpdateOutput(output._tempId, 'username', e.target.value)}
                            />
                          </Grid>
                          <Grid item xs={12} sm={4}>
                            <TextField
                              fullWidth size="small" label="Password" type="password"
                              value={output.password}
                              onChange={(e) => apUpdateOutput(output._tempId, 'password', e.target.value)}
                            />
                          </Grid>
                        </>
                      )}
                    </Grid>
                  </CardContent>
                </Card>
              ))}
            </DialogContent>
            <DialogActions>
              <Button onClick={() => setAudioProcessingDialog({ open: false, station: null, instance: null })}>
                Cancel
              </Button>
              <Button
                variant="contained"
                onClick={handleSaveAudioProcessingInstance}
                disabled={audioProcessingLoading}
                sx={{ bgcolor: '#22d3ee', '&:hover': { bgcolor: '#00b8e6' } }}
              >
                {audioProcessingLoading ? 'Saving…' : 'Save'}
              </Button>
            </DialogActions>
          </Dialog>

          {/* Logs Dialog */}
          <Dialog
            open={audioProcessingLogsOpen !== null}
            onClose={() => setAudioProcessingLogsOpen(null)}
            maxWidth="md"
            fullWidth
          >
            <DialogTitle>
              Processing Logs
              <IconButton
                size="small"
                sx={{ ml: 1, color: 'var(--rs-muted)' }}
                onClick={() => {
                  if (audioProcessingLogsOpen !== null) handleViewAudioProcessingLogs(audioProcessingLogsOpen);
                }}
                title="Refresh logs"
              >
                <RefreshIcon fontSize="small" />
              </IconButton>
            </DialogTitle>
            <DialogContent>
              <Box
                component="pre"
                sx={{
                  bgcolor: 'var(--rs-bg)',
                  border: '1px solid var(--rs-border)',
                  borderRadius: 1,
                  p: 2,
                  fontFamily: 'monospace',
                  fontSize: '0.75rem',
                  color: '#ccc',
                  overflow: 'auto',
                  maxHeight: 400,
                  whiteSpace: 'pre-wrap',
                  wordBreak: 'break-all',
                }}
              >
                {audioProcessingLogs || '(no log output yet)'}
              </Box>
            </DialogContent>
            <DialogActions>
              <Button onClick={() => setAudioProcessingLogsOpen(null)}>Close</Button>
            </DialogActions>
          </Dialog>
        </Box>
      )}

      {/* Storage Tab */}
      {activeTab === 'storage' && (
        <Box>
          <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', mb: 3 }}>
            <Typography variant="h6" sx={{ color: '#22d3ee' }}>
              Library Storage
            </Typography>
            <Button
              variant="outlined"
              startIcon={<RefreshIcon />}
              onClick={fetchStorageInfo}
              disabled={storageLoading}
              size="small"
              sx={{ borderColor: '#3a4456', color: 'var(--rs-muted)', '&:hover': { borderColor: '#22d3ee', color: '#22d3ee' } }}
            >
              Refresh
            </Button>
          </Box>

          {storageMessage.text && (
            <Alert severity={storageMessage.type} sx={{ mb: 2 }} onClose={() => setStorageMessage({ type: '', text: '' })}>
              {storageMessage.text}
              {storageMessage.details?.length > 0 && (
                <Box component="ul" sx={{ mt: 1, mb: 0, pl: 2.5, maxHeight: 240, overflow: 'auto' }}>
                  {storageMessage.details.map((warning, index) => (
                    <li key={`${index}-${warning}`}>
                      <Typography variant="body2">{warning}</Typography>
                    </li>
                  ))}
                </Box>
              )}
            </Alert>
          )}

          {storageLoading ? (
            <Box sx={{ display: 'flex', justifyContent: 'center', py: 6 }}>
              <CircularProgress sx={{ color: '#22d3ee' }} />
            </Box>
          ) : storageInfo ? (
            <>
              {/* Usage card */}
              <Card sx={{ bgcolor: 'var(--rs-paper)', border: '1px solid var(--rs-border)', mb: 3 }}>
                <CardContent>
                  <Typography variant="subtitle1" sx={{ color: '#22d3ee', mb: 2 }}>
                    Current Usage
                  </Typography>

                  {(() => {
                    const pct = storageInfo.percentage_used || 0;
                    const color = pct >= 95 ? '#ef4444' : pct >= 90 ? '#f97316' : pct >= 75 ? '#f59e0b' : '#22c55e';
                    const usedGb = (storageInfo.used_bytes / (1024 * 1024 * 1024)).toFixed(2);
                    const limitGb = (storageInfo.limit_bytes / (1024 * 1024 * 1024)).toFixed(2);
                    const freeGb = Math.max(0, storageInfo.limit_bytes - storageInfo.used_bytes) / (1024 * 1024 * 1024);

                    return (
                      <>
                        <Box sx={{ display: 'flex', justifyContent: 'space-between', mb: 1 }}>
                          <Typography variant="body2" sx={{ color: 'var(--rs-muted)' }}>
                            {usedGb} GB used of {limitGb} GB
                          </Typography>
                          <Typography variant="body2" sx={{ color, fontWeight: 700 }}>
                            {pct}%
                          </Typography>
                        </Box>
                        <LinearProgress
                          variant="determinate"
                          value={Math.min(100, pct)}
                          sx={{
                            height: 12,
                            borderRadius: 6,
                            bgcolor: 'var(--rs-border)',
                            mb: 2,
                            '& .MuiLinearProgress-bar': { bgcolor: color, borderRadius: 6 },
                          }}
                        />
                        <Box sx={{ display: 'flex', gap: 3 }}>
                          <Box>
                            <Typography variant="caption" sx={{ color: '#666', textTransform: 'uppercase', letterSpacing: 1 }}>Used</Typography>
                            <Typography variant="body1" sx={{ color: 'var(--rs-text)', fontWeight: 600 }}>{usedGb} GB</Typography>
                          </Box>
                          <Box>
                            <Typography variant="caption" sx={{ color: '#666', textTransform: 'uppercase', letterSpacing: 1 }}>Limit</Typography>
                            <Typography variant="body1" sx={{ color: 'var(--rs-text)', fontWeight: 600 }}>{limitGb} GB</Typography>
                          </Box>
                          <Box>
                            <Typography variant="caption" sx={{ color: '#666', textTransform: 'uppercase', letterSpacing: 1 }}>Available</Typography>
                            <Typography variant="body1" sx={{ color: '#22c55e', fontWeight: 600 }}>{freeGb.toFixed(2)} GB</Typography>
                          </Box>
                        </Box>
                        {pct >= 90 && (
                          <Alert
                            severity={pct >= 95 ? 'error' : 'warning'}
                            sx={{ mt: 2 }}
                          >
                            {pct >= 100
                              ? 'Storage limit reached. New uploads may be blocked.'
                              : `Storage is at ${pct}%. Admins will be notified by email. Consider removing unused assets or increasing the limit.`}
                          </Alert>
                        )}
                      </>
                    );
                  })()}
                </CardContent>
              </Card>

              {/* Limit configuration — superadmin only */}
              {isSuperAdmin() && (
                <>
                  <Card sx={{ bgcolor: 'var(--rs-paper)', border: '1px solid #22d3ee55', mb: 3 }}>
                    <CardContent>
                      <Typography variant="subtitle1" sx={{ color: '#22d3ee', mb: 1 }}>
                        Canonical Playout Audio
                      </Typography>
                      <Typography variant="body2" sx={{ color: '#888', mb: 2 }}>
                        Select the format used for new uploads, generated playout audio, and the internal
                        Icecast source feed. Changing it migrates all existing playable files in the background.
                        FLAC gives the highest quality and uses considerably more storage. Opus uses 320 kbps
                        but remains a lossy format.
                      </Typography>
                      <Box sx={{ display: 'flex', alignItems: 'center', gap: 2, flexWrap: 'wrap' }}>
                        <FormControl size="small" sx={{ minWidth: 240 }}>
                          <InputLabel>Playout Format</InputLabel>
                          <Select
                            label="Playout Format"
                            value={playoutAudioFormat}
                            onChange={(e) => setPlayoutAudioFormat(e.target.value)}
                          >
                            <MenuItem
                              value="flac"
                              disabled={playoutAudioFormatInfo?.capabilities?.canonical?.flac === false}
                            >
                              FLAC (lossless)
                            </MenuItem>
                            <MenuItem
                              value="opus_320"
                              disabled={playoutAudioFormatInfo?.capabilities?.canonical?.opus_320 === false}
                            >
                              Opus 320 kbps
                            </MenuItem>
                          </Select>
                        </FormControl>
                        <Button
                          variant="contained"
                          onClick={handleSavePlayoutAudioFormat}
                          disabled={playoutAudioFormatSaving}
                          sx={{ bgcolor: '#22d3ee', color: '#000', '&:hover': { bgcolor: '#00b8e6' } }}
                        >
                          {playoutAudioFormatSaving ? 'Applying…' : 'Apply Format'}
                        </Button>
                      </Box>

                      {playoutAudioFormatInfo?.migration && (
                        <Box sx={{ mt: 2 }}>
                          <Box sx={{ display: 'flex', justifyContent: 'space-between', mb: 0.75 }}>
                            <Typography variant="body2" sx={{ color: 'var(--rs-muted)' }}>
                              Library migration: {playoutAudioFormatInfo.migration.status}
                            </Typography>
                            <Typography variant="body2" sx={{ color: '#22d3ee' }}>
                              {playoutAudioFormatInfo.migration.completed_items || 0}
                              {' / '}
                              {playoutAudioFormatInfo.migration.total_items || 0}
                            </Typography>
                          </Box>
                          <LinearProgress
                            variant={
                              ['queued', 'running'].includes(playoutAudioFormatInfo.migration.status)
                              && !playoutAudioFormatInfo.migration.total_items
                                ? 'indeterminate'
                                : 'determinate'
                            }
                            value={playoutAudioFormatInfo.migration.percent || 0}
                            sx={{ bgcolor: 'var(--rs-border)', '& .MuiLinearProgress-bar': { bgcolor: '#22d3ee' } }}
                          />
                          {!!playoutAudioFormatInfo.migration.failed_items && (
                            <Alert
                              severity="warning"
                              sx={{ mt: 1.5 }}
                              action={
                                playoutAudioFormatInfo.migration.status === 'failed'
                                  ? <Button color="inherit" size="small" onClick={handleRetryAudioFormatMigration}>Retry</Button>
                                  : null
                              }
                            >
                              {playoutAudioFormatInfo.migration.failed_items} file(s) could not be migrated.
                            </Alert>
                          )}
                        </Box>
                      )}
                    </CardContent>
                  </Card>

                  <Card sx={{ bgcolor: 'var(--rs-paper)', border: '1px solid var(--rs-border)', mb: 3 }}>
                    <CardContent>
                      <Typography variant="subtitle1" sx={{ color: '#22d3ee', mb: 1 }}>
                        Set Storage Limit
                      </Typography>
                      <Typography variant="body2" sx={{ color: '#888', mb: 2 }}>
                        Define the maximum amount of library asset storage this installation is allowed to use.
                        A warning email is sent to all admins when usage reaches 90% or above.
                      </Typography>
                      <Box sx={{ display: 'flex', alignItems: 'center', gap: 2 }}>
                        <TextField
                          label="Storage Limit (GB)"
                          type="number"
                          value={storageLimitGb}
                          onChange={(e) => setStorageLimitGb(e.target.value)}
                          inputProps={{ min: 1, step: 1 }}
                          size="small"
                          sx={{ width: 200 }}
                        />
                        <Button
                          variant="contained"
                          onClick={handleSaveStorageLimit}
                          disabled={storageSaving}
                          sx={{ bgcolor: '#22d3ee', '&:hover': { bgcolor: '#00b8e6' }, color: '#000' }}
                        >
                          {storageSaving ? 'Saving…' : 'Save Limit'}
                        </Button>
                      </Box>
                    </CardContent>
                  </Card>

                  <Card sx={{ bgcolor: 'var(--rs-paper)', border: '1px solid #22d3ee55', mb: 3 }}>
                    <CardContent>
                      <Typography variant="subtitle1" sx={{ color: '#22d3ee', mb: 1 }}>
                        Portable Library Transfer
                      </Typography>
                      <Typography variant="body2" sx={{ color: '#888', mb: 2 }}>
                        Export the complete library as a ZIP with category folders, audio,
                        related files such as .mmd sidecars, and all cue, segue, intro, hook,
                        and beatmix marker data. Import the ZIP on another RadioSuite system
                        to recreate its categories, files, metadata, and markers.
                      </Typography>
                      <Box sx={{ display: 'flex', flexWrap: 'wrap', gap: 2 }}>
                        <Button
                          variant="contained"
                          startIcon={<DownloadIcon />}
                          onClick={handlePortableLibraryExport}
                          disabled={portableImporting}
                          sx={{ bgcolor: '#22d3ee', color: '#000', '&:hover': { bgcolor: '#00b8e6' } }}
                        >
                          Download Entire Library
                        </Button>
                        <Button
                          component="label"
                          variant="outlined"
                          startIcon={portableImporting
                            ? <CircularProgress size={16} sx={{ color: '#22d3ee' }} />
                            : <UploadFileIcon />}
                          disabled={portableImporting}
                          sx={{ borderColor: '#22d3ee', color: '#22d3ee', '&:hover': { borderColor: '#00b8e6', bgcolor: '#22d3ee22' } }}
                        >
                          {portableImporting ? 'Importing…' : 'Import Portable Library'}
                          <input
                            hidden
                            type="file"
                            accept=".zip,application/zip"
                            onChange={handlePortableLibraryImport}
                          />
                        </Button>
                      </Box>
                      {portableImporting && (
                        <Box sx={{ mt: 2 }}>
                          <LinearProgress
                            variant={portableImportProgress > 0 && portableImportProgress < 100 ? 'determinate' : 'indeterminate'}
                            value={portableImportProgress}
                            sx={{ bgcolor: 'var(--rs-border)', '& .MuiLinearProgress-bar': { bgcolor: '#22d3ee' } }}
                          />
                          <Typography variant="caption" sx={{ color: '#888', mt: 0.75, display: 'block' }}>
                            {portableImportProgress > 0 && portableImportProgress < 100
                              ? `Uploading archive… ${portableImportProgress}%`
                              : 'Restoring files, categories, and marker data…'}
                          </Typography>
                        </Box>
                      )}
                    </CardContent>
                  </Card>

                  <Card sx={{ bgcolor: 'var(--rs-paper)', border: '1px solid #f59e0b55', mb: 3 }}>
                    <CardContent>
                      <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', mb: 1 }}>
                        <Typography variant="subtitle1" sx={{ color: '#f59e0b' }}>
                          Recover Missing Audio
                        </Typography>
                        <Box sx={{ display: 'flex', gap: 1 }}>
                          <Button
                            variant="outlined"
                            size="small"
                            startIcon={<DownloadIcon />}
                            onClick={handleDownloadMissingReport}
                            disabled={!missingAudio || missingAudio.missing_count === 0 || missingAudioLoading}
                            sx={{ borderColor: '#3a4456', color: 'var(--rs-muted)', '&:hover': { borderColor: '#f59e0b', color: '#f59e0b' } }}
                          >
                            Download Report
                          </Button>
                          <Button
                            variant="outlined"
                            size="small"
                            startIcon={missingAudioLoading ? <CircularProgress size={14} sx={{ color: 'var(--rs-muted)' }} /> : <RefreshIcon />}
                            onClick={fetchMissingAudio}
                            disabled={missingAudioLoading || recoveryUploading}
                            sx={{ borderColor: '#3a4456', color: 'var(--rs-muted)', '&:hover': { borderColor: '#f59e0b', color: '#f59e0b' } }}
                          >
                            Rescan
                          </Button>
                        </Box>
                      </Box>
                      <Typography variant="body2" sx={{ color: '#888', mb: 2 }}>
                        Re-upload source audio for library assets whose files are missing from disk.
                        Uploads are matched by filename, embedded artist/title tags, and duration, then
                        converted losslessly to the library format and restored under the original
                        filename — so all cue, segue, intro, and fade markers keep working. Existing
                        audio is never overwritten: files whose audio is already present are skipped,
                        so it is safe to bulk-select your entire source folder.
                      </Typography>

                      {missingAudio && (
                        <Alert
                          severity={missingAudio.missing_count > 0 ? 'warning' : 'success'}
                          sx={{ mb: 2 }}
                        >
                          {missingAudio.missing_count > 0
                            ? `${missingAudio.missing_count} of ${missingAudio.total_assets} library assets are missing their audio file.`
                            : 'All library assets have their audio present on disk.'}
                        </Alert>
                      )}

                      {missingAudio?.missing_count > 0 && (
                        <Box sx={{ display: 'flex', alignItems: 'center', gap: 2, flexWrap: 'wrap', mb: 2 }}>
                          <FormControl size="small" sx={{ minWidth: 260 }}>
                            <InputLabel sx={{ color: '#888' }}>Match within category</InputLabel>
                            <Select
                              value={recoveryCategory}
                              label="Match within category"
                              onChange={(e) => setRecoveryCategory(e.target.value)}
                              disabled={recoveryUploading}
                              sx={{ color: 'var(--rs-text)' }}
                            >
                              <MenuItem value="">All categories ({missingAudio.missing_count})</MenuItem>
                              {(missingAudio.categories || []).map((category) => (
                                <MenuItem key={category.id} value={category.id}>
                                  {category.name} ({category.missing_count})
                                </MenuItem>
                              ))}
                            </Select>
                          </FormControl>
                          <Button
                            component="label"
                            variant="contained"
                            startIcon={recoveryUploading
                              ? <CircularProgress size={16} sx={{ color: '#000' }} />
                              : <UploadFileIcon />}
                            disabled={recoveryUploading}
                            sx={{ bgcolor: '#f59e0b', color: '#000', '&:hover': { bgcolor: '#d97706' } }}
                          >
                            {recoveryUploading
                              ? `Recovering… ${recoveryProgress.done}/${recoveryProgress.total}`
                              : 'Upload Source Audio'}
                            <input
                              hidden
                              type="file"
                              multiple
                              accept=".mp3,.wav,.flac,.ogg,.opus,.m4a,.aac,audio/*"
                              onChange={(e) => handleRecoveryUpload(e)}
                            />
                          </Button>
                          {recoveryCategory && (
                            <Typography variant="caption" sx={{ color: '#888' }}>
                              Uploads will only be matched against missing assets in this category —
                              ideal when your source folders mirror categories.
                            </Typography>
                          )}
                        </Box>
                      )}

                      {recoveryUploading && (
                        <Box sx={{ mb: 2 }}>
                          <LinearProgress
                            variant={recoveryProgress.total > 0 ? 'determinate' : 'indeterminate'}
                            value={recoveryProgress.total > 0
                              ? Math.round((recoveryProgress.done / recoveryProgress.total) * 100)
                              : 0}
                            sx={{ bgcolor: 'var(--rs-border)', '& .MuiLinearProgress-bar': { bgcolor: '#f59e0b' } }}
                          />
                          <Typography variant="caption" sx={{ color: '#888', mt: 0.75, display: 'block' }}>
                            Uploading and converting… {recoveryProgress.done} of {recoveryProgress.total} files processed
                            {recoveryResults ? ` — ${recoveryResults.restored.length} restored so far` : ''}
                          </Typography>
                        </Box>
                      )}

                      {recoveryResults && !recoveryUploading && (
                        <Box sx={{ mb: 2 }}>
                          <Typography variant="body2" sx={{ color: 'var(--rs-text)', mb: 1 }}>
                            Last run: <strong style={{ color: '#22c55e' }}>{recoveryResults.restored.length} restored</strong>
                            {', '}{recoveryResults.skipped.length} skipped
                            {', '}{recoveryResults.unmatched.length} unmatched
                          </Typography>
                          {recoveryResults.unmatched.length > 0 && (
                            <Box sx={{ maxHeight: 200, overflow: 'auto', bgcolor: '#0f131b', border: '1px solid var(--rs-border)', borderRadius: 1, p: 1 }}>
                              {recoveryResults.unmatched.map((entry, idx) => (
                                <Typography key={`${idx}-${entry.file}`} variant="caption" sx={{ color: '#f97316', display: 'block' }}>
                                  {entry.file} — {entry.reason}
                                </Typography>
                              ))}
                            </Box>
                          )}
                        </Box>
                      )}

                      {missingAudio?.missing_count > 0 && (
                        <>
                          <TextField
                            placeholder="Filter missing assets…"
                            size="small"
                            value={missingAudioFilter}
                            onChange={(e) => setMissingAudioFilter(e.target.value)}
                            sx={{ width: 320, mb: 1 }}
                          />
                          {(() => {
                            const filter = missingAudioFilter.trim().toLowerCase();
                            const filtered = (missingAudio.missing || []).filter((asset) => {
                              if (recoveryCategory
                                && !(asset.category_ids || []).includes(recoveryCategory)) {
                                return false;
                              }
                              return !filter
                                || String(asset.title || '').toLowerCase().includes(filter)
                                || String(asset.artist || '').toLowerCase().includes(filter)
                                || String(asset.filename || '').toLowerCase().includes(filter)
                                || (asset.category_names || []).some(
                                  (name) => String(name).toLowerCase().includes(filter)
                                );
                            });
                            const shown = filtered.slice(0, 200);
                            return (
                              <Box sx={{ maxHeight: 320, overflow: 'auto', bgcolor: '#0f131b', border: '1px solid var(--rs-border)', borderRadius: 1 }}>
                                {shown.map((asset) => {
                                  const rowState = rowUploads[asset.id];
                                  return (
                                    <Box
                                      key={asset.id}
                                      sx={{
                                        display: 'flex',
                                        alignItems: 'center',
                                        justifyContent: 'space-between',
                                        gap: 1,
                                        px: 1.5,
                                        py: 0.5,
                                        borderBottom: '1px solid #1a2130',
                                      }}
                                    >
                                      <Box sx={{ minWidth: 0 }}>
                                        <Typography variant="body2" sx={{ color: 'var(--rs-text)', whiteSpace: 'nowrap', overflow: 'hidden', textOverflow: 'ellipsis' }}>
                                          {asset.artist ? `${asset.artist} — ${asset.title}` : asset.title}
                                        </Typography>
                                        <Typography variant="caption" sx={{ color: '#666' }}>
                                          {asset.filename}
                                          {asset.category_names?.length > 0 && ` — ${asset.category_names.join(', ')}`}
                                        </Typography>
                                        {rowState?.status === 'error' && (
                                          <Typography variant="caption" sx={{ color: '#f97316', display: 'block' }}>
                                            {rowState.message}
                                          </Typography>
                                        )}
                                      </Box>
                                      {rowState?.status === 'restored' ? (
                                        <Chip
                                          label="Restored"
                                          size="small"
                                          sx={{ flexShrink: 0, bgcolor: '#22c55e22', color: '#22c55e', border: '1px solid #22c55e55' }}
                                        />
                                      ) : (
                                        <Button
                                          component="label"
                                          size="small"
                                          variant="outlined"
                                          disabled={recoveryUploading || rowState?.status === 'uploading'}
                                          startIcon={rowState?.status === 'uploading'
                                            ? <CircularProgress size={12} sx={{ color: 'var(--rs-muted)' }} />
                                            : null}
                                          sx={{ flexShrink: 0, borderColor: '#3a4456', color: 'var(--rs-muted)', '&:hover': { borderColor: '#f59e0b', color: '#f59e0b' } }}
                                        >
                                          {rowState?.status === 'uploading'
                                            ? 'Uploading…'
                                            : rowState?.status === 'error' ? 'Retry' : 'Upload'}
                                          <input
                                            hidden
                                            type="file"
                                            accept=".mp3,.wav,.flac,.ogg,.opus,.m4a,.aac,audio/*"
                                            onChange={(e) => handleRowRecoveryUpload(e, asset.id)}
                                          />
                                        </Button>
                                      )}
                                    </Box>
                                  );
                                })}
                                {filtered.length > shown.length && (
                                  <Typography variant="caption" sx={{ color: '#666', display: 'block', px: 1.5, py: 0.75 }}>
                                    …and {filtered.length - shown.length} more. Use the filter to narrow the list.
                                  </Typography>
                                )}
                              </Box>
                            );
                          })()}
                        </>
                      )}
                    </CardContent>
                  </Card>

                  <Card sx={{ bgcolor: 'var(--rs-paper)', border: '1px solid var(--rs-border)' }}>
                    <CardContent>
                      <Typography variant="subtitle1" sx={{ color: '#22d3ee', mb: 1 }}>
                        Recalculate Usage
                      </Typography>
                      <Typography variant="body2" sx={{ color: '#888', mb: 2 }}>
                        Scan all library assets and populate missing file size data from disk. Run this once to fix usage showing as 0.00 GB for assets uploaded before this feature was tracked.
                      </Typography>
                      <Button
                        variant="outlined"
                        onClick={handleBackfillFileSizes}
                        sx={{ borderColor: '#22d3ee', color: '#22d3ee', '&:hover': { borderColor: '#00b8e6', bgcolor: '#22d3ee22' } }}
                      >
                        Recalculate File Sizes
                      </Button>
                    </CardContent>
                  </Card>
                </>
              )}
            </>
          ) : (
            <Typography color="text.secondary">Unable to load storage information.</Typography>
          )}
        </Box>
      )}

      {/* ═══════════════════════════ BACKUP TAB ═══════════════════════════ */}
      {activeTab === 'backup' && (
        <Box>
          <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', mb: 3 }}>
            <Typography variant="h6" sx={{ color: '#22d3ee', display: 'flex', alignItems: 'center', gap: 1 }}>
              <BackupIcon /> Backup
            </Typography>
            <Box sx={{ display: 'flex', gap: 1 }}>
              <Button
                variant="outlined"
                size="small"
                startIcon={<RefreshIcon />}
                onClick={() => { fetchBackupJobs(); fetchBackupDropboxFiles(); }}
                sx={{ borderColor: '#3a4456', color: 'var(--rs-muted)', '&:hover': { borderColor: '#22d3ee', color: '#22d3ee' } }}
              >
                Refresh
              </Button>
              <Button
                variant="contained"
                size="small"
                startIcon={backupRunning ? <CircularProgress size={14} sx={{ color: '#000' }} /> : <BackupIcon />}
                onClick={handleRunBackup}
                disabled={backupRunning}
                sx={{ bgcolor: '#22d3ee', color: '#000', '&:hover': { bgcolor: '#00b8e6' }, fontWeight: 700 }}
              >
                {backupRunning ? 'Starting…' : 'Run Backup Now'}
              </Button>
            </Box>
          </Box>

          {backupMessage.text && (
            <Alert severity={backupMessage.type || 'info'} sx={{ mb: 2 }} onClose={() => setBackupMessage({ type: '', text: '' })}>
              {backupMessage.text}
            </Alert>
          )}
          {backupConfigMessage.text && (
            <Alert severity={backupConfigMessage.type || 'info'} sx={{ mb: 2 }} onClose={() => setBackupConfigMessage({ type: '', text: '' })}>
              {backupConfigMessage.text}
            </Alert>
          )}

          {backupConfigLoading ? (
            <Box sx={{ display: 'flex', justifyContent: 'center', py: 6 }}>
              <CircularProgress sx={{ color: '#22d3ee' }} />
            </Box>
          ) : (
            <>
              {/* ── Superadmin Controls ────────────────────────────────── */}
              {isSuperAdmin() && (
                <Card sx={{ bgcolor: 'var(--rs-paper)', border: '1px solid #f59e0b55', mb: 3 }}>
                  <CardContent>
                    <Typography variant="h6" sx={{ color: '#f59e0b', mb: 1, display: 'flex', alignItems: 'center', gap: 1 }}>
                      <SettingsIcon sx={{ fontSize: 18 }} /> Superadmin Backup Controls
                    </Typography>
                    <Typography variant="body2" sx={{ color: '#888', mb: 3 }}>
                      Control who manages backups and whether the backup tab is visible to admins.
                    </Typography>
                    <Grid container spacing={3}>
                      <Grid item xs={12} sm={6}>
                        <FormControl fullWidth size="small">
                          <InputLabel sx={{ color: 'var(--rs-muted)' }}>Managed By</InputLabel>
                          <Select
                            label="Managed By"
                            value={backupConfig.backup_managed_by || 'client'}
                            onChange={e => setBackupConfig(p => ({ ...p, backup_managed_by: e.target.value }))}
                            sx={{ bgcolor: 'var(--rs-bg)', color: 'var(--rs-text)', '& .MuiOutlinedInput-notchedOutline': { borderColor: '#3a4456' } }}
                          >
                            <MenuItem value="client">Client / Admin manages backups</MenuItem>
                            <MenuItem value="superadmin">Superadmin manages backups</MenuItem>
                          </Select>
                        </FormControl>
                      </Grid>
                      <Grid item xs={12} sm={6}>
                        <FormControlLabel
                          control={
                            <Switch
                              checked={backupConfig.backup_hide_from_admin === 'true'}
                              onChange={e => setBackupConfig(p => ({ ...p, backup_hide_from_admin: e.target.checked ? 'true' : 'false' }))}
                              sx={{ '& .MuiSwitch-switchBase.Mui-checked': { color: '#f59e0b' }, '& .MuiSwitch-switchBase.Mui-checked + .MuiSwitch-track': { bgcolor: '#f59e0b' } }}
                            />
                          }
                          label={
                            <Box>
                              <Typography variant="body2" sx={{ fontWeight: 600 }}>Hide Backup tab from Admins</Typography>
                              <Typography variant="caption" sx={{ color: '#888' }}>Admins will not see the Backup tab at all</Typography>
                            </Box>
                          }
                        />
                      </Grid>
                      {/* Backup Type – superadmin only (full backup option) */}
                      <Grid item xs={12} sm={6}>
                        <FormControl fullWidth size="small">
                          <InputLabel sx={{ color: 'var(--rs-muted)' }}>Backup Type</InputLabel>
                          <Select
                            label="Backup Type"
                            value={backupConfig.backup_type || 'audio'}
                            onChange={e => setBackupConfig(p => ({ ...p, backup_type: e.target.value }))}
                            sx={{ bgcolor: 'var(--rs-bg)', color: 'var(--rs-text)', '& .MuiOutlinedInput-notchedOutline': { borderColor: '#3a4456' } }}
                          >
                            <MenuItem value="audio">Audio files only (by category)</MenuItem>
                            <MenuItem value="full">Full backup (audio + database + settings)</MenuItem>
                          </Select>
                        </FormControl>
                        <Typography variant="caption" sx={{ color: '#888', mt: 0.5, display: 'block' }}>
                          Full backup includes a pg_dump of the database and all system settings.
                        </Typography>
                      </Grid>
                      {/* Dropbox type – superadmin can add a dedicated 3rd connection */}
                      <Grid item xs={12} sm={6}>
                        <FormControl fullWidth size="small">
                          <InputLabel sx={{ color: 'var(--rs-muted)' }}>Dropbox Connection</InputLabel>
                          <Select
                            label="Dropbox Connection"
                            value={backupConfig.backup_dropbox_type || 'existing'}
                            onChange={e => setBackupConfig(p => ({ ...p, backup_dropbox_type: e.target.value }))}
                            sx={{ bgcolor: 'var(--rs-bg)', color: 'var(--rs-text)', '& .MuiOutlinedInput-notchedOutline': { borderColor: '#3a4456' } }}
                          >
                            <MenuItem value="existing">Use existing Dropbox connection</MenuItem>
                            <MenuItem value="dedicated">Dedicated backup Dropbox account</MenuItem>
                          </Select>
                        </FormControl>
                      </Grid>
                    </Grid>

                    {/* Dedicated Dropbox connection (superadmin only) */}
                    {backupConfig.backup_dropbox_type === 'dedicated' && (
                      <Box sx={{ mt: 3, p: 2, bgcolor: '#0d0d0d', borderRadius: 1, border: '1px solid var(--rs-border)' }}>
                        <Typography variant="subtitle2" sx={{ color: '#22d3ee', mb: 2 }}>
                          Dedicated Backup Dropbox Account
                        </Typography>
                        {backupDedicatedLoading ? (
                          <CircularProgress size={20} sx={{ color: '#22d3ee' }} />
                        ) : backupDedicatedDropbox.connected ? (
                          <Box sx={{ display: 'flex', alignItems: 'center', gap: 2, flexWrap: 'wrap' }}>
                            <CheckCircleIcon sx={{ color: '#22c55e', fontSize: 20 }} />
                            <Box>
                              <Typography variant="body2" sx={{ color: 'var(--rs-text)', fontWeight: 600 }}>
                                {backupDedicatedDropbox.account_display_name || 'Connected'}
                              </Typography>
                              <Typography variant="caption" sx={{ color: '#888' }}>
                                {backupDedicatedDropbox.account_email}
                              </Typography>
                            </Box>
                            <Button
                              variant="outlined"
                              size="small"
                              color="error"
                              startIcon={<CancelIcon />}
                              onClick={handleDisconnectDedicatedDropbox}
                              sx={{ ml: 'auto' }}
                            >
                              Disconnect
                            </Button>
                          </Box>
                        ) : (
                          <Box>
                            <Typography variant="body2" sx={{ color: 'var(--rs-muted)', mb: 2 }}>
                              No dedicated backup Dropbox connected. Connect a separate Dropbox account exclusively for backups.
                            </Typography>
                            <Button
                              variant="contained"
                              size="small"
                              startIcon={<DropboxIcon />}
                              onClick={handleConnectDedicatedDropbox}
                              sx={{ bgcolor: '#0061fe', '&:hover': { bgcolor: '#0052d9' } }}
                            >
                              Connect Backup Dropbox
                            </Button>
                          </Box>
                        )}
                      </Box>
                    )}
                  </CardContent>
                </Card>
              )}

              {/* ── Backup Destination ────────────────────────────────── */}
              <Card sx={{ bgcolor: 'var(--rs-paper)', border: '1px solid var(--rs-border)', mb: 3 }}>
                <CardContent>
                  <Typography variant="h6" sx={{ color: '#22d3ee', mb: 2, display: 'flex', alignItems: 'center', gap: 1 }}>
                    <DropboxIcon sx={{ fontSize: 20 }} /> Backup Destination
                  </Typography>
                  <Grid container spacing={2} alignItems="flex-start">
                    <Grid item xs={12} sm={8}>
                      <TextField
                        fullWidth
                        label="Dropbox Backup Folder"
                        size="small"
                        value={backupConfig.backup_dropbox_path || ''}
                        onChange={e => setBackupConfig(p => ({ ...p, backup_dropbox_path: e.target.value }))}
                        placeholder="/RadioSuiteBackups"
                        helperText="Folder path in Dropbox where backups will be stored (e.g. /RadioSuiteBackups)"
                        sx={{ '& .MuiInputBase-root': { bgcolor: 'var(--rs-bg)' }, '& label': { color: 'var(--rs-muted)' }, '& .MuiFormHelperText-root': { color: '#666' } }}
                      />
                    </Grid>
                    {!isSuperAdmin() && (
                      <Grid item xs={12}>
                        <Alert severity="info" sx={{ bgcolor: 'var(--rs-bg)', border: '1px solid var(--rs-border)', fontSize: '0.8rem' }}>
                          Audio backups are stored in your connected Dropbox account at the path above, organised by category folder.
                        </Alert>
                      </Grid>
                    )}
                  </Grid>
                </CardContent>
              </Card>

              {/* ── Schedule ─────────────────────────────────────────── */}
              <Card sx={{ bgcolor: 'var(--rs-paper)', border: '1px solid var(--rs-border)', mb: 3 }}>
                <CardContent>
                  <Typography variant="h6" sx={{ color: '#22d3ee', mb: 2, display: 'flex', alignItems: 'center', gap: 1 }}>
                    <ScheduleIcon sx={{ fontSize: 20 }} /> Backup Schedule
                  </Typography>
                  <Grid container spacing={3}>
                    <Grid item xs={12}>
                      <FormControlLabel
                        control={
                          <Switch
                            checked={backupConfig.backup_schedule_enabled === 'true'}
                            onChange={e => setBackupConfig(p => ({ ...p, backup_schedule_enabled: e.target.checked ? 'true' : 'false' }))}
                            sx={{ '& .MuiSwitch-switchBase.Mui-checked': { color: '#22d3ee' }, '& .MuiSwitch-switchBase.Mui-checked + .MuiSwitch-track': { bgcolor: '#22d3ee' } }}
                          />
                        }
                        label={<Typography variant="body2" sx={{ fontWeight: 600 }}>Enable Scheduled Backups</Typography>}
                      />
                    </Grid>
                    {backupConfig.backup_schedule_enabled === 'true' && (
                      <>
                        <Grid item xs={12} sm={6}>
                          <FormControl fullWidth size="small">
                            <InputLabel sx={{ color: 'var(--rs-muted)' }}>Schedule Preset</InputLabel>
                            <Select
                              label="Schedule Preset"
                              value={CRON_PRESETS.find(p => p.value === backupConfig.backup_schedule_cron) ? backupConfig.backup_schedule_cron : '__custom__'}
                              onChange={e => {
                                if (e.target.value !== '__custom__') {
                                  setBackupConfig(p => ({ ...p, backup_schedule_cron: e.target.value }));
                                }
                              }}
                              sx={{ bgcolor: 'var(--rs-bg)', color: 'var(--rs-text)', '& .MuiOutlinedInput-notchedOutline': { borderColor: '#3a4456' } }}
                            >
                              {CRON_PRESETS.map(p => (
                                <MenuItem key={p.value} value={p.value}>{p.label}</MenuItem>
                              ))}
                            </Select>
                          </FormControl>
                        </Grid>
                        <Grid item xs={12} sm={6}>
                          <TextField
                            fullWidth
                            label="Cron Expression"
                            size="small"
                            value={backupConfig.backup_schedule_cron || ''}
                            onChange={e => setBackupConfig(p => ({ ...p, backup_schedule_cron: e.target.value }))}
                            placeholder="0 2 * * *"
                            helperText="Standard cron: minute hour day month weekday"
                            sx={{ '& .MuiInputBase-root': { bgcolor: 'var(--rs-bg)' }, '& label': { color: 'var(--rs-muted)' }, '& .MuiFormHelperText-root': { color: '#666' } }}
                          />
                        </Grid>
                      </>
                    )}
                  </Grid>
                </CardContent>
              </Card>

              {/* ── Auto-Delete ───────────────────────────────────────── */}
              <Card sx={{ bgcolor: 'var(--rs-paper)', border: '1px solid var(--rs-border)', mb: 3 }}>
                <CardContent>
                  <Typography variant="h6" sx={{ color: '#22d3ee', mb: 2, display: 'flex', alignItems: 'center', gap: 1 }}>
                    <AutoDeleteIcon sx={{ fontSize: 20 }} /> Backup Retention & Auto-Delete
                  </Typography>
                  <Grid container spacing={3}>
                    <Grid item xs={12}>
                      <FormControlLabel
                        control={
                          <Switch
                            checked={backupConfig.backup_auto_delete_enabled === 'true'}
                            onChange={e => setBackupConfig(p => ({ ...p, backup_auto_delete_enabled: e.target.checked ? 'true' : 'false' }))}
                            sx={{ '& .MuiSwitch-switchBase.Mui-checked': { color: '#ef4444' }, '& .MuiSwitch-switchBase.Mui-checked + .MuiSwitch-track': { bgcolor: '#ef4444' } }}
                          />
                        }
                        label={<Typography variant="body2" sx={{ fontWeight: 600 }}>Enable Auto-Delete Old Backups</Typography>}
                      />
                    </Grid>
                    {backupConfig.backup_auto_delete_enabled === 'true' && (
                      <>
                        <Grid item xs={12} sm={4}>
                          <FormControl fullWidth size="small">
                            <InputLabel sx={{ color: 'var(--rs-muted)' }}>Retention Mode</InputLabel>
                            <Select
                              label="Retention Mode"
                              value={backupConfig.backup_auto_delete_mode || 'days'}
                              onChange={e => setBackupConfig(p => ({ ...p, backup_auto_delete_mode: e.target.value }))}
                              sx={{ bgcolor: 'var(--rs-bg)', color: 'var(--rs-text)', '& .MuiOutlinedInput-notchedOutline': { borderColor: '#3a4456' } }}
                            >
                              <MenuItem value="days">Delete backups older than X days</MenuItem>
                              <MenuItem value="count">Keep only last N backups</MenuItem>
                            </Select>
                          </FormControl>
                        </Grid>
                        {backupConfig.backup_auto_delete_mode === 'days' ? (
                          <Grid item xs={12} sm={4}>
                            <TextField
                              fullWidth
                              label="Delete After (days)"
                              type="number"
                              size="small"
                              value={backupConfig.backup_auto_delete_days || '30'}
                              onChange={e => setBackupConfig(p => ({ ...p, backup_auto_delete_days: e.target.value }))}
                              inputProps={{ min: 1, max: 365 }}
                              helperText="Backups older than this will be deleted from Dropbox"
                              sx={{ '& .MuiInputBase-root': { bgcolor: 'var(--rs-bg)' }, '& label': { color: 'var(--rs-muted)' }, '& .MuiFormHelperText-root': { color: '#666' } }}
                            />
                          </Grid>
                        ) : (
                          <Grid item xs={12} sm={4}>
                            <TextField
                              fullWidth
                              label="Keep Last N Backups"
                              type="number"
                              size="small"
                              value={backupConfig.backup_auto_delete_keep || '5'}
                              onChange={e => setBackupConfig(p => ({ ...p, backup_auto_delete_keep: e.target.value }))}
                              inputProps={{ min: 1, max: 100 }}
                              helperText="Older backups beyond this count will be deleted"
                              sx={{ '& .MuiInputBase-root': { bgcolor: 'var(--rs-bg)' }, '& label': { color: 'var(--rs-muted)' }, '& .MuiFormHelperText-root': { color: '#666' } }}
                            />
                          </Grid>
                        )}
                        <Grid item xs={12} sm={4} sx={{ display: 'flex', alignItems: 'flex-start', pt: '10px !important' }}>
                          <Button
                            variant="outlined"
                            size="small"
                            color="error"
                            startIcon={backupCleanupRunning ? <CircularProgress size={14} color="error" /> : <DeleteForeverIcon />}
                            onClick={handleBackupCleanup}
                            disabled={backupCleanupRunning}
                          >
                            {backupCleanupRunning ? 'Cleaning…' : 'Run Cleanup Now'}
                          </Button>
                        </Grid>
                      </>
                    )}
                  </Grid>
                </CardContent>
              </Card>

              {/* ── Save Config ───────────────────────────────────────── */}
              <Box sx={{ mb: 4 }}>
                <Button
                  variant="contained"
                  onClick={handleSaveBackupConfig}
                  disabled={backupConfigSaving}
                  sx={{ bgcolor: '#22d3ee', color: '#000', fontWeight: 700, '&:hover': { bgcolor: '#00b8e6' } }}
                >
                  {backupConfigSaving ? 'Saving…' : 'Save Backup Settings'}
                </Button>
              </Box>

              {/* ── Backup History ────────────────────────────────────── */}
              <Card sx={{ bgcolor: 'var(--rs-paper)', border: '1px solid var(--rs-border)', mb: 3 }}>
                <CardContent>
                  <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', mb: 2 }}>
                    <Typography variant="h6" sx={{ color: '#22d3ee', display: 'flex', alignItems: 'center', gap: 1 }}>
                      <LogsIcon sx={{ fontSize: 20 }} /> Backup History
                    </Typography>
                    <Button
                      size="small"
                      variant="outlined"
                      startIcon={<RefreshIcon />}
                      onClick={fetchBackupJobs}
                      sx={{ borderColor: '#3a4456', color: 'var(--rs-muted)', '&:hover': { borderColor: '#22d3ee', color: '#22d3ee' } }}
                    >
                      Refresh
                    </Button>
                  </Box>
                  {backupJobsLoading ? (
                    <Box sx={{ display: 'flex', justifyContent: 'center', py: 4 }}>
                      <CircularProgress sx={{ color: '#22d3ee' }} />
                    </Box>
                  ) : backupJobs.length === 0 ? (
                    <Typography variant="body2" sx={{ color: '#666', textAlign: 'center', py: 3 }}>
                      No backups yet. Run your first backup above.
                    </Typography>
                  ) : (
                    <TableContainer>
                      <Table size="small">
                        <TableHead>
                          <TableRow>
                            <TableCell sx={{ color: '#888', borderColor: 'var(--rs-border)' }}>Status</TableCell>
                            <TableCell sx={{ color: '#888', borderColor: 'var(--rs-border)' }}>Type</TableCell>
                            <TableCell sx={{ color: '#888', borderColor: 'var(--rs-border)' }}>Trigger</TableCell>
                            <TableCell sx={{ color: '#888', borderColor: 'var(--rs-border)' }}>File</TableCell>
                            <TableCell sx={{ color: '#888', borderColor: 'var(--rs-border)' }}>Size</TableCell>
                            <TableCell sx={{ color: '#888', borderColor: 'var(--rs-border)' }}>Started</TableCell>
                            <TableCell sx={{ color: '#888', borderColor: 'var(--rs-border)' }}>Duration</TableCell>
                          </TableRow>
                        </TableHead>
                        <TableBody>
                          {backupJobs.map(job => {
                            const duration = job.completed_at && job.started_at
                              ? Math.round((new Date(job.completed_at) - new Date(job.started_at)) / 1000)
                              : null;
                            return (
                              <TableRow key={job.id} sx={{ '&:hover': { bgcolor: 'var(--rs-surface)' } }}>
                                <TableCell sx={{ borderColor: 'var(--rs-surface)' }}>{backupStatusChip(job.status)}</TableCell>
                                <TableCell sx={{ color: 'var(--rs-text)', borderColor: 'var(--rs-surface)', fontSize: '0.8rem' }}>
                                  <Chip label={job.type} size="small" sx={{ bgcolor: job.type === 'full' ? '#7c3aed22' : '#0369a122', color: job.type === 'full' ? '#a78bfa' : '#38bdf8', border: `1px solid ${job.type === 'full' ? '#7c3aed55' : '#0369a155'}`, fontSize: '0.7rem' }} />
                                </TableCell>
                                <TableCell sx={{ color: 'var(--rs-muted)', borderColor: 'var(--rs-surface)', fontSize: '0.75rem', textTransform: 'capitalize' }}>
                                  {job.triggered_by}
                                </TableCell>
                                <TableCell sx={{ color: 'var(--rs-muted)', borderColor: 'var(--rs-surface)', fontSize: '0.75rem', maxWidth: 200, overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' }}>
                                  {job.file_name || (job.status === 'error' ? <Typography variant="caption" sx={{ color: '#ef4444' }}>{job.error_message}</Typography> : '—')}
                                </TableCell>
                                <TableCell sx={{ color: 'var(--rs-muted)', borderColor: 'var(--rs-surface)', fontSize: '0.75rem' }}>
                                  {formatFileSize(job.file_size)}
                                </TableCell>
                                <TableCell sx={{ color: 'var(--rs-muted)', borderColor: 'var(--rs-surface)', fontSize: '0.75rem' }}>
                                  {job.started_at ? new Date(job.started_at).toLocaleString() : '—'}
                                </TableCell>
                                <TableCell sx={{ color: 'var(--rs-muted)', borderColor: 'var(--rs-surface)', fontSize: '0.75rem' }}>
                                  {duration !== null ? `${duration}s` : '—'}
                                </TableCell>
                              </TableRow>
                            );
                          })}
                        </TableBody>
                      </Table>
                    </TableContainer>
                  )}
                </CardContent>
              </Card>

              {/* ── Dropbox Files (browse & restore) ────────────────── */}
              <Card sx={{ bgcolor: 'var(--rs-paper)', border: '1px solid var(--rs-border)' }}>
                <CardContent>
                  <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', mb: 2 }}>
                    <Typography variant="h6" sx={{ color: '#22d3ee', display: 'flex', alignItems: 'center', gap: 1 }}>
                      <DropboxIcon sx={{ fontSize: 20 }} /> Backups in Dropbox
                    </Typography>
                    <Button
                      size="small"
                      variant="outlined"
                      startIcon={backupDropboxFilesLoading ? <CircularProgress size={14} /> : <RefreshIcon />}
                      onClick={fetchBackupDropboxFiles}
                      disabled={backupDropboxFilesLoading}
                      sx={{ borderColor: '#3a4456', color: 'var(--rs-muted)', '&:hover': { borderColor: '#22d3ee', color: '#22d3ee' } }}
                    >
                      Load Files
                    </Button>
                  </Box>
                  {backupDropboxFilesLoading ? (
                    <Box sx={{ display: 'flex', justifyContent: 'center', py: 4 }}>
                      <CircularProgress sx={{ color: '#22d3ee' }} />
                    </Box>
                  ) : backupDropboxFiles.length === 0 ? (
                    <Typography variant="body2" sx={{ color: '#666', textAlign: 'center', py: 3 }}>
                      No backup files found. Click "Load Files" to browse your Dropbox backup folder.
                    </Typography>
                  ) : (
                    <TableContainer>
                      <Table size="small">
                        <TableHead>
                          <TableRow>
                            <TableCell sx={{ color: '#888', borderColor: 'var(--rs-border)' }}>File</TableCell>
                            <TableCell sx={{ color: '#888', borderColor: 'var(--rs-border)' }}>Size</TableCell>
                            <TableCell sx={{ color: '#888', borderColor: 'var(--rs-border)' }}>Modified</TableCell>
                            <TableCell sx={{ color: '#888', borderColor: 'var(--rs-border)' }} align="right">Actions</TableCell>
                          </TableRow>
                        </TableHead>
                        <TableBody>
                          {backupDropboxFiles.map(file => (
                            <TableRow key={file.path} sx={{ '&:hover': { bgcolor: 'var(--rs-surface)' } }}>
                              <TableCell sx={{ color: 'var(--rs-text)', borderColor: 'var(--rs-surface)', fontSize: '0.8rem' }}>
                                <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                                  <BackupIcon sx={{ fontSize: 16, color: '#0061fe' }} />
                                  {file.name}
                                </Box>
                              </TableCell>
                              <TableCell sx={{ color: 'var(--rs-muted)', borderColor: 'var(--rs-surface)', fontSize: '0.75rem' }}>
                                {formatFileSize(file.size)}
                              </TableCell>
                              <TableCell sx={{ color: 'var(--rs-muted)', borderColor: 'var(--rs-surface)', fontSize: '0.75rem' }}>
                                {new Date(file.modified).toLocaleString()}
                              </TableCell>
                              <TableCell sx={{ borderColor: 'var(--rs-surface)' }} align="right">
                                <Box sx={{ display: 'flex', gap: 1, justifyContent: 'flex-end' }}>
                                  <Button
                                    size="small"
                                    variant="outlined"
                                    startIcon={<RestoreIcon />}
                                    onClick={() => setBackupRestoreDialog({ open: true, file })}
                                    sx={{ borderColor: '#22c55e55', color: '#22c55e', '&:hover': { borderColor: '#22c55e', bgcolor: '#22c55e11' }, fontSize: '0.7rem', py: 0.25 }}
                                  >
                                    Restore
                                  </Button>
                                  <IconButton
                                    size="small"
                                    onClick={() => handleDeleteBackupFile(file)}
                                    sx={{ color: '#ef4444', '&:hover': { bgcolor: '#ef444411' } }}
                                  >
                                    <Delete fontSize="small" />
                                  </IconButton>
                                </Box>
                              </TableCell>
                            </TableRow>
                          ))}
                        </TableBody>
                      </Table>
                    </TableContainer>
                  )}
                </CardContent>
              </Card>
            </>
          )}

          {/* Restore Confirmation Dialog */}
          <Dialog
            open={backupRestoreDialog.open}
            onClose={() => setBackupRestoreDialog({ open: false, file: null })}
            PaperProps={{ sx: { bgcolor: 'var(--rs-paper)', border: '1px solid #3a4456', color: 'var(--rs-text)', minWidth: 400 } }}
          >
            <DialogTitle sx={{ color: '#f59e0b', display: 'flex', alignItems: 'center', gap: 1 }}>
              <RestoreIcon /> Restore from Backup
            </DialogTitle>
            <DialogContent>
              <Alert severity="warning" sx={{ mb: 2 }}>
                <strong>This will overwrite files on the server.</strong> Proceed with caution.
              </Alert>
              <Typography variant="body2" sx={{ color: 'var(--rs-text)', mb: 1 }}>
                File: <strong>{backupRestoreDialog.file?.name}</strong>
              </Typography>
              <Typography variant="body2" sx={{ color: 'var(--rs-muted)', mb: 2 }}>
                Size: {formatFileSize(backupRestoreDialog.file?.size)}
              </Typography>
              {isSuperAdmin() && (
                <FormControl fullWidth size="small">
                  <InputLabel sx={{ color: 'var(--rs-muted)' }}>Restore Type</InputLabel>
                  <Select
                    label="Restore Type"
                    defaultValue="audio"
                    id="restore-type-select"
                    sx={{ bgcolor: 'var(--rs-bg)', color: 'var(--rs-text)', '& .MuiOutlinedInput-notchedOutline': { borderColor: '#3a4456' } }}
                  >
                    <MenuItem value="audio">Audio files only</MenuItem>
                    {isSuperAdmin() && <MenuItem value="full">Full restore (audio + database)</MenuItem>}
                  </Select>
                </FormControl>
              )}
            </DialogContent>
            <DialogActions sx={{ px: 3, pb: 2 }}>
              <Button onClick={() => setBackupRestoreDialog({ open: false, file: null })} sx={{ color: 'var(--rs-muted)' }}>
                Cancel
              </Button>
              <Button
                variant="contained"
                color="warning"
                startIcon={<RestoreIcon />}
                onClick={async () => {
                  const sel = document.getElementById('restore-type-select');
                  const restoreType = sel ? sel.value : 'audio';
                  setBackupRestoreDialog({ open: false, file: null });
                  setBackupMessage({ type: 'info', text: 'Restore initiated. Check the server console for progress.' });
                  try {
                    await axios.post(`${API_URL}/api/backup/restore`,
                      { dropboxPath: backupRestoreDialog.file.path, restoreType },
                      { withCredentials: true }
                    );
                    setBackupMessage({ type: 'success', text: 'Restore started successfully. Files are being re-imported in the background.' });
                  } catch (e) {
                    setBackupMessage({ type: 'error', text: e.response?.data?.message || 'Restore failed.' });
                  }
                }}
              >
                Restore
              </Button>
            </DialogActions>
          </Dialog>
        </Box>
      )}

          </>
        );
      })()}
    </Box>
  );
}
